import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, ApiError } from '../lib/api.js';
import { noteDate } from '../lib/noteDate.js';
import { Modal } from './Modal.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { groupSignChecks } from '../lib/groupNoteV26.js';
import {
  attendanceLabel,
  attendeeLeft,
  cosignOpenWarning,
  cosignResultLines,
  lateHoursOf,
  ABSENT_KIND as NO_SHOW_KIND,
  layoutKindOf,
  rosterLine,
  type RosterLine,
} from '../lib/sessionRoster.js';
import {
  AttendanceWindow,
  RosterGroups,
  SignReadyWindow,
  type AttendanceRow,
} from './SessionTools.js';
import { initials, missingBoxes, noteDraftArgs } from '../lib/groupNotes.js';
import {
  familyGenerateBlock,
  familyGeneratedMsg,
  familySkipReason,
  familyTextFor,
  generateFamilyNote,
} from '../lib/familyGenerate.js';
import {
  addAttendeeShown,
  FAMILY_NOTE_TITLE,
  familySignChecks,
  isFamilyKind,
  othersAway,
  partnerSafetyShown,
  partnersToAsk,
} from '../lib/familyNote.js';
import { Dd } from './Dropdown.js';
import { DateField, TimeField } from './DateTimeField.js';
import { useSession, canManage, displayName } from '../auth/session.js';
import type { NoteUser } from './noteSharing.js';
import { fetchNoteUsers } from '../lib/noteUsers.js';
import { authorSignLabel, isUnsaved } from '../lib/noteLeave.js';
import { railOffFor } from '../lib/noShowRail.js';
import {
  CREDIFY_NOTE_OPTIONS,
  INTERVENTION_ITEMS,
  type InterventionHide,
  NOTE_FORMATS,
  NOTE_PLACES,
  RISK_ACTIONS,
  RISK_LEVELS,
  SAFETY_STATUSES,
  type NoteSafety,
  NOTE_FORMAT_LABELS,
  type ClinicalReviewEventView,
  type NoteDiagnosis,
  type SupervisionView,
  type NoteSuggestions,
  type NotePhrasesView,
  type Measure,
  type MeasuresView,
  ATTENDANCE_STATUSES,
  EMPTY_QUICK_PREFS,
  boxPhrasesFor,
  phraseHasClientInfo,
  LATE_ENTRY_HOURS_DEFAULT,
  GROUP_MOODS,
  GROUP_PARTICIPATION,
  NOTE_KINDS_WITHOUT_DX,
  PARTNER_SAFETY_QUESTIONS,
  quickOnNoteFor,
  v141NoteSettings,
  aiSetBySupervisor,
  canEditPracticeNoteSettings,
  keepRevertRequired,
  noteAiFor,
  noteRoleOf,
  readPracticeNoteSettings,
  noShowFlagsOf,
  supervisorReviews,
  type NoteSettings,
  type PracticeNoteSettings,
  billingModifier,
  payorChecks,
  type PayorRules,
  type NoteAddendumView,
  type MyChange,
  type NoteAppointment,
  type QuickItem,
  partnerSafetyDone,
  partnerSafetyOpen,
  type FamilyAttendeeView,
  type FamilySessionView,
  type GroupCosignResult,
  type GroupSubmitResult,
  type PartnerSafety,
  type FamilyType,
  type TpPlanList,
  type TpPlanSummary,
  REVIEW_FIELD_KEYS,
  readReviewThreads,
  cardThreadId,
  type SavedReviewDraft,
  newLogEntry,
  readReviewLog,
  type ReviewLogEntry,
  type ReviewLogKind,
  groupSessionType,
} from '@ehr/shared';
import {
  defaultLinkablePlan,
  fmtSlash,
  mergeLinkablePlans,
  tpListRows,
  type LinkablePlan,
} from '../lib/tpPlans.js';
import { DiagnosisCodesCard } from './DiagnosisCodes.js';
import { DiagnosisChangeModal } from './DiagnosisChangeModal.js';
import { dxOwnSection } from '../lib/noteDiagnosis.js';
import {
  defaultCodeKindOf,
  generatesOnOpen,
  kindCodeList,
  kindSettingsOf,
  sessionTypeReadOnly,
} from '../lib/skillsMedNote.js';
import { END_PROBLEM_TEXT, endProblem, endSignChecks } from '../lib/endTimeChecks.js';
import { noteErrTarget } from '../lib/noteErrTarget.js';
import { AUTOSAVE_EVENT, type AutosaveDetail } from '../lib/idleSession.js';
import { useNoteTabLock } from '../lib/useNoteTabLock.js';
import { familyLockKey, lockKeyFor } from '../lib/noteTabLock.js';
import { CLINICAL_SETTINGS_CHANGED } from '../lib/settingsEvents.js';
import {
  ADMIN_ROUGH_PLACEHOLDER,
  adminContactDraft,
  isAdminPhoneKind,
  railCardsOff as adminRailCardsOff,
  typePlaceLabel,
} from '../lib/adminPhoneNote.js';
import { AdminTypePlace } from './AdminTypePlace.js';
import { DISCHARGE_ROUGH_PLACEHOLDER, DraftWithAiCard, NoteField } from './NotePilot.js';
import { NoteTemplateMenu } from './NoteTemplateMenu.js';
import { AddAddendumModal, NoteAddendaCard } from './NoteAddenda.js';
import {
  NEW_NOTE,
  clearApptParams,
  clearNoteParam,
  readApptParam,
  readNoteParam,
  holdNoteInUrl,
  normalizeTime,
  sessionTypeFrom,
  to24h,
  type ApptPrefill,
} from '../lib/apptLink.js';
import {
  DRAFT_FIELDS,
  draftNoteSections,
  mergeDrafts,
  serviceDraft,
  sanitizeSummaryHtml,
  summaryToText,
  textToSummary,
  type NpContext,
} from '../lib/notepilot.js';
import {
  COMPLEXITY_OK,
  IC_REASONS,
  OTHER_CPT,
  THERAPY_CPT,
  US_STATES,
  baseCode,
  billingSignChecks,
  codeChecks,
  cptLabel,
  noteCode,
  posOf,
  unitsText,
} from '../lib/noteBilling.js';
import { signatureImg } from '../lib/signature.js';
import {
  applySuggestions,
  commentBullets,
  editHunks,
  keepEdit,
  pendingEdits,
  readSuggestions,
  rebaseSuggestion,
  revertEdit,
} from '../lib/trackChanges.js';
import {
  ChangesBox,
  CommentButton,
  CommentCard,
  CommentComposer,
  EditCard,
  EditChip,
  EditLabel,
  FieldChangeCard,
  SelectionPill,
  type ReviewDraft,
} from './NoteReview.js';
import {
  changedBodyParts,
  fieldChangeText,
  fieldRaw,
  fieldSnapshot,
  withFieldRaw,
  liveFieldChanges,
  mergeFieldChanges,
  readFieldChanges,
  type NoteFieldChange,
} from '../lib/fieldChanges.js';
import {
  cardParts,
  cardState,
  closedCards,
  orderByPlace,
  orderFieldCards,
  partThread,
  withClosedCards,
  withClosedThreads,
  withDecision,
  halfRaw,
  rawLabels,
  withPartSeeded,
  type CardPart,
  type OwnRedCard,
} from '../lib/cardStates.js';
import { savedLength, startLength } from '../lib/sessionLength.js';
import {
  commentThreadsFor,
  draftToSave,
  withReply,
  withTick,
  type ReviewThread,
} from '../lib/reviewCards.js';
import {
  GENERAL,
  anchorMarks,
  canReviewNote,
  changesAction,
  cosignAction,
  editChip,
  findAnchor,
  reviewerEditing,
  reviewPartMode,
  reviewPayload,
  reviewerTexts,
  reviewPrimary,
  type DraftComment,
} from '../lib/noteReview.js';
import { GoalStatusCell, StatusNote, type StatusView } from './GoalStatusCell.js';
import {
  confirmable,
  defaultStatusDate,
  isGoalStatus,
  readGoalLevels,
  statusCounts,
  statusDateRange,
  statusLabel,
  statusLine,
  statusTone,
  withoutStatus,
  type RowStatus,
} from '../lib/goalStatus.js';
import { quickWithout } from '../lib/quickScope.js';
import {
  EMPTY_FOCUS_PREFS,
  addressedFocusOf,
  focusChoices,
  focusPrefsWithAdded,
  focusPrefsWithout,
  planFocusItems,
  requireAddressedFocusOf,
  withAddressedRows,
  type FocusItem,
} from '@ehr/shared';
import {
  PEER_ACTIVITIES_ID,
  PEER_SUPPORT_KIND,
  RECOVERY_GOAL_ID,
  recoveryGoalOf,
} from '@ehr/shared';
import {
  PeerActivitiesField,
  PeerCredentialRow,
  RecoveryGoalField,
  usePeerSupport,
} from './PeerSupportFields.js';
import { peerCredentialPrintHtml, recoveryFromTag } from '../lib/peerNote.js';
import { addFocus, ehrPlanProblems, removeFocus, toggleFocus } from '../lib/focusEdit.js';
import { FocusSection, type FocusChoice } from './FocusSection.js';
import {
  hoursAfterSession,
  isLateEntry,
  lateEntryOf,
  lateText,
  sessionEndAt,
  type LateEntry,
} from '../lib/noteLate.js';
import { InterventionSearchBox } from './InterventionSearch.js';
import { INTERVENTION_LIST_MAX } from '../lib/interventionSearch.js';
import {
  EMPTY_INTERVENTION_HIDE,
  hiddenFor,
  unhideFor,
  visibleInterventions,
} from '../lib/interventionHide.js';
import {
  EMPTY_NOTE_QUERY,
  isActiveNoteQuery,
  matchNote,
  normalizeNoteQuery,
  type NoteQuery,
  type Snippet,
} from '../lib/recordSearch.js';
import { storedTime, timeOpts } from '../lib/timePrefs.js';
import {
  FORMAT_SECTIONS,
  FORMAT_SECTIONS_V81 as FORMAT_SECTIONS_ANY,
} from '../lib/noteSections.js';
import {
  NOTE_TYPE_GROUPS,
  isNoBillingKind,
  NOTE_TYPES_READY,
  noteTypeOf,
  noteTypeOffReason,
  startSessionType,
} from '../lib/noteTypes.js';
import {
  isOtherKind,
  noteBlocks,
  quickLabel,
  SERVICE_KINDS,
  withQuick,
  type NoteBlock,
} from '../lib/noteLayouts.js';
import { dotFill, dotListOf, myPhrasesFrom, stockPillsFor, withPhrase } from '../lib/dotPhrases.js';
import {
  CASE_MANAGEMENT,
  CASE_ROUGH_PLACEHOLDER,
  planGoalsOn,
  quickDotTabOff,
  railOffFor as caseRailOffFor,
} from '../lib/caseManagementNote.js';
import {
  COLLATERAL_ROUGH_PLACEHOLDER,
  isCollateral,
  railCardOn,
  railCardsOff,
  sideCardsOf,
} from '../lib/collateralNote.js';
import { Cog, MyChangesCard, QuickAddCard } from './QuickAddCard.js';
import { DictateMic } from './DictateMic.js';
import {
  BOX_EXAMPLES,
  NoteSettingsWindow,
  ShortcutsWindow,
  type NoteSettingsTab,
} from './NoteSettings.js';
import { NoteReportWindow } from './NoteReports.js';
import { type RailCard, type RailFold } from './RailFold.js';
import { carryTemplateText } from '../lib/noteCarry.js';
import {
  copyQuestions,
  splitSentences,
  unchangedCopied,
  type CopiedRecord,
} from '../lib/copyCheck.js';
import { HistoryPanel, LastSessionCard, type CopyApi } from './NoteHistory.js';
import { MeasuresCard } from './NoteMeasures.js';
import { DischargeConfirm } from './DischargeConfirm.js';
import { NoPlanReasonField } from './NoPlanReasonField.js';
import {
  DISCHARGE_HIDDEN_CARDS,
  cardOffForKind,
  noPlanPrintLine,
  noPlanReasonToSave,
  notSavedMessage,
  supervisionFor,
} from '../lib/dischargeNote.js';
import { personalTimeFormat } from '../lib/timePrefs.js';
import { dischargeOutcomeDone, dischargeOutcomeText, type DischargeOutcome } from '@ehr/shared';
import { NoteApptPicker, prefillFrom } from './NoteApptPicker.js';
import { NOTE_STATUS_LABEL, NoteStatusPill, noteStatusClass } from './NoteStatusPill.js';
import { Pager } from './Pager.js';
import { pageOfTwo } from '../lib/pager.js';
import {
  appendText,
  bringBoxOf,
  followUpLine,
  lastPlanLine,
  pastKind,
  pastSections,
  phraseKindOk,
  planFollowUps,
  signedNotes,
  withFollowUp,
  type PastNote,
} from '../lib/noteHistory.js';
import type { QuickAdd } from './NotePilot.js';
import {
  LENGTHS,
  NECESSITY_PHRASES,
  THERAPY_KINDS,
  addMinutes,
  billingCode,
  GOAL_FINAL_KINDS,
  goalStatusRows,
  leftToSign,
  sessionEndOf,
  sessionMinutes,
} from '../lib/noteV81.js';
import { typeLocked } from '../lib/noteTypeLock.js';
import { SaveButton, SaveCardAnchor, errNotice, okNotice, type SaveNotice } from './SaveToast.js';
import { TakeBackButton } from './TakeBackButton.js';
import { CommentHistory } from './CommentHistory.js';
import { editText, historyRows } from '../lib/reviewHistory.js';
import { takeBackLine } from '../lib/takeBack.js';
import { SAVE_MIN_MS, atLeast } from '../lib/saveTiming.js';
import { NOTE_DRAFT_SAVED, noteDraftErrText } from '../lib/saveCards.js';
import { chartNoteUrl, noteLinkTarget, sessionPageFor } from '../lib/sessionNoteRoute.js';

/**
 * #138 Clinical Notes tab — Progress notes + Treatment plans side by side (Jay, 10-08), each a
 * history table with a read-only preview + PDF, a slide-in editor, and the
 * co-sign workflow (draft → pending → cosigned / changes). Bodies are PHI: the
 * API encrypts them; here we only render what the API returns.
 */

/**
 * ehr#31: a note brought in by Data Import (a legacy note, the "Progress Note 1..N" columns).
 * It belongs with the client's progress notes, so it lists on the Clinical Notes tab, read-only,
 * with its original date and author; the Contact Notes timeline leaves it out.
 */
export interface ImportedNote {
  id: string;
  body: string;
  source?: string;
  legacyAuthorLabel?: string | null;
  originalCreatedAt?: string | null;
  createdAt: string;
}
/** The date an imported note was written in the old system (else when it was imported). */
export const importedDate = (n: ImportedNote) => n.originalCreatedAt ?? n.createdAt;
/** The imported notes in a /contacts/:id/notes answer, newest first. */
/** ehr#663: Why treatment continues, reviewed like a box (its body key, and its name). */
const NEC = 'necessity';
const NEC_LABEL = 'Why treatment continues';
/** ehr#741 (point 9): the note page's side panel hidden, per browser. */
const RAIL_HIDDEN_KEY = 'ehr.pn.railHidden';

export function importedNotesFrom(notes: ImportedNote[]): ImportedNote[] {
  return notes
    .filter((n) => n.source === 'legacy')
    .sort((a, b) => new Date(importedDate(b)).getTime() - new Date(importedDate(a)).getTime());
}
/** ehr#477 (Chase 10-18): the client's Notes list shows 15 notes a page. */
export const NOTES_PAGE_SIZE = 15;

// ehr#682 (V131 wording): "generate", never "draft", for text the AI fills in. Save Draft for the
// note itself stays.
/**
 * Status after Generate Notes fills boxes from the rough notes. ehr#682 (V141 V83.5): it fills only
 * the empty boxes and names the boxes it left alone. `skipped` and `empty` are box names.
 */
export function generatedMsg(r: { drafted: number; skipped: string[]; empty: string[] }): string {
  const parts = [
    r.drafted
      ? `Filled ${r.drafted} empty box${r.drafted === 1 ? '' : 'es'} with the built-in generator.`
      : 'No boxes filled.',
  ];
  if (r.skipped.length) parts.push(skippedMsg(r.skipped));
  if (r.empty.length) parts.push(`Nothing found for ${andList(r.empty)}.`);
  return parts.join(' ');
}
const andList = (l: string[]) =>
  l.length < 2 ? (l[0] ?? '') : `${l.slice(0, -1).join(', ')} and ${l[l.length - 1]}`;
const skippedMsg = (l: string[]) =>
  `Skipped ${andList(l)} (already ${l.length === 1 ? 'has' : 'have'} text).`;
/** Status after a Service note's boxes are filled from the chart (empty boxes only). */
export function serviceGeneratedMsg(names: string[], skipped: string[] = []): string {
  if (!names.length) return 'Nothing generated: the boxes already have text.';
  const msg = `Generated ${andList(names)} from the chart. Review the AI text before signing.`;
  return skipped.length ? `${msg} ${skippedMsg(skipped)}` : msg;
}
/** What's left item for a box still holding unreviewed AI text. */
export const aiReviewLabel = (box: string) => `Review AI text: ${box}`;
/** Submit refusal while a box still holds unedited AI text. */
export const uneditedAiMsg = (boxes: string[]) =>
  `Review and edit the generated text in ${boxes.join(', ')} before submitting.`;
/** ehr#526 (as tp#60): back from the TP tab, the plans list looks once more after this long. */
const TP_REFRESH_AGAIN_MS = 1500;

// ehr#477: the labels live with the status pill (NoteStatusPill), one color per status.
export const STATUS_LABEL = NOTE_STATUS_LABEL;
const FORMATS = NOTE_FORMATS;
export type Format = (typeof FORMATS)[number];
export const asFormat = (f: string): Format =>
  (FORMATS as readonly string[]).includes(f) ? (f as Format) : 'SOAP';
// ehr#437: the templates' sections live in lib/noteSections.ts (the layouts read them too).
export { FORMAT_SECTIONS };

/** The Credify Note's structured fields (body keys; see progressBodySchema). */
export interface CredifyFields {
  concerns: string[];
  concernsOther: string;
  interventions: string[];
  interventionsOther: string;
  clientResponse: string;
  clientResponseOther: string;
  nextSteps: string[];
  nextStepsOther: string;
}
export const EMPTY_CREDIFY: CredifyFields = {
  concerns: [],
  concernsOther: '',
  interventions: [],
  interventionsOther: '',
  clientResponse: '',
  clientResponseOther: '',
  nextSteps: [],
  nextStepsOther: '',
};
export const strList = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
const str = (v: unknown): string => (typeof v === 'string' ? v : '');
export function readCredify(b: Record<string, unknown> | undefined): CredifyFields {
  const x = b ?? {};
  return {
    concerns: strList(x.concerns),
    concernsOther: str(x.concernsOther),
    interventions: strList(x.interventions),
    interventionsOther: str(x.interventionsOther),
    clientResponse: str(x.clientResponse),
    clientResponseOther: str(x.clientResponseOther),
    nextSteps: strList(x.nextSteps),
    nextStepsOther: str(x.nextStepsOther),
  };
}
/** Stored diagnoses (body.diagnoses) as a clean list. */
export function readDiagnoses(v: unknown): NoteDiagnosis[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((d): d is { code: string; label?: unknown; source?: unknown } =>
      Boolean(d && typeof d === 'object' && typeof (d as { code?: unknown }).code === 'string'),
    )
    .map((d) => ({
      code: d.code,
      label: typeof d.label === 'string' ? d.label : '',
      ...(d.source === 'tp' || d.source === 'search' ? { source: d.source } : {}),
    }));
}

/** ehr#437 part 3: stored layout parts (body.extras) as text or picks by id. */
export function readExtras(v: unknown): Record<string, string | string[]> {
  if (!v || typeof v !== 'object') return {};
  const out: Record<string, string | string[]> = {};
  for (const [k, x] of Object.entries(v as Record<string, unknown>)) {
    if (typeof x === 'string') out[k] = x;
    else if (Array.isArray(x)) out[k] = x.filter((y): y is string => typeof y === 'string');
  }
  return out;
}

/** "F41.1 · Generalized anxiety disorder; F32.9 · …" for preview/print (calendar#46). */
export function diagnosisLine(body: Record<string, unknown> | undefined): string {
  return readDiagnoses(body?.diagnoses)
    .map((d) => (d.label ? `${d.code} · ${d.label}` : d.code))
    .join('; ');
}

/** NotePilot "Treatment approach" picks as one line for preview/print (calendar#46 step 5). */
export function approachLine(body: Record<string, unknown> | undefined): string {
  return strList(body?.approaches).join(', ');
}

/** Picks + "Other" text as one line for preview/print. */
const withOther = (picks: string[], other: string) =>
  [...picks, ...(other.trim() ? [other.trim()] : [])].join(', ');

/**
 * The note's sections as [label, text] pairs in template order — one source for the
 * preview and the PDF so they can't drift. Exported for tests.
 */
export function noteSections(r: {
  format: string;
  body?: Record<string, unknown>;
}): [string, string][] {
  const fmt = asFormat(r.format);
  const b = r.body ?? {};
  // calendar#46 step 8: a group note carries the group summary saved at submit.
  // ehr#437: a family / couples note's shared text is its session summary
  const fam = b.noteType === 'Family' || b.noteType === 'Couples';
  const group: [string, string][] = sectionText(b, 'groupSummary').trim()
    ? [[fam ? 'Session summary' : 'Group summary', sectionText(b, 'groupSummary')]]
    : [];
  if (fmt === 'CREDIFY') {
    const c = readCredify(b);
    return [
      ...group,
      ['Presenting concerns', withOther(c.concerns, c.concernsOther)],
      ['Interventions used', withOther(c.interventions, c.interventionsOther)],
      [
        'Client response',
        withOther(c.clientResponse ? [c.clientResponse] : [], c.clientResponseOther),
      ],
      ['Objective', sectionText(b, 'objective')],
      ['Assessment', sectionText(b, 'assessment')],
      ['Next steps', withOther(c.nextSteps, c.nextStepsOther)],
    ];
  }
  // ehr#437 (V81): the parts in the note's layout order, under its names (part 3), with
  // Interventions used, Why treatment continues, the layout's own parts and Safety when the note
  // has them. Session details, goals and diagnoses print on their own.
  const noteKind = noteTypeOf({
    noteType: b.noteType,
    sessionLabel: (b.session as { type?: string } | undefined)?.type ?? '',
  });
  // ehr#571 round 2 (V22 26 B): a group member who did not attend printed as the no-show note
  // (and a family member's: Not present with no billing code; the client's carries 90846)
  const famMemberAway =
    (noteKind === 'Family' || noteKind === 'Couples') &&
    !(b.session as { code?: string } | undefined)?.code;
  const kind = layoutKindOf(
    noteKind,
    (noteKind === 'Group' || famMemberAway) && attendanceOf(b) === 'Not present',
  );
  const blocks = withQuick(
    noteBlocks(fmt, kind, { absent: kind !== noteKind }), // ehr#731: an absent member has no Flags
    quickList(b.quick),
    fmt,
  );
  const used = strList(b.interventionsUsed);
  const nec = sectionText(b, 'necessity').trim();
  const extras = readExtras(b.extras);
  const host = blocks.some((x) => x.id === 'assessment') ? 'assessment' : 'plan';
  const out: [string, string][] = [...group];
  for (const x of blocks) {
    if (x.kind === 'text') {
      out.push([x.label, sectionText(b, x.id)]);
      if (x.id === host && nec) out.push(['Why treatment continues', nec]);
    } else if (x.kind === 'interventions' && used.length) {
      out.push([x.label, used.join(', ')]);
    } else if (x.kind === 'box' || x.kind === 'checklist') {
      const v = extras[x.id];
      const t = Array.isArray(v) ? v.join(', ') : (v ?? '');
      if (t.trim()) out.push([x.label, t]);
    } else if (x.kind === 'rating') {
      const v = extras[x.id];
      if (typeof v === 'string' && v.trim()) out.push([x.label, `${v} of ${x.max ?? 10}`]);
    } else if (x.kind === 'safety') {
      const sf = safetyLine(b.safety);
      if (sf) out.push([x.label, sf]);
      // FAMILY_COUPLES_NOTE 60 A: printed on this partner's note only
      const pv = fam ? partnerSafetyLines(b.partnerSafety) : '';
      if (pv) out.push(['Safety between partners', pv]);
    }
  }
  return out;
}

/** A group or family note's attendance ("Attended", "Not present"), or '' on any other note. */
export function attendanceOf(b: Record<string, unknown>): string {
  const a = b.attendance as { status?: unknown } | undefined;
  return typeof a?.status === 'string' ? a.status : '';
}

/**
 * ehr#571 round 2 (V22 / V11 print head): a session note's attendance, and a group member's
 * Participation and Mood, for the printout's strip.
 */
export function sessionPrintFacts(b: Record<string, unknown>): [string, string][] {
  const att = attendanceOf(b);
  if (!att) return [];
  const group = b.noteType === 'Group';
  const out: [string, string][] = [['Attendance', group ? attendanceLabel(att) : att]];
  if (typeof b.participation === 'string' && b.participation)
    out.push(['Participation', b.participation]);
  if (typeof b.groupMood === 'string' && b.groupMood) out.push(['Mood', b.groupMood]);
  return out;
}

/** ehr#437 (FAMILY_COUPLES_NOTE 60 A): Safety between partners for preview and print. */
export function partnerSafetyLines(v: unknown): string {
  const p = v as PartnerSafety | undefined;
  if (!p || p.notPartner || !(p.a ?? []).some(Boolean)) return '';
  const lines = PARTNER_SAFETY_QUESTIONS.map((q, i) => `${q} ${p.a?.[i] ?? 'Not asked'}`);
  const head = p.at ? `Asked alone, ${fmtMDY(p.at)}` : 'Asked alone';
  const rv = p.review
    ? [`Reviewed by ${p.review.by}, ${fmtMDY(p.review.at)}: ${p.review.text}`]
    : [];
  return [head, ...lines, ...rv].join('\n');
}

/** ehr#437 (V81 Quick add): the Quick add sections a note keeps (its own copy of each). */
export function quickList(v: unknown): QuickItem[] {
  return Array.isArray(v)
    ? (v as QuickItem[]).filter(
        (q) => q && typeof q.id === 'string' && typeof q.label === 'string' && !!q.group,
      )
    : [];
}

/** ehr#437: Safety as one line for preview and print ("Concerns reported. Risk level: High. …"). */
export function safetyLine(v: unknown): string {
  if (!v || typeof v !== 'object') return '';
  const s = v as Partial<NoteSafety>;
  if (!s.status) return '';
  if (s.status !== 'Concerns reported') return s.status;
  return [
    'Concerns reported.',
    s.level ? `Risk level: ${s.level}.` : '',
    s.actions?.length ? `Actions taken: ${s.actions.join(', ')}.` : '',
    s.notified?.trim() ? `Notified: ${s.notified.trim()}.` : '',
    s.followup?.trim() ? `Follow-up: ${s.followup.trim()}` : '',
  ]
    .filter(Boolean)
    .join(' ');
}

/** Today as YYYY-MM-DD in the user's own time zone (toISOString is UTC: evenings in PT gave tomorrow). */
function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface GoalAddressed extends RowStatus {
  goalText: string;
  objectiveText: string;
  addressed: boolean;
  progress: string; // Improved | No change | Worsened (before ehr#571)
}
/** ehr#571: a goal's own status, for a goal that has objectives. */
interface GoalLevel extends RowStatus {
  goalText: string;
}
interface ProgressBody {
  linkedPlanId?: string | null;
  /** ehr#708: a discharge summary with no plan on file: why. */
  noPlanReason?: string;
  session?: {
    time?: string;
    type?: string;
    durationMin?: number;
    // ehr#437 (V81 Session details)
    end?: string;
    place?: (typeof NOTE_PLACES)[number];
    code?: string;
    modifier?: string; // ehr#543: the billing line's modifier (95 / 93 or the payor's)
    // ehr#478 (V81 Type, place and billing)
    codePick?: string;
    complexity?: boolean;
    icReason?: string;
    locState?: string;
    clientAt?: 'Home' | 'Other';
  };
  goalsAddressed?: GoalAddressed[];
  safety?: NoteSafety; // ehr#437
  // SOAP/DAP/BIRP section text, keyed by section id (subjective, plan, …).
  [section: string]: unknown;
}
/** Read one SOAP/DAP/BIRP section as a string (body values are typed unknown). */
const sectionText = (b: ProgressBody | undefined, key: string): string => {
  const v = b ? b[key] : undefined;
  return typeof v === 'string' ? v : '';
};
export interface ProgressNote {
  id: string;
  authorId: string;
  date: string;
  sessionLabel: string;
  format: string;
  isGroup: boolean;
  status: string;
  assignedCosignerId: string | null;
  submittedAt: string | null;
  cosignedAt: string | null;
  reviewRound?: number;
  appointmentKey?: string | null;
  groupSessionId?: string | null;
  body: ProgressBody;
  createdAt: string;
  updatedAt: string;
  /** ehr#571 (reopen 10-27): the API's answer to "does the caller review this note?" */
  canReview?: boolean;
  /** ehr#741 (point 4): the caller's own saved draft of their review of this pending note. */
  reviewDraft?: SavedReviewDraft;
  /** ehr#682 (V141 V83.4): until when the caller, who sent it back, may take it back. */
  takeBackUntil?: string;
}
interface Intervention {
  description: string;
  frequency: string;
  format: string;
  length: string;
}
interface Objective {
  objective_text: string;
  progress_status?: string;
  interventions: Intervention[];
}
interface Goal {
  goal_text: string;
  target_date?: string;
  responsible_party?: string;
  objectives: Objective[];
}
interface PlanBody {
  presenting_problem?: string;
  goals: Goal[];
}
interface TreatmentPlan {
  id: string;
  authorId: string;
  date: string;
  mode: string;
  status: string;
  assignedCosignerId: string | null;
  submittedAt: string | null;
  cosignedAt: string | null;
  body: PlanBody;
  createdAt: string;
  updatedAt: string;
}

/** ehr#437: a saved note's kind: the one it was started as, else from its session type. */
const kindOf = (pn: { isGroup: boolean; sessionLabel: string; body?: object }) =>
  noteTypeOf({
    isGroup: pn.isGroup,
    sessionLabel: pn.sessionLabel,
    noteType: (pn.body as { noteType?: unknown } | undefined)?.noteType,
  });

// ehr#484: a note's saved date is its day at midnight UTC, read as that calendar day.
const fmtMDY = (s: string) => {
  const d = noteDate(s);
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
};
// sec 2026-10-24: quotes too. The print builders put names in attributes (the signature image's
// alt), and the print window runs in the EHR's origin, so a name must not open a new attribute.
const esc = (v: unknown) =>
  String(v ?? '').replace(
    /[&<>"]/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c] ?? c,
  );

type Kind = 'progress' | 'plan';

export function ClinicalNotesPanel({
  contactId,
  contactName,
  tpLink,
  openNoteId,
  refreshKey,
  readOnly = false,
}: {
  contactId: string;
  contactName: string;
  /** tp#299: the TP app link, made only by an ACTIVE client's chart (tpChartLink); null for an
   *  archived client: no + Add new for plans, no Open in Treatment Plans, no plan row opens. */
  tpLink: string | null;
  /** alertshub#6: the note an Alerts Hub card links to (?view=clinical_notes&record=<id>). */
  openNoteId?: string;
  /** ehr#408: the chart's live stamp (a group note, a teammate's note); the list reloads when it moves. */
  refreshKey?: number;
  /** ehr#730: a View tab (Share & Access): no Add new. A note's author and reviewer still work it. */
  readOnly?: boolean;
}) {
  const { user } = useSession();
  const [progress, setProgress] = useState<ProgressNote[]>([]);
  const [imported, setImported] = useState<ImportedNote[]>([]); // ehr#31
  const [importedOpen, setImportedOpen] = useState<ImportedNote | null>(null);
  const [plans, setPlans] = useState<TreatmentPlan[]>([]);
  // ehr#526: plans built in the Treatment Plan Builder (TP app), listed with the EHR's own.
  // Loaded on their own so a slow or unreachable TP app never holds up the chart.
  const [tpList, setTpList] = useState<TpPlanList | null>(null);
  const [users, setUsers] = useState<NoteUser[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'forbidden' | 'error'>('loading');

  // Preview + editor state.
  const [preview, setPreview] = useState<{ kind: Kind; id: string } | null>(null);
  const [editor, setEditor] = useState<{
    kind: Kind;
    id: string | null;
    prefill?: ApptPrefill;
    /** fbs#97: a new note started as a copy of this saved note. */
    copyFrom?: { id: string; date: string };
    /** ehr#270: a group attendee's note shown read-only here (it's written in the group workspace). */
    viewOnly?: boolean;
    /** ehr#437: the kind picked in "What kind of note?" (a new note's session type). */
    noteType?: string;
  } | null>(null);
  // ehr#681 follow-up: Save Draft goes back to the list; "Draft saved" shows there, under + Add new.
  const [draftCard, setDraftCard] = useState<SaveNotice | null>(null);
  // ehr#662: another note opened while one is on screen replaces it, after the open note's own
  // unsaved-changes question (Save Draft / Don't save / Go back). The editor is keyed on the note
  // it shows, so nothing of the first note (status, id, review edits, goals) carries over.
  const leaveEditor = useRef<((go: () => void) => void) | null>(null);
  const switchEditor = (next: NonNullable<typeof editor>) => {
    if (editorOpen.current && leaveEditor.current) leaveEditor.current(() => setEditor(next));
    else setEditor(next);
  };
  // ehr#437: + Add new asks which appointment (Pranav 10-16), then "What kind of note?".
  const [choosingAppt, setChoosingAppt] = useState(false);
  const [pickedAppt, setPickedAppt] = useState<NoteAppointment | null>(null);
  const [picking, setPicking] = useState(false);
  // ehr#437 (FAMILY_COUPLES_NOTE): an open family / couples session, on one attendee's note.
  const [family, setFamily] = useState<{ sessionId: string; contactId: string } | null>(null);
  const [familyErr, setFamilyErr] = useState('');
  // calendar#139: start (or reopen) a family / couples session, tied to the appointment it's for.
  const openFamilySession = (
    type: 'Family' | 'Couples',
    appt?: { appointmentKey: string; durationMin: number | null },
  ) => {
    setFamilyErr('');
    api
      .post<FamilySessionView>('/family-notes/open', {
        clientId: contactId,
        type,
        ...(appt ? { appointmentKey: appt.appointmentKey } : {}),
        ...(appt?.durationMin ? { durationMin: appt.durationMin } : {}),
      })
      .then((v) => setFamily({ sessionId: v.id, contactId }))
      .catch((e: unknown) =>
        setFamilyErr(e instanceof ApiError ? e.message : 'Could not start the family session.'),
      );
  };
  // ehr#689 (Jay 10-30, ruling #4): a family / couples attendee's note opens on its family page
  // with this client (the attendee) selected, from the list, the To review queue, a Dashboard or
  // Alerts Hub card and a deep link alike, so its reviewer gets the attendees, Pending review and
  // Co-sign all. The page's Back returns to this chart, as the single note's does. Group notes
  // are not routed here yet (#688 Q2): see lib/sessionNoteRoute. False: open the note as before.
  const openOnSessionPage = (r: ProgressNote): boolean => {
    const page = sessionPageFor(r);
    if (page?.kind !== 'family') return false;
    const go = () => {
      setEditor(null);
      setFamily({ sessionId: page.sessionId, contactId });
    };
    if (editorOpen.current && leaveEditor.current) leaveEditor.current(go);
    else go();
    return true;
  };
  // ehr#270: a note opens in the editor, looking as it does while being written: editable for its
  // author's draft (a group note's author edits it in the group workspace) and for the reviewer of a
  // pending one, read-only for everyone else.
  const openNote = (r: ProgressNote) => {
    const ownDraft = (r.status === 'draft' || r.status === 'changes') && r.authorId === user?.id;
    // ehr#437: a family / couples note opens in its session, on this client's note
    if (openOnSessionPage(r)) return;
    if (ownDraft && r.groupSessionId && r.appointmentKey) {
      window.location.assign(
        `/?group=${encodeURIComponent(r.appointmentKey)}&focus=${encodeURIComponent(contactId)}`,
      );
      return;
    }
    switchEditor({ kind: 'progress', id: r.id, viewOnly: r.isGroup });
  };
  // calendar#46 step 6: a link from a calendar appointment is handled once per mount.
  const apptHandled = useRef(false);
  // ehr#210: Search notes (date range and/or words) filters the progress-notes list.
  // ehr#210 (10-06): the fields sit on the tab and filter as you type; no pop-up.
  const [noteQuery, setNoteQuery] = useState<NoteQuery>(EMPTY_NOTE_QUERY);
  // ehr#437: the Notes list holds every kind of note; Type shows one kind ('' = All types).
  const [typeFilter, setTypeFilter] = useState('');
  useEffect(() => {
    setNoteQuery(EMPTY_NOTE_QUERY);
    setTypeFilter('');
  }, [contactId]);
  // ehr#477 (Chase 10-18): the Notes list shows 15 a page; a new client, search or type
  // starts on page 1.
  const [notesPage, setNotesPage] = useState(1);
  useEffect(() => setNotesPage(1), [contactId, noteQuery, typeFilter]);

  const isManager = Boolean(user && canManage(user.role));

  // ehr#526: the client's Treatment Plan Builder plans. Quiet on failure: the API answers
  // `available: false` when the TP app can't be reached and the list keeps the EHR's own plans.
  const tpFor = useRef(contactId);
  const loadTp = () => {
    const forId = contactId;
    if (tpFor.current !== forId) setTpList(null);
    tpFor.current = forId;
    api
      .get<TpPlanList>(`/contacts/${forId}/tp-plans`)
      .then((l) => tpFor.current === forId && setTpList(l))
      .catch(() => tpFor.current === forId && setTpList({ available: false, plans: [] }));
  };
  // Plans are written in the TP app's own tab: after Add new / Open, look again when the user
  // comes back (and once more a moment later, for a TP save still on its way, tp#60).
  const wentToTp = useRef(false);
  const openTp = (url: string) => {
    wentToTp.current = true;
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  useEffect(() => {
    let again: ReturnType<typeof setTimeout> | undefined;
    const back = () => {
      if (!wentToTp.current || document.visibilityState !== 'visible') return;
      wentToTp.current = false;
      loadTp();
      clearTimeout(again);
      again = setTimeout(loadTp, TP_REFRESH_AGAIN_MS);
    };
    document.addEventListener('visibilitychange', back);
    window.addEventListener('focus', back);
    return () => {
      document.removeEventListener('visibilitychange', back);
      window.removeEventListener('focus', back);
      clearTimeout(again);
    };
  }, [contactId]);

  const load = () => {
    loadTp();
    // ehr#31: the client's imported notes list with the progress notes (never blocks them).
    api
      .get<ImportedNote[]>(`/contacts/${contactId}/notes`)
      .then((n) => setImported(importedNotesFrom(Array.isArray(n) ? n : [])))
      .catch(() => setImported([]));
    Promise.all([
      api.get<ProgressNote[]>(`/contacts/${contactId}/progress-notes`),
      api.get<TreatmentPlan[]>(`/contacts/${contactId}/treatment-plans`),
    ])
      .then(([p, t]) => {
        setProgress(p);
        setPlans(t);
        setStatus('ready');
        // calendar#46 step 6: opened from a calendar appointment's "Progress Note"
        // button: reopen that appointment's note, or start one pre-filled from it.
        // ehr#622: only a link for this client (see parseApptParams)
        const appt = apptHandled.current ? null : readApptParam(contactId);
        if (appt) {
          apptHandled.current = true;
          clearApptParams();
          const hit = p.find((n) => n.appointmentKey === appt.appointmentKey);
          // calendar#139: a Family / Couples appointment opens (or reopens) its family session
          if (!hit && appt.familyType) openFamilySession(appt.familyType, appt);
          else
            setEditor(
              hit
                ? { kind: 'progress', id: hit.id }
                : { kind: 'progress', id: null, prefill: appt },
            );
        }
        // calendar#46 step 9: opened from the Progress Notes queue on one note.
        // alertshub#6: or from an Alerts Hub card about one note.
        const noteId = apptHandled.current ? null : (readNoteParam() ?? openNoteId ?? null);
        if (noteId) {
          apptHandled.current = true;
          clearNoteParam();
          // ehr#181: the page's + New note starts a note for this client. ehr#479: as + Add new
          // does (which appointment, then what kind), so family, group and case notes are there too.
          if (noteId === NEW_NOTE)
            setChoosingAppt(!readOnly); // ehr#730: not on a View tab
          else {
            // ehr#689: a family / couples note opens on its family page (openOnSessionPage)
            const target = noteLinkTarget(p, noteId);
            if (target && !openOnSessionPage(target.note))
              switchEditor({ kind: 'progress', id: noteId });
          }
        }
      })
      .catch((e: unknown) =>
        setStatus(e instanceof ApiError && e.status === 403 ? 'forbidden' : 'error'),
      );
  };
  useEffect(load, [contactId]);
  // ehr#408: a note saved elsewhere (a group note's attendee note, another tab) shows in the list.
  // Not while a note is open in the editor, so nothing changes under the person writing it.
  const editorOpen = useRef(false);
  editorOpen.current = editor !== null;
  useEffect(() => {
    if (refreshKey && !editorOpen.current) load();
  }, [refreshKey]);
  // ehr#571: names for clinicians too (they can't read /settings/users).
  useEffect(() => {
    void fetchNoteUsers().then(setUsers);
  }, []);

  const userName = (id: string | null) => {
    if (!id) return '—';
    const u = users.find((x) => x.id === id);
    return u ? [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email : '—';
  };
  const previewRow = useMemo(() => {
    if (!preview) return null;
    return preview.kind === 'progress'
      ? (progress.find((r) => r.id === preview.id) ?? null)
      : (plans.find((r) => r.id === preview.id) ?? null);
  }, [preview, progress, plans]);

  if (status === 'loading') return <p className="muted">Loading clinical notes…</p>;
  if (status === 'forbidden')
    return <p className="muted">Your role cannot view clinical notes (403).</p>;
  if (status === 'error') return <p className="muted">Could not load clinical notes.</p>;

  const badge = (s: string) => <NoteStatusPill status={s} />;
  // ehr#437: your own unsigned note reads Continue (the mockup's "Continue ›"), others Open.
  const rowOpen = (word: 'Open' | 'Continue') => (
    <span className="elig-row-open">
      {word}
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </span>
  );
  const chevron = rowOpen('Open');

  const section = (kind: Kind) => {
    const isProgress = kind === 'progress';
    const label = isProgress ? 'Notes' : 'Treatment plans';
    const all: (ProgressNote | TreatmentPlan)[] = isProgress ? progress : plans;
    // ehr#210: while a search is set, only matching progress notes show, each with the
    // words it matched on (a row for the excerpt under the note).
    const nq = normalizeNoteQuery(noteQuery); // a reversed range still means what was meant
    const searching = isProgress && isActiveNoteQuery(nq);
    const typed = isProgress && typeFilter !== '';
    const filtering = searching || typed;
    const snippets = new Map<string, Snippet>();
    const rows = all.filter((r) => {
      if (!isProgress) return true;
      const pn = r as ProgressNote;
      if (typed && kindOf(pn) !== typeFilter) return false;
      if (!searching) return true;
      const m = matchNote(
        pn,
        [
          kindOf(pn),
          pn.sessionLabel,
          NOTE_FORMAT_LABELS[asFormat(pn.format)],
          userName(pn.authorId),
        ],
        nq,
      );
      if (m.snippet) snippets.set(pn.id, m.snippet);
      return m.hit;
    });
    // ehr#31: imported notes, after the progress notes, under the same search. They have no
    // kind, so a Type pick leaves them out.
    const importedRows =
      !isProgress || typed
        ? []
        : imported.filter((n) => {
            if (!searching) return true;
            const m = matchNote(
              { date: importedDate(n), body: n.body },
              ['Imported', n.legacyAuthorLabel ?? ''],
              nq,
            );
            if (m.snippet) snippets.set(n.id, m.snippet);
            return m.hit;
          });
    // ehr#526: the Treatment Plan Builder's plans lead the Treatment plans list (the live one
    // first), then any plan written in the EHR itself.
    const tpRows = isProgress ? [] : tpListRows(tpList, tpLink, localToday());
    const shown = rows.length + importedRows.length + tpRows.length;
    const onFile = all.length + (isProgress ? imported.length : tpRows.length);
    const cols = isProgress ? 7 : 6;
    // ehr#477: 15 notes a page, progress notes first, then imported ones (plans: one page).
    const pg = pageOfTwo(
      rows,
      importedRows,
      isProgress ? notesPage : 1,
      isProgress ? NOTES_PAGE_SIZE : Math.max(1, shown),
    );
    const pageRows = pg.a;
    const pageImported = pg.b;
    return (
      <div className="cn-section">
        <div className="tab-toolbar save-host">
          <div className="tab-toolbar-label">
            {label} · {filtering ? `${shown} of ${onFile} match` : `${shown} on file`}
          </div>
          <div className="cn-toolbar-btns">
            <SaveCardAnchor
              notice={isProgress ? draftCard : null}
              onClose={() => setDraftCard(null)}
            >
              <button
                className="auth-add-btn"
                hidden={readOnly || (!isProgress && !tpLink)}
                onClick={() =>
                  // #138 - Treatment plans open the standalone Credify TP module (same as the
                  // old TP tab). ehr#437: a note asks "What kind of note?" first.
                  // tp#299: only an active client's chart has a TP link to open.
                  isProgress ? setChoosingAppt(true) : tpLink && openTp(tpLink)
                }
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                Add new
              </button>
            </SaveCardAnchor>
          </div>
        </div>
        {!isProgress && !tpLink && !readOnly && (
          // tp#299: why there is no + Add new on an archived client's chart
          <p className="cn-tp-inactive">Treatment plans open only for active clients.</p>
        )}
        {isProgress && (
          <div className="cn-search-row" role="search" aria-label="Search notes">
            <div className="cn-search-fld cn-type-fld">
              <span>Type</span>
              <Dd
                value={typeFilter}
                ariaLabel="Note type"
                placeholder="All types"
                searchable={false}
                options={[
                  { value: '', label: 'All types' },
                  ...NOTE_TYPE_GROUPS.flatMap((g) =>
                    g.items.map((it) => ({ value: it.type, label: it.type, group: g.group })),
                  ),
                ]}
                onChange={setTypeFilter}
              />
            </div>
            <label className="cn-search-fld">
              <span>From</span>
              <DateField
                value={noteQuery.from}
                onChange={(v) => setNoteQuery((q) => ({ ...q, from: v }))}
              />
            </label>
            <label className="cn-search-fld">
              <span>To</span>
              <DateField
                value={noteQuery.to}
                onChange={(v) => setNoteQuery((q) => ({ ...q, to: v }))}
                min={noteQuery.from || undefined}
              />
            </label>
            <label className="cn-search-fld cn-search-words">
              <span>Note contains</span>
              <input
                type="search"
                value={noteQuery.text}
                placeholder="Words from the note, e.g. panic sleep"
                title="Searches each note's text, session, format and clinician. A note matches when it has every word, in any order."
                onChange={(e) => setNoteQuery((q) => ({ ...q, text: e.target.value }))}
              />
            </label>
            <button
              type="button"
              className="cn-search-link"
              disabled={!filtering}
              onClick={() => {
                setNoteQuery(EMPTY_NOTE_QUERY);
                setTypeFilter('');
              }}
            >
              Clear
            </button>
          </div>
        )}
        <div className="elig-table-wrap">
          <table className="elig-table cn-table">
            <thead>
              {isProgress ? (
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Format</th>
                  <th>Clinician</th>
                  <th>Status</th>
                  <th></th>
                  <th></th>
                </tr>
              ) : (
                <tr>
                  <th>Date</th>
                  <th>Mode</th>
                  <th>Clinician</th>
                  <th>Status</th>
                  <th></th>
                  <th></th>
                </tr>
              )}
            </thead>
            <tbody>
              {shown === 0 ? (
                <tr>
                  <td colSpan={cols} className="cn-empty">
                    {filtering ? 'No notes match.' : `No ${label.toLowerCase()} on file.`}
                  </td>
                </tr>
              ) : (
                <>
                  {tpRows.map((t) => (
                    <tr
                      key={`tp-${t.plan.id}`}
                      className={`cn-row-tp${t.url ? '' : ' cn-row-tp-past'}`}
                      data-tp-plan={t.plan.id}
                      onClick={t.url ? () => openTp(t.url!) : undefined}
                      style={t.url ? { cursor: 'pointer' } : undefined}
                      title={
                        t.url
                          ? 'Opens in the Treatment Plan Builder'
                          : 'An earlier plan, archived in the Treatment Plan Builder'
                      }
                    >
                      <td style={{ whiteSpace: 'nowrap' }}>{fmtSlash(t.plan.createdAt) || '—'}</td>
                      <td className="td-muted">
                        {t.mode}
                        <span className="cn-sub">{t.where}</span>
                      </td>
                      <td className="td-muted">{t.plan.clinician || '—'}</td>
                      <td>{badge(t.status)}</td>
                      <td></td>
                      <td>{t.url ? chevron : null}</td>
                    </tr>
                  ))}
                  {pageRows.map((r) => {
                    const pn = r as ProgressNote;
                    const snip = snippets.get(r.id);
                    const rowCls = isProgress && pn.isGroup ? 'cn-row-group' : '';
                    // ehr#437: the note's kind, with its session type under it when that says more
                    // ("Intake & Assessment" under New Client, not "Individual Therapy" under Individual).
                    const noteKind = isProgress ? kindOf(pn) : '';
                    const sub =
                      isProgress &&
                      !pn.sessionLabel.toLowerCase().startsWith(noteKind.toLowerCase())
                        ? pn.sessionLabel
                        : '';
                    const own =
                      isProgress &&
                      (pn.status === 'draft' || pn.status === 'changes') &&
                      pn.authorId === user?.id;
                    return (
                      <Fragment key={r.id}>
                        <tr
                          className={`${rowCls}${snip ? ' cn-row-has-snip' : ''}`}
                          onClick={() =>
                            isProgress ? openNote(pn) : setPreview({ kind, id: r.id })
                          }
                          style={{ cursor: 'pointer' }}
                        >
                          <td style={{ whiteSpace: 'nowrap' }}>{fmtMDY(r.date)}</td>
                          {isProgress ? (
                            <>
                              <td>
                                {noteKind}
                                {sub && <span className="cn-sub">{sub}</span>}
                              </td>
                              <td className="td-muted">
                                {NOTE_FORMAT_LABELS[asFormat(pn.format)]}
                              </td>
                            </>
                          ) : (
                            <td className="td-muted">{(r as TreatmentPlan).mode}</td>
                          )}
                          <td className="td-muted">{userName(r.authorId)}</td>
                          <td>{badge(r.status)}</td>
                          <td></td>
                          <td>{rowOpen(own ? 'Continue' : 'Open')}</td>
                        </tr>
                        {snip && (
                          <tr
                            className={`cn-snip-row ${rowCls}`}
                            onClick={() =>
                              isProgress ? openNote(pn) : setPreview({ kind, id: r.id })
                            }
                            style={{ cursor: 'pointer' }}
                          >
                            <td colSpan={cols}>
                              {snip[0]}
                              <mark>{snip[1]}</mark>
                              {snip[2]}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                  {pageImported.map((n) => {
                    const snip = snippets.get(n.id);
                    return (
                      <Fragment key={n.id}>
                        <tr
                          className={`cn-row-imported${snip ? ' cn-row-has-snip' : ''}`}
                          onClick={() => setImportedOpen(n)}
                          style={{ cursor: 'pointer' }}
                          title="Imported from your previous system (Data Import). Read-only."
                        >
                          <td style={{ whiteSpace: 'nowrap' }}>{fmtMDY(importedDate(n))}</td>
                          <td>Imported note</td>
                          <td className="td-muted">Imported</td>
                          <td className="td-muted">{n.legacyAuthorLabel || '—'}</td>
                          <td>
                            <span className="cn-badge imported">Imported</span>
                          </td>
                          <td></td>
                          <td>{chevron}</td>
                        </tr>
                        {snip && (
                          <tr
                            className="cn-snip-row cn-row-imported"
                            onClick={() => setImportedOpen(n)}
                            style={{ cursor: 'pointer' }}
                          >
                            <td colSpan={cols}>
                              {snip[0]}
                              <mark>{snip[1]}</mark>
                              {snip[2]}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </>
              )}
            </tbody>
          </table>
        </div>
        {!isProgress && tpList && !tpList.available && (
          <p className="muted cn-tp-down">
            Plans from the Treatment Plan Builder can&apos;t be listed right now.
          </p>
        )}
        <Pager
          className="cn-pager"
          page={pg.safePage}
          totalPages={pg.totalPages}
          first={pg.first}
          last={pg.last}
          total={shown}
          onPage={setNotesPage}
        />
      </div>
    );
  };

  return (
    <div className="notes-panel">
      <div className="cn-sections-wrap">
        {/* Jay (10-08): Progress notes and Treatment plans together, side by side. */}
        <div className="cn-col">{section('progress')}</div>
        <div className="cn-col">{section('plan')}</div>
      </div>

      {choosingAppt && (
        <NoteApptPicker
          contactId={contactId}
          contactName={contactName}
          currentUserId={user?.id ?? null}
          onClose={() => setChoosingAppt(false)}
          onPick={(appt) => {
            setChoosingAppt(false);
            // ehr#479: a group session's note is written in the group note page, roster filled
            if (appt?.group) {
              window.location.assign(
                `/?group=${encodeURIComponent(appt.appointmentKey)}&focus=${encodeURIComponent(contactId)}`,
              );
              return;
            }
            setPickedAppt(appt);
            setPicking(true);
          }}
          onOpenNote={(id) => {
            setChoosingAppt(false);
            const r = progress.find((n) => n.id === id);
            if (r) openNote(r);
            else switchEditor({ kind: 'progress', id });
          }}
        />
      )}
      {picking && (
        <NoteTypePicker
          onClose={() => setPicking(false)}
          onPick={(noteType) => {
            setPicking(false);
            if (noteType === 'Family' || noteType === 'Couples') {
              // ehr#437: a new family / couples session, this client first (and billed);
              // calendar#139: on the appointment picked, so it reopens from there too
              openFamilySession(noteType, pickedAppt ?? undefined);
              return;
            }
            setEditor({
              kind: 'progress',
              id: null,
              noteType,
              ...(pickedAppt ? { prefill: prefillFrom(pickedAppt, noteType) } : {}),
            });
          }}
        />
      )}

      {importedOpen && (
        <Modal
          title="Imported note"
          onClose={() => setImportedOpen(null)}
          className="cn-imported-modal"
          actions={
            <button className="btn" onClick={() => setImportedOpen(null)}>
              Close
            </button>
          }
        >
          <p className="cn-imported-meta">
            {fmtMDY(importedDate(importedOpen))} ·{' '}
            {importedOpen.legacyAuthorLabel || 'Author not recorded'} · imported from your previous
            system (Data Import). Read-only.
          </p>
          <div className="cn-imported-body">{importedOpen.body}</div>
        </Modal>
      )}

      {preview && previewRow && (
        <PreviewModal
          kind={preview.kind}
          row={previewRow}
          contactName={contactName}
          clinician={userName(previewRow.authorId)}
          onClose={() => setPreview(null)}
          action={(() => {
            // calendar#46: a saved progress note could only be previewed, so a draft
            // couldn't be continued and a reviewer couldn't reach Co-sign & Finalize.
            // Open the editor (it already loads a note by id) from here.
            // ehr#256 (Chase 10-10): a treatment plan's first choice is the Treatment Plans
            // builder (where + Add new goes too, on the client's plan); Download PDF stays beside it.
            // tp#299: not on an archived client's chart (no TP link).
            if (preview.kind !== 'progress')
              return tpLink
                ? {
                    label: 'Open in Treatment Plans',
                    onClick: () => openTp(tpLink),
                  }
                : undefined;
            const r = previewRow as ProgressNote;
            const open = () => {
              setPreview(null);
              setEditor({ kind: 'progress', id: r.id });
            };
            // calendar#46 step 8: a group note is written in the group workspace, with
            // the session setup and the other attendees; reviewing works from here.
            const openGroup = () =>
              window.location.assign(
                `/?group=${encodeURIComponent(r.appointmentKey ?? '')}&focus=${encodeURIComponent(contactId)}`,
              );
            if ((r.status === 'draft' || r.status === 'changes') && r.authorId === user?.id)
              return {
                label: 'Edit',
                onClick: r.groupSessionId && r.appointmentKey ? openGroup : open,
              };
            if (
              r.status === 'pending' &&
              (isManager || (r.assignedCosignerId != null && r.assignedCosignerId === user?.id))
            )
              return { label: 'Review', onClick: open };
            return undefined;
          })()}
          secondary={(() => {
            // fbs#97: start a new note from a saved one (not a group note: those are
            // written for one group session in the group workspace).
            if (preview.kind !== 'progress') return undefined;
            const r = previewRow as ProgressNote;
            if (r.groupSessionId) return undefined;
            return {
              label: 'Copy to new note',
              onClick: () => {
                setPreview(null);
                setEditor({
                  kind: 'progress',
                  id: null,
                  copyFrom: { id: r.id, date: String(r.date).slice(0, 10) },
                });
              },
            };
          })()}
        />
      )}

      {familyErr && (
        <p className="pn-inline-err" role="alert">
          {familyErr}
        </p>
      )}
      {family && (
        <FamilyNotePage
          sessionId={family.sessionId}
          initialContactId={family.contactId}
          history={progress}
          historyFor={contactId}
          users={users}
          currentUserId={user?.id ?? null}
          canReviewNotes={isManager}
          onClose={() => {
            setFamily(null);
            load();
          }}
          onHandOff={(noteId, cid) => {
            // ehr#689: opened from a family member's chart, the client's note opens on the client's
            if (cid !== contactId) {
              window.location.assign(chartNoteUrl(cid, noteId));
              return;
            }
            setFamily(null);
            load();
            setEditor({ kind: 'progress', id: noteId });
          }}
        />
      )}

      {editor && editor.kind === 'progress' && (
        <ProgressNoteEditor
          key={editorKey(editor)}
          editId={editor.id}
          bindLeave={(fn) => {
            leaveEditor.current = fn;
          }}
          prefill={editor.prefill}
          copyFrom={editor.copyFrom}
          noteType={editor.noteType}
          history={progress}
          viewOnly={editor.viewOnly}
          onCopy={(id, date) =>
            setEditor({ kind: 'progress', id: null, copyFrom: { id, date: date.slice(0, 10) } })
          }
          contactId={contactId}
          contactName={contactName}
          users={users}
          currentUserId={user?.id ?? null}
          canReviewNotes={isManager}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            load();
          }}
          onDraftSaved={() =>
            setDraftCard(okNotice(NOTE_DRAFT_SAVED, { at: 'draft', below: true }))
          }
          onDischarged={(text, done) =>
            setDraftCard((done ? okNotice : errNotice)(text, { at: 'draft', below: true }))
          }
        />
      )}

      {editor && editor.kind === 'plan' && (
        <ClinicalEditor
          kind={editor.kind}
          editId={editor.id}
          contactId={contactId}
          onClose={() => setEditor(null)}
          onSaved={() => {
            setEditor(null);
            load();
          }}
        />
      )}
    </div>
  );
}

// ── ehr#437 "What kind of note?" (CLINICAL_NOTES_TAB_10-14-V15) ──────────────
/**
 * Every kind of note as a tile, grouped, with its billing codes. The kinds whose note is built
 * open it; the rest are greyed (Taras, ehr#437) and say why when clicked. Group never starts
 * here: it starts from the group appointment, so the roster is filled.
 */
export function NoteTypePicker({
  onPick,
  onClose,
}: {
  onPick: (type: string) => void;
  onClose: () => void;
}) {
  const [why, setWhy] = useState('');
  return (
    <Modal
      title="What kind of note?"
      onClose={onClose}
      className="cn-pick-modal"
      actions={
        <button type="button" className="btn" onClick={onClose}>
          Cancel
        </button>
      }
    >
      {NOTE_TYPE_GROUPS.map((g) => (
        <div key={g.group} className="cn-pick-group">
          <div className="cn-pick-label">{g.group}</div>
          <div className="cn-pick-tiles">
            {g.items.map((it) => {
              const ready = NOTE_TYPES_READY.includes(it.type);
              return (
                <button
                  key={it.type}
                  type="button"
                  className={`cn-pick-tile${ready ? '' : ' off'}`}
                  aria-disabled={ready ? undefined : true}
                  onClick={() => (ready ? onPick(it.type) : setWhy(noteTypeOffReason(it.type)))}
                >
                  <b>{it.type}</b>
                  <span>{it.hint}</span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
      {why && (
        <p className="cn-pick-why" role="status">
          {why}
        </p>
      )}
    </Modal>
  );
}

// ── Read-only preview modal + PDF ──────────────────────────────────────────
function PreviewModal({
  kind,
  row,
  contactName,
  clinician,
  onClose,
  action,
  secondary,
}: {
  kind: Kind;
  row: ProgressNote | TreatmentPlan;
  contactName: string;
  clinician: string;
  onClose: () => void;
  /** Open the note in the editor (Edit a draft / Review a pending note), when allowed. */
  action?: { label: string; onClick: () => void };
  /** fbs#97: a second action beside it ("Copy to new note"). */
  secondary?: { label: string; onClick: () => void };
}) {
  const isProgress = kind === 'progress';
  const pn = row as ProgressNote;
  const tp = row as TreatmentPlan;
  const title = isProgress ? pn.sessionLabel : `Treatment plan · ${tp.mode}`;

  const bodyHtml = isProgress ? progressPreview(pn) : planPreview(tp);

  const download = () => {
    const html = isProgress
      ? progressPrint(contactName, clinician, pn)
      : planPrint(contactName, clinician, tp);
    const w = window.open('', '_blank', 'width=800,height=1000');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };

  return (
    <Modal
      title={`${contactName} · ${title}`}
      onClose={onClose}
      actions={
        <>
          <button className="btn" onClick={onClose}>
            Close
          </button>
          <button className={action || secondary ? 'btn' : 'btn primary'} onClick={download}>
            Download PDF
          </button>
          {secondary && (
            <button className={action ? 'btn' : 'btn primary'} onClick={secondary.onClick}>
              {secondary.label}
            </button>
          )}
          {action && (
            <button className="btn primary" onClick={action.onClick}>
              {action.label}
            </button>
          )}
        </>
      }
    >
      <div
        className="em-sub"
        style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}
      >
        <span className="muted">{fmtMDY(row.date)}</span>
        <span className="muted">·</span>
        <span className="muted">
          {isProgress ? NOTE_FORMAT_LABELS[asFormat(pn.format)] : tp.mode}
        </span>
        <span className="muted">·</span>
        <span className="muted">Clinician {clinician}</span>
      </div>
      <NoteStatusPill status={row.status} />
      <div className="cn-note-body" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
    </Modal>
  );
}

function progressPreview(r: ProgressNote): string {
  const goals = r.body?.goalsAddressed ?? [];
  const goalsHtml = goals.length
    ? `<div class="cn-note-sec"><div class="cn-note-sec-t">Goals &amp; objectives addressed</div>${goals
        .map(
          (g) =>
            `<p>${g.addressed ? '☑' : '☐'} ${esc(g.goalText)} — ${esc(g.objectiveText)}${goalStatusText(g) ? ` · <b>${esc(goalStatusText(g))}</b>` : ''}</p>`,
        )
        .join('')}${goalLevelsHtml(r.body?.goalStatuses)}</div>`
    : '';
  const dx = diagnosisLine(r.body);
  const ta = approachLine(r.body);
  const dxHtml =
    `<div class="cn-note-sec"><div class="cn-note-sec-t">Diagnosis codes (ICD-10)</div><p>${esc(dx || '—')}</p></div>` +
    (ta
      ? `<div class="cn-note-sec"><div class="cn-note-sec-t">Treatment approach</div><p>${esc(ta)}</p></div>`
      : '');
  const secs = noteSections(r)
    .map(
      ([label, text]) =>
        `<div class="cn-note-sec"><div class="cn-note-sec-t">${esc(label)}</div><p>${esc(text || '—')}</p></div>`,
    )
    .join('');
  // ehr#724: an Admin / phone note is its boxes only (no goals or diagnosis)
  return adminNote(r) ? secs : goalsHtml + dxHtml + secs;
}
/** ehr#724: a saved note is an Admin / phone note. */
const adminNote = (r: ProgressNote): boolean =>
  isAdminPhoneKind(noteTypeOf({ noteType: r.body?.noteType, sessionLabel: r.sessionLabel }));
function planPreview(r: TreatmentPlan): string {
  const b = r.body || { goals: [] };
  const goals = (b.goals || [])
    .map(
      (g, gi) => `
      <div class="cn-goal">
        <div class="cn-goal-t">Goal ${gi + 1}<span class="cn-goal-meta">${g.target_date ? 'Target ' + esc(g.target_date) : ''}${g.responsible_party ? ' · ' + esc(g.responsible_party) : ''}</span></div>
        <p>${esc(g.goal_text || '')}</p>
        ${(g.objectives || [])
          .map(
            (o, oi) => `
          <div class="cn-objective">
            <div class="cn-objective-t">Objective ${gi + 1}.${oi + 1}<span class="cn-badge ${o.progress_status === 'Met' ? 'cosigned' : o.progress_status === 'In progress' ? 'pending' : 'draft'}">${esc(o.progress_status || 'Not started')}</span></div>
            <p>${esc(o.objective_text || '')}</p>
            ${(o.interventions || [])
              .map(
                (iv) =>
                  `<div class="cn-intervention">${esc(iv.description || '')} · ${esc(iv.frequency || '')} · ${esc(iv.format || '')} · ${esc(iv.length || '')}</div>`,
              )
              .join('')}
          </div>`,
          )
          .join('')}
      </div>`,
    )
    .join('');
  return `${b.presenting_problem ? `<div class="cn-note-sec"><div class="cn-note-sec-t">Presenting problem</div><p>${esc(b.presenting_problem)}</p></div>` : ''}${goals}`;
}

function printStyles() {
  return `body{font-family:'Sora',Arial,sans-serif;font-size:12.5px;color:#0d1f18;margin:28px}
  h1{font-size:22px;margin:0 0 4px}h2{font-size:12px;letter-spacing:.05em;text-transform:uppercase;color:#527060;margin:18px 0 6px}
  .meta{color:#527060;font-size:11.5px;margin-bottom:14px}.sec{margin-bottom:14px}
  .sec-t{font-size:11px;font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:#527060;margin-bottom:3px}
  p{margin:0;line-height:1.55}.goal{border-top:1px solid #dde8e3;padding-top:12px;margin-top:12px}
  .goal-t{font-size:13px;font-weight:700;margin-bottom:4px}.obj{margin:10px 0 0 14px}.obj-t{font-size:11.5px;font-weight:700}
  .iv{font-size:11.5px;color:#527060;margin:2px 0 0 14px}`;
}
function progressPrint(name: string, clinician: string, r: ProgressNote): string {
  // calendar#46: the PDF now carries the goals addressed too (the preview always did).
  const goals = r.body?.goalsAddressed ?? [];
  const goalsHtml = goals.length
    ? `<div class="sec"><div class="sec-t">Goals &amp; objectives addressed</div>${goals
        .map(
          (g) =>
            `<p>${g.addressed ? '☑' : '☐'} ${esc(g.goalText)} — ${esc(g.objectiveText)}${goalStatusText(g) ? ` · <b>${esc(goalStatusText(g))}</b>` : ''}</p>`,
        )
        .join('')}${goalLevelsHtml(r.body?.goalStatuses)}</div>`
    : '';
  const secs = noteSections(r)
    .map(
      ([label, text]) =>
        `<div class="sec"><div class="sec-t">${esc(label)}</div><p>${esc(text)}</p></div>`,
    )
    .join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(name)} ${esc(r.sessionLabel)}</title><style>${printStyles()}</style></head><body>
  <h1>${esc(name)} · ${esc(r.sessionLabel)}</h1>
  <div class="meta">${esc(NOTE_FORMAT_LABELS[asFormat(r.format)])} progress note · ${esc(fmtMDY(r.date))} · clinician ${esc(clinician)}${r.isGroup ? ' · group session' : ''} · ${esc(STATUS_LABEL[r.status] ?? r.status)}</div>
  ${adminNote(r) ? '' : `${goalsHtml}<div class="sec"><div class="sec-t">Diagnosis codes (ICD-10)</div><p>${esc(diagnosisLine(r.body))}</p></div>`}${approachLine(r.body) ? `<div class="sec"><div class="sec-t">Treatment approach</div><p>${esc(approachLine(r.body))}</p></div>` : ''}${secs}</body></html>`;
}
/** ehr#270: what a printed progress note shows (the editor's own values). */
export interface NotePrintData {
  contactName: string;
  status: string;
  format: string;
  date: string;
  /** "3:00 PM", or ''. */
  time: string;
  sessionType: string;
  durationMin: number;
  /** The linked treatment plan's label, or '' for none. */
  planLabel: string;
  body: Record<string, unknown>;
  clinician: NoteSigner;
  /** Null when the note has no co-signature. */
  supervisor: NoteSigner | null;
  /** ehr#544: the signed note's addenda, oldest first. */
  addenda?: NoteAddendumView[];
  /** ehr#713: a no-show (or an absent member's no-show note) prints its start only. */
  startOnly?: boolean;
}
interface NoteSigner {
  name: string;
  signed: boolean;
  signedAt: string | null;
  /** ehr#278: their saved signature (ehr#524: About > Signature), once signed; a PNG/JPG data URL. */
  img?: string | null;
}
const SIG_IMG_RE = /^data:image\/(png|jpeg);base64,[A-Za-z0-9+/=]+$/;

const PROGRESS_TONE: Record<string, string> = { Improved: 'up', Worsened: 'down' };
/** ehr#571: a goal row's pill tone in the printout: a status by its own colour, else the old progress. */
const goalTone = (g: RowStatus & { progress?: string }): string => {
  if (!isGoalStatus(g.status)) return PROGRESS_TONE[g.progress ?? ''] ?? '';
  const t = statusTone(g.status);
  return t === 'ok' ? 'up' : t === 'bad' ? 'down' : '';
};
/** ehr#571: a goal row's status (and note) as text, else its progress from before statuses. */
const goalStatusText = (g: RowStatus & { progress?: string }): string =>
  statusLine(g) || (g.progress ?? '');
/** ehr#571: the goal-level statuses under the rows, in the preview and the simple printout. */
const goalLevelsHtml = (v: unknown): string =>
  readGoalLevels(v)
    .filter((l) => isGoalStatus(l.status))
    .map((l) => `<p>Goal: ${esc(l.goalText)} · <b>${esc(statusLine(l))}</b></p>`)
    .join('');

/** ehr#724: the cards an Admin / phone note's printout leaves out. */
const ADMIN_PRINT_SKIP = [
  'Goals & objectives addressed this session',
  'Problems, goals, and objectives addressed',
  'Diagnosis codes (ICD-10)',
];

/**
 * ehr#270: a progress note printed (or saved as PDF) the way it looks in the editor: the title and
 * status, the session strip, the goals, diagnosis codes and NotePilot cards, each section in its
 * own box, and the Signatures card. Plain HTML + inline CSS for a print window.
 */
/**
 * ehr#437: the note's title on screen and in print. A Family / Couples note and a Group note say so;
 * an Other note says its type, as V58 (19 A) does ("Case management note"); the rest are Progress Notes.
 * ehr#689: a Family / Couples note reads "Couples and Family Notes", as V15 does.
 */
export function noteTitle(kind: string): string {
  if (isFamilyKind(kind)) return FAMILY_NOTE_TITLE;
  if (kind === 'Group') return 'Group Note';
  return isOtherKind(kind) ? `${kind} note` : 'Progress Note';
}

export function progressPrintHtml(d: NotePrintData): string {
  // ehr#724 (ADMIN_PHONE_NOTE_10-30-V7): an Admin / phone note prints nothing about a plan,
  // diagnosis or billing: no Linked plan, goals or Diagnosis card, no POS
  const adminOnly = isAdminPhoneKind(
    noteTypeOf({ noteType: d.body.noteType, sessionLabel: d.sessionType }),
  );
  const goals = (d.body.goalsAddressed as GoalAddressed[] | undefined) ?? [];
  const dx = readDiagnoses(d.body.diagnoses);
  const approaches = strList(d.body.approaches);
  const summary = sanitizeSummaryHtml(String(d.body.sessionSummary ?? ''));
  const pill = STATUS_LABEL[d.status] ?? 'Draft';
  // ehr#437 (V81): start to end, where it was held, and the billing code, when the note has them.
  const ss = (d.body.session ?? {}) as {
    end?: string;
    place?: string;
    code?: string;
    modifier?: string;
    locState?: string;
    clientAt?: string;
    complexity?: boolean;
    icReason?: string;
  };
  // ehr#666: a note saved with a start and length but no end prints start plus length
  const ssEnd = d.startOnly ? '' : sessionEndOf({ ...ss, durationMin: d.durationMin });
  const session = [
    fmtMDY(d.date),
    d.time && ssEnd ? `${d.time} to ${storedTime(ssEnd)}` : d.time,
    d.sessionType,
    d.durationMin && !d.startOnly ? `${d.durationMin} min` : '',
    // ehr#478: a telehealth or phone client's state
    ss.place && ss.place !== 'In person' && ss.locState
      ? `${ss.place}, client in ${ss.locState}`
      : (ss.place ?? ''),
    ss.code ?? '',
    // ehr#543: the billing line's modifier (V81 print: "modifier 95")
    ss.code && ss.modifier ? `modifier ${ss.modifier}` : '',
  ]
    .filter(Boolean)
    .map(esc)
    .join(' · ');
  const sig = (role: string, p: NoteSigner) =>
    `<div class="sig-row"><div>${
      p.signed && p.img && SIG_IMG_RE.test(p.img)
        ? `<img class="sig-img" src="${esc(p.img)}" alt="Signature of ${esc(p.name)}">`
        : ''
    }<div class="sig-role">${esc(role)}</div><div class="sig-name">${esc(p.name || '—')}</div></div>${
      p.signed
        ? `<div class="sig-state ok">✓ Signed${p.signedAt ? ' ' + esc(fmtWhen(p.signedAt)) : ''}</div>`
        : '<div class="sig-state">Not yet signed</div>'
    }</div>`;
  const card = (title: string, inner: string) =>
    adminOnly && ADMIN_PRINT_SKIP.includes(title)
      ? ''
      : `<section class="card"><h3>${esc(title)}</h3>${inner}</section>`;
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(d.contactName)} ${isFamilyKind(String(d.body.noteType ?? '')) ? FAMILY_NOTE_TITLE : 'progress note'} ${esc(fmtMDY(d.date))}</title><style>
  body{font-family:'Sora',Arial,sans-serif;font-size:12.5px;color:#0d1f18;margin:24px;background:#fff}
  .head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:12px}
  .head h1{font-family:'Instrument Serif',Georgia,serif;font-weight:400;font-size:26px;margin:0;display:flex;align-items:center;gap:10px}
  .pill{font-family:Arial,sans-serif;font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;background:#e6f4ec;color:#1a6b4a;border-radius:7px;padding:4px 9px}
  .pill.ns-draft{background:#fdf1c7;color:#7a5600}.pill.ns-pending{background:#e6eeff;color:#2c5bb3}.pill.ns-changes{background:#fdecea;color:#b42318}.pill.ns-cosigned{background:#d9f2f4;color:#0e6470}
  .tmpl{color:#527060;font-size:11.5px}.tmpl b{color:#0d1f18}
  .strip{background:#eef7f2;border:1px solid #d7ebe0;border-radius:10px;padding:10px 14px;display:flex;flex-wrap:wrap;gap:6px 22px;margin-bottom:12px}
  .strip span b{margin-right:4px}
  .card{border:1px solid #dfe8e3;border-radius:10px;padding:12px 14px;margin:0 0 12px;break-inside:avoid}
  .card h3{font-size:13px;margin:0 0 8px}
  .goal{display:flex;justify-content:space-between;gap:12px;padding:6px 0;border-top:1px solid #eef3f0}.goal:first-of-type{border-top:0}
  .goal .t{font-weight:600}.goal .o{color:#527060;font-size:11.5px}
  .gp{white-space:nowrap;align-self:center;border:1px solid #dfe8e3;border-radius:7px;padding:3px 9px;font-size:11.5px}
  .gp.up{background:#e6f4ec;color:#1a6b4a;border-color:#cfe8da}.gp.down{background:#fdecec;color:#b42318;border-color:#f6cfcf}
  .chips{display:flex;flex-wrap:wrap;gap:6px}.chip{border:1px solid #cfe8da;background:#f2f9f5;color:#1a6b4a;border-radius:999px;padding:3px 10px;font-size:11.5px}
  .muted{color:#7a8f84}.sum{margin-top:8px}
  .field{margin:0 0 12px;break-inside:avoid}.field label{display:block;font-size:11px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#527060;margin:0 0 5px}
  .box{border:1px solid #dfe8e3;border-radius:8px;padding:10px;min-height:54px;white-space:pre-wrap;line-height:1.5}
  .sig-row{display:flex;justify-content:space-between;gap:12px;padding:8px 0;border-top:1px solid #eef3f0}.sig-row:first-of-type{border-top:0}
  .sig-role{font-size:10.5px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:#527060}.sig-name{font-weight:700;font-size:13.5px}
  .sig-state{align-self:center;color:#7a8f84}.sig-state.ok{color:#1a6b4a;font-weight:700}
  .sig-img{display:block;max-width:220px;max-height:60px;margin:0 0 4px}
  .foot{margin-top:16px;color:#7a8f84;font-size:10.5px}
  @media print{body{margin:14mm}}
  </style></head><body>
  <div class="head"><h1>${esc(noteTitle(String(d.body.noteType ?? '')))} <span class="pill ${noteStatusClass(d.status || 'draft')}">${esc(pill)}</span></h1><div class="tmpl">Template: <b>${esc(NOTE_FORMAT_LABELS[asFormat(d.format)])}</b></div></div>
  <div class="strip"><span><b>Client:</b>${esc(d.contactName)}</span><span><b>Session:</b>${session}</span>${adminOnly ? '' : `<span><b>Linked plan:</b>${esc(d.planLabel || 'No plan linked')}</span>`}${sessionPrintFacts(
    d.body,
  )
    .map(([k, v]) => `<span><b>${esc(k)}:</b>${esc(v)}</span>`)
    .join(
      '',
    )}${ss.place && !adminOnly ? `<span><b>POS:</b>${esc(posOf(ss.place, ss.clientAt))}</span>` : ''}${ss.complexity && ss.icReason && /\b90785\b/.test(ss.code ?? '') ? `<span><b>90785:</b>${esc(ss.icReason)}</span>` : ''}</div>
  ${card(
    'Goals & objectives addressed this session',
    goals.length
      ? goals
          .map(
            (g) =>
              `<div class="goal"><div><div class="t">${g.addressed ? '☑' : '☐'} ${esc(g.goalText)}</div><div class="o">${esc(g.objectiveText)}</div>${g.note && isGoalStatus(g.status) ? `<div class="o">Note: ${esc(g.note)}</div>` : ''}</div><span class="gp ${goalTone(g)}">${esc(isGoalStatus(g.status) ? statusLabel(g.status, g.statusDate) : g.progress || 'No change')}</span></div>`,
          )
          .join('') +
          readGoalLevels(d.body.goalStatuses)
            .filter((l) => isGoalStatus(l.status))
            .map(
              (l) =>
                `<div class="goal"><div><div class="t">${esc(l.goalText)}</div><div class="o">Goal status</div>${l.note ? `<div class="o">Note: ${esc(l.note)}</div>` : ''}</div><span class="gp ${goalTone(l)}">${esc(statusLabel(l.status, l.statusDate))}</span></div>`,
            )
            .join('')
      : // ehr#708 (Q2): a discharge summary signed with no plan on file says why
        `<div class="muted">${esc(noPlanPrintLine(d.body) ?? 'No linked plan goals.')}</div>`,
  )}
  ${
    // ehr#682 (V141): Problems, goals, and objectives addressed (older notes: their ticked rows)
    addressedFocusOf(d.body).length
      ? card(
          'Problems, goals, and objectives addressed',
          `<div class="chips">${addressedFocusOf(d.body)
            .map((f) => `<span class="chip">${esc(f.label)}</span>`)
            .join('')}</div>`,
        )
      : ''
  }
  ${card(
    'Diagnosis codes (ICD-10)',
    dx.length
      ? `<div class="chips">${dx.map((x) => `<span class="chip">${esc(x.label ? `${x.code} · ${x.label}` : x.code)}</span>`).join('')}</div>`
      : '<div class="muted">None</div>',
  )}
  ${
    approaches.length || summary.trim()
      ? card(
          'Credify NotePilot',
          (approaches.length
            ? `<div class="chips">${approaches.map((a) => `<span class="chip">${esc(a)}</span>`).join('')}</div>`
            : '') + (summary.trim() ? `<div class="sum">${summary}</div>` : ''),
        )
      : ''
  }
  ${noteSections({ format: d.format, body: d.body })
    .map(
      ([label, text]) =>
        `<div class="field"><label>${esc(label)}</label><div class="box">${esc(text)}</div></div>`,
    )
    .join('')}
  ${
    // ehr#544 (V81 print): each addendum with its reason, who signed it and when
    d.addenda?.length
      ? card(
          'Addenda',
          d.addenda
            .map(
              (a) =>
                `<div class="field"><label>Addendum: ${esc(a.reason)}</label><div class="muted">${esc(a.authorName || 'Unknown')}, ${esc(fmtWhen(String(a.signedAt)))}</div><div class="box">${esc(a.text)}</div></div>`,
            )
            .join(''),
        )
      : ''
  }
  ${lateEntryOf(d.body.lateEntry) ? card('Late entry', `<p>${esc(lateText(lateEntryOf(d.body.lateEntry)!.hours))}. Reason: ${esc(lateEntryOf(d.body.lateEntry)!.reason)}</p>`) : ''}
  ${card('Signatures', sig('Clinician', d.clinician) + peerCredentialPrintHtml(d.body) + (d.supervisor ? sig('Co-signed by supervisor', d.supervisor) : ''))}
  <div class="foot">Printed ${esc(fmtWhen(new Date().toISOString()))} · Credify EHR</div>
  </body></html>`;
}

function planPrint(name: string, clinician: string, r: TreatmentPlan): string {
  const b = r.body || { goals: [] };
  const goals = (b.goals || [])
    .map(
      (
        g,
        gi,
      ) => `<div class="goal"><div class="goal-t">Goal ${gi + 1}</div><p>${esc(g.goal_text || '')}</p>
    ${(g.objectives || []).map((o, oi) => `<div class="obj"><div class="obj-t">Objective ${gi + 1}.${oi + 1} · ${esc(o.progress_status || 'Not started')}</div><p>${esc(o.objective_text || '')}</p>${(o.interventions || []).map((iv) => `<div class="iv">${esc(iv.description || '')} · ${esc(iv.frequency || '')} · ${esc(iv.format || '')} · ${esc(iv.length || '')}</div>`).join('')}</div>`).join('')}
    </div>`,
    )
    .join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>${esc(name)} Treatment Plan</title><style>${printStyles()}</style></head><body>
  <h1>${esc(name)} · Treatment plan</h1>
  <div class="meta">${esc(r.mode)} · ${esc(fmtMDY(r.date))} · clinician ${esc(clinician)} · ${esc(STATUS_LABEL[r.status] ?? r.status)}</div>
  ${b.presenting_problem ? `<div class="sec"><div class="sec-t">Presenting problem</div><p>${esc(b.presenting_problem)}</p></div>` : ''}
  ${goals}</body></html>`;
}

// ── Editor (create / edit) ──────────────────────────────────────────────────
const PROGRESS_STATUS_OPTS = ['Not started', 'In progress', 'Met'];

function ClinicalEditor({
  kind,
  editId,
  contactId,
  onClose,
  onSaved,
}: {
  kind: Kind;
  editId: string | null;
  contactId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isProgress = kind === 'progress';
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [loaded, setLoaded] = useState(editId === null);

  // Shared
  const [date, setDate] = useState(localToday);
  // Progress fields
  const [sessionLabel, setSessionLabel] = useState('');
  const [format, setFormat] = useState<Format>('SOAP');
  const [isGroup, setIsGroup] = useState(false);
  const [pbody, setPbody] = useState<Record<string, string>>({});
  // Plan fields
  const [mode, setMode] = useState('Standard');
  const [presenting, setPresenting] = useState('');
  const [goals, setGoals] = useState<Goal[]>([]);

  useEffect(() => {
    if (editId === null) return;
    const path = `/contacts/${contactId}/${isProgress ? 'progress-notes' : 'treatment-plans'}/${editId}`;
    api
      .get<ProgressNote | TreatmentPlan>(path)
      .then((r) => {
        setDate(String(r.date).slice(0, 10));
        if (isProgress) {
          const pn = r as ProgressNote;
          setSessionLabel(pn.sessionLabel);
          setFormat(
            (FORMATS as readonly string[]).includes(pn.format) ? (pn.format as Format) : 'SOAP',
          );
          setIsGroup(pn.isGroup);
          setPbody(
            Object.fromEntries(
              Object.entries(pn.body ?? {}).filter(([, v]) => typeof v === 'string'),
            ) as Record<string, string>,
          );
        } else {
          const tp = r as TreatmentPlan;
          setMode(tp.mode);
          setPresenting(tp.body?.presenting_problem ?? '');
          setGoals(tp.body?.goals ?? []);
        }
        setLoaded(true);
      })
      .catch((e: unknown) => setErr(e instanceof ApiError ? e.message : 'Could not load record.'));
  }, [editId, contactId, isProgress]);

  const save = () => {
    setErr('');
    if (isProgress && !sessionLabel.trim()) {
      setErr('Session label is required.');
      return;
    }
    setBusy(true);
    const body = isProgress
      ? { date, sessionLabel: sessionLabel.trim(), format, isGroup, body: pbody }
      : { date, mode: mode.trim() || 'Standard', body: { presenting_problem: presenting, goals } };
    const collection = isProgress ? 'progress-notes' : 'treatment-plans';
    const req = editId
      ? api.patch(`/contacts/${contactId}/${collection}/${editId}`, body)
      : api.post(`/contacts/${contactId}/${collection}`, body);
    req
      .then(onSaved)
      .catch((e: unknown) => setErr(e instanceof ApiError ? e.message : 'Save failed.'))
      .finally(() => setBusy(false));
  };

  // Goal/objective/intervention mutators
  const addGoal = () =>
    setGoals((g) => [
      ...g,
      { goal_text: '', target_date: '', responsible_party: '', objectives: [] },
    ]);
  const patchGoal = (gi: number, patch: Partial<Goal>) =>
    setGoals((g) => g.map((x, i) => (i === gi ? { ...x, ...patch } : x)));
  const removeGoal = (gi: number) => setGoals((g) => g.filter((_, i) => i !== gi));
  const addObjective = (gi: number) =>
    patchGoalObjectives(gi, (os) => [
      ...os,
      { objective_text: '', progress_status: 'Not started', interventions: [] },
    ]);
  const patchGoalObjectives = (gi: number, fn: (os: Objective[]) => Objective[]) =>
    setGoals((g) => g.map((x, i) => (i === gi ? { ...x, objectives: fn(x.objectives) } : x)));
  const patchObjective = (gi: number, oi: number, patch: Partial<Objective>) =>
    patchGoalObjectives(gi, (os) => os.map((o, i) => (i === oi ? { ...o, ...patch } : o)));
  const removeObjective = (gi: number, oi: number) =>
    patchGoalObjectives(gi, (os) => os.filter((_, i) => i !== oi));
  const addIntervention = (gi: number, oi: number) =>
    patchObjective(gi, oi, {
      interventions: [
        ...(goals[gi]?.objectives[oi]?.interventions ?? []),
        { description: '', frequency: '', format: '', length: '' },
      ],
    });
  const patchIntervention = (gi: number, oi: number, ii: number, patch: Partial<Intervention>) =>
    patchObjective(gi, oi, {
      interventions: (goals[gi]?.objectives[oi]?.interventions ?? []).map((iv, i) =>
        i === ii ? { ...iv, ...patch } : iv,
      ),
    });
  const removeIntervention = (gi: number, oi: number, ii: number) =>
    patchObjective(gi, oi, {
      interventions: (goals[gi]?.objectives[oi]?.interventions ?? []).filter((_, i) => i !== ii),
    });

  return (
    <>
      <div className="pb-overlay open" onClick={onClose} />
      <div
        className="pb-panel open"
        role="dialog"
        aria-modal="true"
        aria-label="Clinical record editor"
      >
        <div className="pb-head">
          <h2>
            <span>
              {editId ? 'Edit' : 'New'} {isProgress ? 'progress note' : 'treatment plan'}
            </span>
          </h2>
          <button className="close-btn" aria-label="Close" onClick={onClose}>
            ✕
          </button>
        </div>
        <div className="pb-body">
          {!loaded ? (
            <p className="muted">Loading…</p>
          ) : (
            <>
              <div className="field-group" style={{ marginBottom: 12 }}>
                <label>Date</label>
                <DateField value={date} onChange={setDate} />
              </div>

              {isProgress ? (
                <>
                  <div className="field-group" style={{ marginBottom: 12 }}>
                    <label>
                      Session label <span style={{ color: 'var(--red)' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={sessionLabel}
                      onChange={(e) => setSessionLabel(e.target.value)}
                      placeholder="e.g. Session 9"
                    />
                  </div>
                  <div
                    style={{ display: 'flex', gap: 12, marginBottom: 12, alignItems: 'flex-end' }}
                  >
                    <div className="field-group" style={{ flex: 1 }}>
                      <label>Format</label>
                      <Dd
                        value={format}
                        searchable={false}
                        onChange={(v) => setFormat(v as Format)}
                        options={FORMATS.map((f) => ({ value: f, label: f }))}
                      />
                    </div>
                    <label className="cn-check">
                      <input
                        type="checkbox"
                        checked={isGroup}
                        onChange={(e) => setIsGroup(e.target.checked)}
                      />
                      <span>Group session</span>
                    </label>
                  </div>
                  {FORMAT_SECTIONS[format].map(([label, key]) => (
                    <div className="field-group" key={key} style={{ marginBottom: 12 }}>
                      <label>{label}</label>
                      <textarea
                        rows={3}
                        value={pbody[key] ?? ''}
                        onChange={(e) => setPbody((b) => ({ ...b, [key]: e.target.value }))}
                      />
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div className="field-group" style={{ marginBottom: 12 }}>
                    <label>Mode</label>
                    <input
                      type="text"
                      value={mode}
                      onChange={(e) => setMode(e.target.value)}
                      placeholder="Initial / Standard"
                    />
                  </div>
                  <div className="field-group" style={{ marginBottom: 12 }}>
                    <label>Presenting problem</label>
                    <textarea
                      rows={2}
                      value={presenting}
                      onChange={(e) => setPresenting(e.target.value)}
                    />
                  </div>
                  <div className="section-label" style={{ marginBottom: 8 }}>
                    Goals
                  </div>
                  {goals.map((g, gi) => (
                    <div className="cn-edit-goal" key={gi}>
                      <div className="cn-edit-goal-head">
                        <strong>Goal {gi + 1}</strong>
                        <button
                          className="pb-row-remove"
                          title="Remove goal"
                          onClick={() => removeGoal(gi)}
                        >
                          ✕
                        </button>
                      </div>
                      <textarea
                        rows={2}
                        placeholder="Goal statement"
                        value={g.goal_text}
                        onChange={(e) => patchGoal(gi, { goal_text: e.target.value })}
                      />
                      <div style={{ display: 'flex', gap: 8, margin: '8px 0' }}>
                        <input
                          type="text"
                          placeholder="Target date (YYYY-MM-DD)"
                          value={g.target_date ?? ''}
                          onChange={(e) => patchGoal(gi, { target_date: e.target.value })}
                        />
                        <input
                          type="text"
                          placeholder="Responsible party"
                          value={g.responsible_party ?? ''}
                          onChange={(e) => patchGoal(gi, { responsible_party: e.target.value })}
                        />
                      </div>
                      {g.objectives.map((o, oi) => (
                        <div className="cn-edit-obj" key={oi}>
                          <div className="cn-edit-goal-head">
                            <span className="muted">
                              Objective {gi + 1}.{oi + 1}
                            </span>
                            <button
                              className="pb-row-remove"
                              title="Remove objective"
                              onClick={() => removeObjective(gi, oi)}
                            >
                              ✕
                            </button>
                          </div>
                          <textarea
                            rows={2}
                            placeholder="Objective"
                            value={o.objective_text}
                            onChange={(e) =>
                              patchObjective(gi, oi, { objective_text: e.target.value })
                            }
                          />
                          <div style={{ margin: '6px 0' }}>
                            <Dd
                              value={o.progress_status ?? 'Not started'}
                              searchable={false}
                              onChange={(v) => patchObjective(gi, oi, { progress_status: v })}
                              options={PROGRESS_STATUS_OPTS.map((s) => ({ value: s, label: s }))}
                            />
                          </div>
                          {o.interventions.map((iv, ii) => (
                            <div className="cn-edit-iv" key={ii}>
                              <input
                                type="text"
                                placeholder="Intervention"
                                value={iv.description}
                                onChange={(e) =>
                                  patchIntervention(gi, oi, ii, { description: e.target.value })
                                }
                              />
                              <input
                                type="text"
                                placeholder="Frequency"
                                value={iv.frequency}
                                onChange={(e) =>
                                  patchIntervention(gi, oi, ii, { frequency: e.target.value })
                                }
                              />
                              <input
                                type="text"
                                placeholder="Format"
                                value={iv.format}
                                onChange={(e) =>
                                  patchIntervention(gi, oi, ii, { format: e.target.value })
                                }
                              />
                              <input
                                type="text"
                                placeholder="Length"
                                value={iv.length}
                                onChange={(e) =>
                                  patchIntervention(gi, oi, ii, { length: e.target.value })
                                }
                              />
                              <button
                                className="pb-row-remove"
                                title="Remove intervention"
                                onClick={() => removeIntervention(gi, oi, ii)}
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                          <button className="btn sm" onClick={() => addIntervention(gi, oi)}>
                            + Intervention
                          </button>
                        </div>
                      ))}
                      <button
                        className="btn sm"
                        onClick={() => addObjective(gi)}
                        style={{ marginTop: 6 }}
                      >
                        + Objective
                      </button>
                    </div>
                  ))}
                  <button className="btn sm" onClick={addGoal} style={{ marginTop: 6 }}>
                    + Goal
                  </button>
                </>
              )}
              {err && <span className="badge urgent">{err}</span>}
            </>
          )}
        </div>
        <div className="pb-foot">
          <button className="fh-btn" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="fh-btn primary" onClick={save} disabled={busy || !loaded}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Full-screen Progress Note editor (#138, PROGRESS_NOTE_A mockup) ──────────
const SESSION_TYPES = ['Individual', 'Group', 'Family', 'Couples', 'Intake', 'Other'];
const REVIEW_ROLES = new Set(['admin', 'manager', 'supervisor']);

function planPickerLabel(p: TreatmentPlan): string {
  return `${p.mode} Treatment Plan${p.cosignedAt ? ` (signed ${fmtMDY(p.cosignedAt)})` : ''}`;
}
function flattenPlanGoals(p: TreatmentPlan | undefined): GoalAddressed[] {
  if (!p) return [];
  const out: GoalAddressed[] = [];
  for (const g of p.body?.goals ?? [])
    for (const o of g.objectives ?? [])
      out.push({
        goalText: g.goal_text || '',
        objectiveText: o.objective_text || '',
        addressed: false,
        progress: 'No change',
        // ehr#571: the plan's own status ("Same as plan", Confirm the other N)
        ...(isGoalStatus(o.progress_status) ? { planStatus: o.progress_status } : {}),
      });
  return out;
}
/** ehr#526: an EHR-written plan as a "Linked plan" choice. */
const ehrLinkable = (p: TreatmentPlan): LinkablePlan => ({
  id: p.id,
  label: planPickerLabel(p),
  goals: flattenPlanGoals(p),
  problems: ehrPlanProblems(p.body?.presenting_problem), // ehr#682
  source: 'ehr',
  signedAt: p.cosignedAt ?? null,
  signed: Boolean(p.cosignedAt), // ehr#855
});
function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return `${fmtMDY(iso)}, ${d.toLocaleTimeString('en-US', timeOpts())}`;
}

/**
 * ehr#437 reopen (V81 Alt + ↓ / Alt + ↑): the next or previous section of the note, from the one
 * with focus (or the first on screen); it scrolls into view and its first box takes the focus.
 */
function jumpSection(dir: 1 | -1) {
  const secs = [...document.querySelectorAll<HTMLElement>('.pn-page-main [data-pn-sec]')];
  if (!secs.length) return;
  const at = (document.activeElement as HTMLElement | null)?.closest<HTMLElement>('[data-pn-sec]');
  let i = at
    ? secs.indexOf(at)
    : secs.findIndex((el) => el.getBoundingClientRect().bottom > 80) - dir;
  i = Math.min(secs.length - 1, Math.max(0, i + dir));
  const el = secs[i]!;
  el.scrollIntoView({ block: 'start', behavior: 'smooth' });
  el.querySelector<HTMLElement>('textarea, input, button[aria-pressed]')?.focus({
    preventScroll: true,
  });
}

/**
 * ehr#662: which note an editor shows, as its React key: a saved note by id, a copy by its source,
 * a new note by how it was started. Another note gets a fresh editor, never the first one's state.
 */
export function editorKey(e: {
  id: string | null;
  copyFrom?: { id: string };
  prefill?: { appointmentKey?: string | null };
  noteType?: string;
}): string {
  if (e.id) return `note:${e.id}`;
  if (e.copyFrom) return `copy:${e.copyFrom.id}`;
  return `new:${e.prefill?.appointmentKey ?? ''}:${e.noteType ?? ''}`;
}

export function ProgressNoteEditor({
  editId,
  prefill,
  copyFrom,
  noteType,
  history = [],
  family,
  group,
  contactId,
  contactName,
  users,
  currentUserId,
  canReviewNotes,
  viewOnly = false,
  onCopy,
  onClose,
  onSaved,
  onDraftSaved,
  onDischarged,
  bindLeave,
}: {
  editId: string | null;
  /** calendar#46 step 6: a new note started from a calendar appointment. */
  prefill?: ApptPrefill;
  /** fbs#97: a new note started as a copy of this saved note. */
  copyFrom?: { id: string; date: string };
  /** ehr#437: the kind picked in "What kind of note?"; a new note starts on that session type. */
  noteType?: string;
  /** ehr#437 part 4: the client's notes (for "From your notes" and {last plan}). */
  history?: ProgressNote[];
  /** ehr#437 (FAMILY_COUPLES_NOTE): this note is one attendee's in a family / couples session. */
  family?: FamilyMode;
  /** ehr#437 (GROUP_NOTE V26): this note is one member's in a group session. */
  group?: GroupMode;
  contactId: string;
  contactName: string;
  users: NoteUser[];
  currentUserId: string | null;
  canReviewNotes: boolean;
  /** ehr#270: shown read-only (a group attendee's note opened from the client's tab). */
  viewOnly?: boolean;
  /** ehr#270: "Copy to new note" from a saved note (was in the preview window). */
  onCopy?: (noteId: string, date: string) => void;
  onClose: () => void;
  onSaved: () => void;
  /**
   * ehr#681 follow-up: Save Draft went through and the editor is going back to the list, which
   * shows "Draft saved". Without it (a family or group session page, which stays open) the card
   * shows above Save Draft.
   */
  onDraftSaved?: () => void;
  /**
   * ehr#708 (Jay 10-30, Q4): a discharge summary became final; what the EHR did (and whether all
   * of it went through), for the list to show as it reopens.
   */
  onDischarged?: (text: string, done: boolean) => void;
  /** ehr#662: hands the parent this note's leave check, for opening another note over it. */
  bindLeave?: (leave: ((go: () => void) => void) | null) => void;
}) {
  const [loaded, setLoaded] = useState(editId === null && !copyFrom);
  const [isGroupNote, setIsGroupNote] = useState(false);
  // ehr#682: a family / couples attendee's note opened on its own holds its session's lock
  const [noteFamilySession, setNoteFamilySession] = useState<string | null>(null);
  // #695 follow-up: a group member's note opened on its own holds its session's lock too
  const [noteGroupSession, setNoteGroupSession] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // ehr#681 follow-up: Save Draft reads "Saving…" (at least 2 s); its card on a session page.
  const [draftSaving, setDraftSaving] = useState(false);
  const [draftNote, setDraftNote] = useState<SaveNotice | null>(null);
  const [err, setErr] = useState('');
  // ehr#274: an error (a required field missing at sign-off, a failed save) opens as a red
  // pop-up; the grey line at the bottom of the note went unseen.
  const [errOpen, setErrOpen] = useState(false);
  useEffect(() => {
    if (err) setErrOpen(true);
  }, [err]);
  // fbs#97: the saved note this one was copied from (kept in the body so the banner and
  // the server's "still word-for-word the source" check survive saving a draft).
  const [copiedFrom, setCopiedFrom] = useState<{ noteId: string; date?: string } | null>(
    copyFrom ? { noteId: copyFrom.id, date: copyFrom.date } : null,
  );

  const [plans, setPlans] = useState<TreatmentPlan[]>([]);
  // ehr#526: the client's Treatment Plan Builder plans (null = not answered / unreachable).
  const [tp, setTp] = useState<{ list: TpPlanList | null; summary: TpPlanSummary | null }>({
    list: null,
    summary: null,
  });
  // A plan picked by hand is never replaced by the default arriving late.
  const planPicked = useRef(false);
  // ehr#189: the clinician's Supervision setting (Provider Profile → Forms and Supervision).
  // Off: their signature alone finishes the note. On: it goes to their co-signing supervisor.
  // Re-read whenever the signed-in user changes (an admin's "View as" on or off): it was read
  // once when the editor opened, so after switching users it kept the other user's setting.
  const [supervisionAll, setSupervision] = useState<SupervisionView | null>(null);
  useEffect(() => {
    let live = true;
    setSupervision(null);
    api
      .get<SupervisionView>('/progress-notes/supervision')
      .then((v) => live && setSupervision(v))
      .catch(() => live && setSupervision(null));
    return () => {
      live = false;
    };
  }, [currentUserId]);
  const [date, setDate] = useState(() => prefill?.date ?? localToday());
  // calendar#46 (PROGRESS_NOTE_A-15): one "Select template" picker with all four
  // templates; the A-4 "Format rights: SOAP only / All formats" toggle is gone.
  const [format, setFormat] = useState<Format>('SOAP');
  const [credify, setCredify] = useState<CredifyFields>(EMPTY_CREDIFY);
  const [diagnoses, setDiagnoses] = useState<NoteDiagnosis[]>([]);
  const [sessionTime, setSessionTime] = useState(prefill?.time ?? '');
  const [sessionType, setSessionType] = useState(prefill?.sessionType ?? noteType ?? 'Individual');
  // ehr#713: a no-show has no length (none pressed, Duration and End blank); 0 is "none"
  // ehr#666: nor does any new note, unless its appointment or session brings its booked length
  const [sessionDuration, setSessionDuration] = useState(() =>
    startLength({ noShow: noteType === NO_SHOW_KIND, booked: prefill?.durationMin }),
  );
  // A family session switched to Couples (or back) is written as that type, as the server does.
  const familyType = family?.type;
  useEffect(() => {
    if (familyType)
      setSessionType(familyType === 'Couples' ? 'Couples Counseling' : 'Family Therapy');
    if (group) setSessionType('Group Therapy');
  }, [familyType, loaded]);
  // GROUP_NOTE: one template for the whole group, set on the session
  const groupFormat = group?.format;
  useEffect(() => {
    if (groupFormat) setFormat(asFormat(groupFormat));
  }, [groupFormat, loaded]);
  // GROUP_NOTE V26 (V73 101 A): this member's Participation and Mood
  const [participation, setParticipation] = useState<(typeof GROUP_PARTICIPATION)[number] | ''>('');
  const [groupMood, setGroupMood] = useState<(typeof GROUP_MOODS)[number] | ''>('');
  // ehr#437 (PROGRESS_NOTE_10-14-V81): the end time, where the session was held, Interventions
  // used, Why treatment continues, Safety, and which sections are folded.
  const [sessionEnd, setSessionEnd] = useState(() =>
    prefill?.time && prefill?.durationMin && noteType !== NO_SHOW_KIND
      ? addMinutes(prefill.time, prefill.durationMin)
      : '',
  );
  const [place, setPlace] = useState<(typeof NOTE_PLACES)[number]>(prefill?.place ?? 'In person');
  // ehr#478 (V81 Type, place and billing): a code picked by hand (undefined: the type and length
  // pick it; '' is No code), +90785 and its reason, the telehealth client's state and where they
  // were, and whether the row is open (null: as Note settings' Open every section says).
  const [codePick, setCodePick] = useState<string | undefined>(undefined);
  // ehr#543: the modifier the note was saved (signed) with.
  const [savedModifier, setSavedModifier] = useState('');
  const [complexity, setComplexity] = useState(false);
  const [icReason, setIcReason] = useState('');
  const [locState, setLocState] = useState('');
  const [clientAt, setClientAt] = useState<'Home' | 'Other'>('Home');
  const [sxOpen, setSxOpen] = useState<boolean | null>(null);
  const [interventionsUsed, setInterventionsUsed] = useState<string[]>([]);
  const [necessity, setNecessity] = useState('');
  const [safety, setSafety] = useState<NoteSafety | null>(null);
  const [folded, setFolded] = useState<Set<string>>(() => new Set());
  const toggleFold = (id: string) =>
    setFolded((f) => {
      const n = new Set(f);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  // ehr#437 part 4 (V81 Quick add): the dot phrases (the practice's and your own).
  const [phrases, setPhrases] = useState<NotePhrasesView | null>(null);
  useEffect(() => {
    let live = true;
    api
      .get<NotePhrasesView>('/note-phrases')
      .then((v) => live && setPhrases(v))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [currentUserId]);
  // ehr#437: the part of the note a sign-off message is about (OK on the red pop-up goes there).
  const [errTarget, setErrTarget] = useState<string | null>(null);
  // ehr#437 part 3: the kind of note it was started as (the picker; body.noteType), and the
  // layouts' own parts (Presenting problem, Mental status, Safety plan review, …) by id.
  // ehr#527: Individual too, so the session type can't turn it into another kind (New Client).
  const [noteKind, setNoteKind] = useState<string | null>(noteType ?? null);
  const [extras, setExtras] = useState<Record<string, string | string[]>>({});
  // ehr#727: the peer specialist credential the note was saved (signed) with (body.peerCredential)
  const [savedPeerCred, setSavedPeerCred] = useState('');
  // ehr#437 part 6: answers to the last plan's follow-up questions, and the History panel.
  const [followUps, setFollowUps] = useState<
    { item: string; ans: 'yes' | 'no'; why?: string; text: string }[]
  >([]);
  const [histOpen, setHistOpen] = useState(false);
  // ehr#437 (FAMILY_COUPLES_NOTE): this attendee's attendance; the client Not present bills 90846.
  const [attendance, setAttendance] = useState<(typeof ATTENDANCE_STATUSES)[number]>('Attended');
  // ehr#437 (FAMILY_COUPLES_NOTE 60 A): Safety between partners, asked of this partner alone.
  const [partnerSafety, setPartnerSafety] = useState<PartnerSafety | null>(null);
  const [pvsReview, setPvsReview] = useState<string | null>(null);
  const [pvsErr, setPvsErr] = useState('');
  // ehr#437 (V81 Measures): the client's scored measures; History opens on a measure from the card.
  const [measures, setMeasures] = useState<Measure[]>([]);
  const [histAt, setHistAt] = useState<{ tab: 'notes' | 'measures'; key?: string }>({
    tab: 'notes',
  });
  useEffect(() => {
    let live = true;
    api
      .get<MeasuresView>(`/contacts/${contactId}/measures`)
      .then((v) => live && setMeasures(Array.isArray(v?.measures) ? v.measures : []))
      .catch(() => live && setMeasures([]));
    return () => {
      live = false;
    };
  }, [contactId]);
  // ehr#437 part 7 (V81 copied-text check): what was copied forward into each box, and the
  // sign-off warnings waiting for a "Sign anyway".
  const [copied, setCopied] = useState<Record<string, CopiedRecord[]>>({});
  const [signWarn, setSignWarn] = useState<string[] | null>(null);
  // ehr#571 (V141): a note signed past the practice's late-entry hours asks for the reason
  const [lateLimit, setLateLimit] = useState(LATE_ENTRY_HOURS_DEFAULT);
  const [lateEntry, setLateEntry] = useState<LateEntry | null>(null);
  const [lateAsk, setLateAsk] = useState<number | null>(null);
  const [lateReason, setLateReason] = useState('');
  const [lateErr, setLateErr] = useState('');
  const noteCopied = (key: string, from: string, text: string) =>
    setCopied((c) => ({
      ...c,
      [key]: [...(c[key] ?? []), { from: from.slice(0, 10), sentences: splitSentences(text) }],
    }));
  const setExtra = (id: string, v: string | string[]) => setExtras((x) => ({ ...x, [id]: v }));
  // ehr#437 (V81 Quick add): the sections this note was given from Quick add, and My changes.
  const [quick, setQuick] = useState<QuickItem[]>([]);
  const [myChanges, setMyChanges] = useState<MyChange[]>([]);
  const [quickAsk, setQuickAsk] = useState<QuickItem | null>(null);
  // ehr#571 (V141 + Add phrase): the box a phrase is being added to
  const [phraseAsk, setPhraseAsk] = useState<{ key: string; label: string } | null>(null);
  const [phraseText, setPhraseText] = useState('');
  const [phraseScope, setPhraseScope] = useState<'client' | 'all'>('client');
  const [phraseErr, setPhraseErr] = useState('');
  useEffect(() => {
    if (!phraseAsk) return;
    setPhraseText('');
    setPhraseScope('client');
    setPhraseErr('');
  }, [phraseAsk]);
  const savePhrase = () => {
    if (!phraseAsk) return;
    const v = phraseText.replace(/\s+/g, ' ').trim();
    if (!v) return setPhraseErr('Type the phrase first.');
    if (phraseHasClientInfo(v, contactName.split(/\s+/)))
      return setPhraseErr('Remove client details (names, dates, phone numbers) from the phrase.');
    const first = contactName.split(' ')[0] ?? contactName;
    api
      .post<NotePhrasesView>('/note-phrases/box-phrases', {
        box: phraseAsk.key,
        phrase: v,
        ...(phraseScope === 'client' ? { contactId } : {}),
      })
      .then((view) => {
        setPhrases(view);
        setNotice(
          phraseScope === 'client'
            ? `Added to ${phraseAsk.label} for ${first} only.`
            : `Added to ${phraseAsk.label} for all clients.`,
        );
        setPhraseAsk(null);
      })
      .catch((e: unknown) =>
        setPhraseErr(e instanceof ApiError ? e.message : 'Could not add the phrase.'),
      );
  };
  const quickInit = useRef(false);
  const [npKey, setNpKey] = useState(0);
  const autoDrafted = useRef(false);
  // ehr#682 (V141 V120, V126): no writing style, Note mode or "How do you want to write notes?"
  // picker; every note runs V141's settings, with the person's own Generate notes with AI On / Off.
  // ehr#682 (V141 V126, V135, V136): the practice's note rules, from GET /practice/clinical
  const [practiceNotes, setPracticeNotes] = useState<PracticeNoteSettings>(() =>
    readPracticeNoteSettings(null),
  );
  const { user: nsUser } = useSession();
  // V141's associate: notes that need a co-signature (Supervision on); unknown until it loads
  const noteRole = noteRoleOf(nsUser?.role, !!supervisionAll?.required);
  const aiBySupervisor = aiSetBySupervisor(noteRole);
  const ownNs = v141NoteSettings(phrases?.noteSettings ?? null);
  // an associate's AI is the practice's setting for associates; off until Supervision is read
  const ns: NoteSettings = {
    ...ownNs,
    ai: supervisionAll ? noteAiFor(ownNs.ai, noteRole, practiceNotes) : 'off',
  };
  // V136: this role must Keep or Revert each supervisor edit before resubmitting
  const keepGate = keepRevertRequired(practiceNotes, noteRole);
  const pillsOn = ns.mode.pills;
  // ehr#682: which Note settings tab it opens on (the Quick add card's cog: Quick add)
  const [nsOpen, setNsOpen] = useState<NoteSettingsTab | null>(null);
  // ehr#682 (V141 gap #43): Sign timeliness or Supervision, opened from the Note settings footer
  const [reportOpen, setReportOpen] = useState<'timeliness' | 'supervision' | null>(null);
  // ehr#437 Payor rules (they live in RCM, rcm#162): the rules of the client's payor on
  // Demographics, for the billing line's modifier and "Billing checks on the note".
  const [payorRules, setPayorRules] = useState<PayorRules | null>(null);
  useEffect(() => {
    let live = true;
    api
      .get<PayorRules | null>(`/payors/rules/${contactId}`)
      .then((r) => live && setPayorRules(r ?? null))
      .catch(() => live && setPayorRules(null));
    return () => {
      live = false;
    };
  }, [contactId]);
  const keysRef = useRef<{
    save: () => void;
    sign: () => void;
    next: () => void;
    list: () => void;
  } | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const k = keysRef.current;
      if (!k || e.defaultPrevented) return;
      // a window open on top of the note has the keys
      if (document.querySelector('.modal-std')) return;
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === 's' || e.key === 'S')) {
        e.preventDefault();
        k.save();
      } else if (mod && e.key === 'Enter') {
        e.preventDefault();
        k.sign();
      } else if (mod && e.key === '.') {
        e.preventDefault();
        k.next();
      } else if (e.altKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault();
        jumpSection(e.key === 'ArrowDown' ? 1 : -1);
      } else if (e.key === '?' && !mod && !e.altKey) {
        const t = e.target as HTMLElement | null;
        if (t && (t.closest('input, textarea, select') || t.isContentEditable)) return;
        e.preventDefault();
        k.list();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);
  const [keysOpen, setKeysOpen] = useState(false);
  const [clearAsk, setClearAsk] = useState(false);
  // ehr#571 (Chase 10-21, ask 7): every item left to sign, listed
  const [leftListOpen, setLeftListOpen] = useState(false);
  const [notice, setNotice] = useState('');
  useEffect(() => {
    if (!notice) return;
    const t = window.setTimeout(() => setNotice(''), 6000);
    return () => window.clearTimeout(t);
  }, [notice]);
  // V81 Box prompts: an example answer, the hint, or nothing
  const promptFor = (key: string, hint: string) =>
    ns.mode.prompt === 'none'
      ? ''
      : ns.mode.prompt === 'example'
        ? (BOX_EXAMPLES[key] ?? hint)
        : hint;
  // ehr#437 reopen (V81 rail): which rail cards are open
  const [railOpen, setRailOpen] = useState<Partial<Record<RailCard, boolean>>>({});
  const railFold = (card: RailCard): RailFold | undefined => {
    const qa = card === 'quick' || card === 'changes';
    if (qa && ns.mode.quickOpen) return undefined; // set to stay open: no fold
    // ehr#571 (V141 V111, V115): every card starts open; its arrow folds it for this note only
    const open = railOpen[card] ?? true;
    return { open, onToggle: () => setRailOpen((r) => ({ ...r, [card]: !open })) };
  };
  const addQuick = (it: QuickItem, log = true) => {
    if (quick.some((q) => q.id === it.id)) return;
    setQuick((l) => [...l, it]);
    if (log) setMyChanges((c) => [...c, { at: new Date().toISOString(), kind: 'add', item: it }]);
  };
  const dropQuick = (it: QuickItem, log = true) => {
    const value = extras[it.id];
    setQuick((l) => l.filter((q) => q.id !== it.id));
    setExtras(({ [it.id]: _gone, ...rest }) => rest);
    if (log)
      setMyChanges((c) => [
        ...c,
        { at: new Date().toISOString(), kind: 'remove', item: it, ...(value ? { value } : {}) },
      ]);
  };
  const quickFilled = (id: string) => {
    const v = extras[id];
    return Array.isArray(v) ? v.length > 0 : !!v?.trim();
  };
  // a filled section asks before its pill takes it off; the section's × always asks (V81 64 A)
  const removeQuick = (it: QuickItem) => (quickFilled(it.id) ? setQuickAsk(it) : dropQuick(it));
  // ehr#571 (V141 V131): off this note, and off this client's or every client's new notes
  const dropForward = (it: QuickItem, scope: 'client' | 'all') => {
    dropQuick(it);
    const first = contactName.split(' ')[0] ?? contactName;
    const next = quickWithout(phrases?.quick ?? EMPTY_QUICK_PREFS, it.id, scope, contactId);
    if (!next) {
      setNotice(`${quickLabel(it)} removed from ${first}'s note.`);
      return;
    }
    api
      .put<NotePhrasesView>('/note-phrases/quick-add', next)
      .then((v) => {
        setPhrases(v);
        setNotice(
          scope === 'client'
            ? `${quickLabel(it)} is off ${first}'s new notes.`
            : `${quickLabel(it)} is off every new note.`,
        );
      })
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.message : 'Could not update your Quick add settings.'),
      );
  };
  // ehr#682 (V141 V129): Show Quick add Off in Note settings > Quick add takes the card off the
  // rail (the old Hide Quick add, same setting); V130: the other rail cards switched off there
  // ehr#731 (NO_SHOW V4): a no-show note has no Quick add, Measures or My changes
  const kindRail = railOffFor(noteKind);
  const undoChange = (i: number) => {
    const ch = myChanges[i];
    if (!ch) return;
    if (ch.kind === 'add') dropQuick(ch.item, false);
    else {
      addQuick(ch.item, false);
      if (ch.value !== undefined) setExtra(ch.item.id, ch.value);
    }
    setMyChanges((c) => c.filter((_, j) => j !== i));
  };
  // A note with no kind of its own (from before part 3) is named by its session type.
  const kind = family
    ? family.type
    : group
      ? 'Group'
      : (noteKind ?? noteTypeOf({ sessionLabel: sessionType, isGroup: isGroupNote }));
  // ehr#728: a Collateral contact note reads its own Side cards switches, not the Quick add tab's
  // ehr#731: plus the cards a no-show note never has
  // ehr#725 (CASE_MANAGEMENT_NOTE_10-30-V9 16 A): a case management note has no Quick add,
  // Measures or My changes card, whatever the switches above say
  const caseRail = caseRailOffFor(kind);
  // ehr#708 (Jay 10-30 Q6): a discharge summary leaves out Quick add, Measures and My changes
  const quickHidden =
    !!phrases?.quick?.hidden ||
    kindRail.quick ||
    caseRail.includes('quick') ||
    cardOffForKind(kind, 'quick');
  // ehr#724 (ADMIN_PHONE_NOTE_10-30-V7 ADMIN_ONLY): an Admin / phone note, nothing about billing
  const adminOnly = isAdminPhoneKind(kind);
  const cardsOff = [
    ...railCardsOff(phrases?.quick ?? EMPTY_QUICK_PREFS, contactId, kind),
    ...kindRail.cards,
    ...caseRail,
    // ehr#724: plus Measures, always off on an Admin / phone note
    ...adminRailCardsOff(kind, []),
    ...DISCHARGE_HIDDEN_CARDS.filter((c) => cardOffForKind(kind, c)),
  ];
  // ehr#437 (V81 autoCode, IS_FAMILY): only the identified client's note bills: family therapy
  // (90847), or without the client when they weren't there (90846).
  const familyCode = family?.billed ? (attendance === 'Not present' ? '90846' : '90847') : '';
  const otherKind = isOtherKind(kind);
  // ehr#708 (DISCHARGE_SUMMARY_NOTE_10-30-V6): a discharge summary's own parts; Jay 10-30 Q5: it
  // goes by the supervisor's Co-sign discharge summaries switch
  const isDischarge = kind === 'Discharge summary';
  const supervision = supervisionFor(supervisionAll, kind);
  // V5: an unreadable Date, Start or End goes back to the saved one, and the card above Save Draft
  // says how to type it (before, the typo sat in the box in red while the old value was signed)
  const snapBack = (field: 'Date' | 'Start time' | 'End time') =>
    isDischarge
      ? () =>
          setDraftNote(
            errNotice(notSavedMessage(field, personalTimeFormat() === '24h'), { at: 'draft' }),
          )
      : undefined;
  // ehr#725 (V9 20 A to 22 A): its plan goals follow Practice note settings (off by default)
  const caseGoals = planGoalsOn(kind, practiceNotes);
  // ehr#571 round 2 (GROUP_NOTE_10-20-V22, 26 B): a member who did not attend has a no-show note
  // ... and so does a family member who did not come (FAMILY_COUPLES_NOTE_10-20-V11); the
  // identified client's Not present note stays a full note (billed 90846)
  const layoutKind = layoutKindOf(
    kind,
    (!!group || (!!family && !family.billed)) && attendance === 'Not present',
  );
  // ehr#713: a saved body of an absent group or family member (written on the no-show layout)
  const absentOf = (b: object) =>
    (b as { attendance?: { status?: string } }).attendance?.status === 'Not present' &&
    (!!group || (!!family && !family.billed));
  const blocks = withQuick(
    // ehr#731: a no-show's required Contact note and Flags (an absent member's note has neither)
    noteBlocks(otherKind ? 'SOAP' : format, layoutKind, {
      planGoals: caseGoals,
      absent: layoutKind !== kind,
    }),
    quick,
    format,
  );
  // ehr#682 (V141 gap #9): the diagnosis is edited in Type, place and billing (its Change window);
  // the layout keeps its Diagnosis part for the sign-off check and field-change cards, but it has
  // no card of its own on the note (a New Client note keeps its section)
  const dxInline = !dxOwnSection(layoutKind);
  const shownBlocks = dxInline ? blocks.filter((b) => b.kind !== 'dx') : blocks;
  const [dxOpen, setDxOpen] = useState(false);
  // Parts a layout starts folded (Treatment plan goals on a Crisis note), once per kind.
  const foldedFor = useRef('');
  useEffect(() => {
    if (foldedFor.current === kind) return;
    foldedFor.current = kind;
    const start = blocks.filter((b) => b.collapsed).map((b) => b.id);
    if (start.length) setFolded((f) => new Set([...f, ...start]));
  }, [kind]);
  // ehr#266: the session types are the practice's Calendar appointment types (the editor's own
  // list when there are none); picking one fills the length from the type.
  const [apptTypes, setApptTypes] = useState<
    { id: string; name: string; durationMin: number | null }[]
  >([]);
  useEffect(() => {
    let live = true;
    api
      .get<{ id: string; name: string; durationMin: number | null }[]>('/appointments/types')
      .then((t) => live && setApptTypes(Array.isArray(t) ? t : []))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  const typeOptions = (() => {
    const names = apptTypes.length ? apptTypes.map((t) => t.name) : SESSION_TYPES;
    // A saved note's type that the list no longer has stays pickable.
    const all = sessionType && !names.includes(sessionType) ? [...names, sessionType] : names;
    return all.map((t) => ({ value: t, label: t }));
  })();
  // A new note starts on one of the practice's own types: a Calendar appointment's own type when
  // the practice has it, else the first type of the picked kind (Individual -> "Individual
  // Therapy"), else the kind's own name; its length too, unless the appointment brought one.
  // ehr#527: never the first type of another kind ("New Client" for Individual, billed 90791).
  useEffect(() => {
    if (editId !== null || copyFrom || !apptTypes.length) return;
    if (apptTypes.some((x) => x.name === sessionType)) return;
    const name = startSessionType(
      noteType ?? noteTypeOf({ sessionLabel: prefill?.typeLabel || sessionType }),
      apptTypes.map((x) => x.name),
      prefill?.typeLabel ?? '',
    );
    if (name === sessionType) return;
    setSessionType(name);
    // ehr#666: the type's usual length is not pressed for the clinician
  }, [apptTypes]);
  const pickType = (name: string) => {
    setSessionType(name);
    setCodePick(undefined); // V81 setSess: a new type picks its own code again
    // ehr#666: nor when a type is picked; Length stays as the clinician left it
  };
  // ehr#437 (V81 Session details): a Length button (or the type's length) sets the end from the
  // start; a new start keeps the length; an end sets the length from the start.
  const setLength = (min: number) => {
    setSessionDuration(min);
    if (sessionTime) setSessionEnd(addMinutes(sessionTime, min));
  };
  const setStart = (v: string) => {
    const t = v ? normalizeTime(v) : '';
    setSessionTime(t);
    if (t && sessionDuration > 0) setSessionEnd(addMinutes(t, sessionDuration));
  };
  const setEnd = (v: string) => {
    const t = v ? normalizeTime(v) : '';
    setSessionEnd(t);
    const m = sessionMinutes(sessionTime, t);
    if (m !== null && m > 0) setSessionDuration(m);
  };
  const [appointmentKey, setAppointmentKey] = useState<string | null>(
    prefill?.appointmentKey ?? null,
  );
  const [linkedPlanId, setLinkedPlanId] = useState<string | null>(null);
  // ehr#855: the unsigned plan a new note linked because the client has no signed plan
  const [unsignedDefault, setUnsignedDefault] = useState<string | null>(null);
  // ehr#708 (Jay 10-30, Q2): a discharge summary with no plan on file: why
  const [noPlanReason, setNoPlanReason] = useState('');
  const [goals, setGoals] = useState<GoalAddressed[]>([]);
  // ehr#571 (V141): each goal's own status (a goal with objectives), and the one open status menu
  const [goalStatuses, setGoalStatuses] = useState<GoalLevel[]>([]);
  // ehr#682 (V141): Problems, goals, and objectives addressed: picked, and taken off this note;
  // the practice's "at least one" setting (on unless an admin turns it off)
  const [focusOn, setFocusOn] = useState<FocusItem[]>([]);
  const [focusOff, setFocusOff] = useState<string[]>([]);
  const [requireFocus, setRequireFocus] = useState(true);
  const [statusPop, setStatusPop] = useState<{ key: string; view: StatusView } | null>(null);
  const [pbody, setPbody] = useState<Record<string, string>>({});
  const [reviewerId, setReviewerId] = useState('');
  // calendar#46 step 5 — Credify NotePilot: approaches, the session summary (limited
  // rich text), sections holding an unedited draft, and the practice's dictation switch.
  const [approaches, setApproaches] = useState<string[]>([]);
  // ehr#478 (V81 Draft with AI): the rough notes, plain text, kept as the summary HTML. A stored
  // summary is written back as it was until the notes are changed.
  const [roughNotes, setRoughNotes] = useState('');
  const roughFrom = useRef({ html: '', text: '' });
  const loadRough = (html: string) => {
    const text = summaryToText(html);
    roughFrom.current = { html, text };
    setRoughNotes(text);
  };
  const [npDrafted, setNpDrafted] = useState<string[]>([]);
  const [dictationEnabled, setDictationEnabled] = useState(false);
  const [serviceCodes, setServiceCodes] = useState<Partial<Record<string, string>>>({});
  // ehr#437 (V81 Interventions tab): the practice's list; admins and managers edit it in Note settings
  const [practiceInterventions, setPracticeInterventions] = useState<string[]>([
    ...INTERVENTION_ITEMS,
  ]);
  // ehr#682 (V141 V127): Show / hide per intervention, for all clients or one client
  const [interHide, setInterHide] = useState<InterventionHide>(EMPTY_INTERVENTION_HIDE);
  const { user: sessionUser } = useSession();
  const editsPracticeLists = canManage(sessionUser?.role);

  const [status, setStatus] = useState('draft');
  const [authorId, setAuthorId] = useState<string | null>(currentUserId);
  const [assignedCosignerId, setAssignedCosignerId] = useState<string | null>(null);
  // ehr#571 (reopen 10-27): who reviews, as the API decides it (null until a saved note loads)
  const [apiCanReview, setApiCanReview] = useState<boolean | null>(null);
  // ehr#571 (reopen 10-27, ask 2): the note's field-change cards as loaded, and comments typed on
  // this side's cards not saved yet
  const [fcLoaded, setFcLoaded] = useState<NoteFieldChange[]>([]);
  const [fcComments, setFcComments] = useState<Record<string, string>>({});
  // ehr#741 (Chase 10-30, points 1A, 1C, 6): the review cards' conversations as stored, and as this
  // person has them now (their replies and ticks not saved yet on top)
  const [storedThreads, setStoredThreads] = useState<ReviewThread[]>([]);
  const [liveThreads, setLiveThreads] = useState<ReviewThread[]>([]);
  // ehr#890 (Chase 11-02, point 2): the person's own cards turned down with the red X this sitting.
  // The change is put back, so the note has no card for it any more; the card stays, red with its
  // X boxed, like a kept card goes green. Its old replies and tick are closed as before (ehr#741
  // point 6), so a later change to the field starts a new, white card.
  const [ownRed, setOwnRed] = useState<OwnRedCard[]>([]);
  // ehr#741 (point 8): the boxes as they were when the note came back
  const [returnedTexts, setReturnedTexts] = useState<Record<string, string> | null>(null);
  // ehr#741 (point 4): the reviewer's saved draft, put back once the note has opened for them
  const draftToRestore = useRef<SavedReviewDraft | null>(null);
  const [draftRestored, setDraftRestored] = useState(false);
  const draftSavedKey = useRef<string | null>(null);
  const [reviewSaving, setReviewSaving] = useState(false);
  // ehr#741 (point 7): Provider Profile photos of the people on the cards, by user id
  const [photos, setPhotos] = useState<Record<string, string>>({});
  // ehr#741 (point 9): the side panel hidden, remembered in this browser
  const [railHidden, setRailHidden] = useState(() => {
    try {
      return window.localStorage.getItem(RAIL_HIDDEN_KEY) === '1';
    } catch {
      return false;
    }
  });
  const hideRail = (on: boolean) => {
    setRailHidden(on);
    try {
      if (on) window.localStorage.setItem(RAIL_HIDDEN_KEY, '1');
      else window.localStorage.removeItem(RAIL_HIDDEN_KEY);
    } catch {
      /* not remembered: fine */
    }
  };
  const [submittedAt, setSubmittedAt] = useState<string | null>(null);
  const [cosignedAt, setCosignedAt] = useState<string | null>(null);
  const [noteId, setNoteId] = useState<string | null>(editId);
  // calendar#46 step 4 — the review loop: rounds, the thread (supervisor notes +
  // replies), and the Request changes / Reply composers.
  const [reviewRound, setReviewRound] = useState(0);
  const [thread, setThread] = useState<ClinicalReviewEventView[]>([]);
  // ehr#682 (V141 V83.4): the reviewer's few minutes to take a note they sent back into review again
  const [takeBackUntil, setTakeBackUntil] = useState<string | null>(null);
  const tookBack = useRef(false);
  // ehr#682 (V141 V86): the Comment history: entries kept on the note (and in the reviewer's own
  // draft), and this person's new ones not saved yet (the API stamps who and when)
  const [logKept, setLogKept] = useState<ReviewLogEntry[]>([]);
  const [logNew, setLogNew] = useState<ReviewLogEntry[]>([]);
  const logNoted = useRef<Record<string, string>>({});
  const [historyOpen, setHistoryOpen] = useState(false);
  // ehr#682 (V141 V92): Show changes / Final text, and the boxes as first sent for review
  const [reviewView, setReviewView] = useState<'changes' | 'final'>('changes');
  const [reviewBase, setReviewBase] = useState<Record<string, string> | null>(null);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  // ehr#571 (reopen 10-24): co-signing over edits or comments not sent yet asks first (V141)
  const [cosignAsk, setCosignAsk] = useState(false);
  // ehr#708 (Jay 10-30, Q4): signing a discharge summary lists what it will change first
  const [dischargeAsk, setDischargeAsk] = useState<{ label: string; run: () => void } | null>(null);
  const dischargeOk = useRef(false);
  const [reviewDraft, setReviewDraft] = useState<ReviewDraft>({ sections: {}, comments: [] });
  // ehr#277: the reviewer's suggested edits and comments (body.suggestions) while the note is
  // back with its author, shown as tracked changes until accepted or rejected.
  const [suggestions, setSuggestions] = useState<NoteSuggestions | null>(null);
  // ehr#571 (reopen 10-24, points 3-5, V141): the one comment being typed (on a section, on words
  // selected in it, or General), and the words just selected in a box, offered as a comment
  const [compose, setCompose] = useState<{ section: string; quote?: string; pos?: number } | null>(
    null,
  );
  const [composeText, setComposeText] = useState('');
  const [selPick, setSelPick] = useState<{
    section: string;
    quote: string;
    pos: number;
    x: number;
    y: number;
  } | null>(null);
  // The Comment button goes away on scroll, Escape or a press anywhere else (V141 V92).
  useEffect(() => {
    if (!selPick) return;
    const hide = () => setSelPick(null);
    const down = (e: MouseEvent) => {
      if (!(e.target as HTMLElement | null)?.closest?.('.tc-sel-pop')) hide();
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hide();
    };
    window.addEventListener('scroll', hide, true);
    document.addEventListener('mousedown', down);
    document.addEventListener('keydown', key);
    return () => {
      window.removeEventListener('scroll', hide, true);
      document.removeEventListener('mousedown', down);
      document.removeEventListener('keydown', key);
    };
  }, [selPick]);
  // ehr#571 (reopen, item 1): the note as it was when the person first touched it (or last saved
  // it); null = untouched. Leaving with anything different asks first.
  // (Not ehr#416's autosave baseline below: that one is taken as the note opens, before the
  // async defaults land, so it would ask about changes nobody made.)
  const leaveBase = useRef<string | null>(null);
  const [leaveAsk, setLeaveAsk] = useState<{ go: () => void; saved: () => void } | null>(null);

  // ehr#571: the signed-in person is named from their session when the people list has no row
  // for them (it was empty for clinicians and for an admin viewing as one: "Signed in as —").
  const userName = (id: string | null) => {
    if (!id) return '—';
    const u = users.find((x) => x.id === id);
    if (u) return [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
    if (sessionUser && id === sessionUser.id)
      return displayName(sessionUser.name, sessionUser.email, '—');
    return '—';
  };

  // Load the contact's treatment plans (for the plan picker + goals). New notes
  // default to the most recent plan and snapshot its goals.
  // ehr#526: the plans built in the Treatment Plan Builder too: the client's live TP plan, with
  // the goals the TP app summarizes for it (/tp-summary), leads the picker and is a new note's
  // default. TP failing leaves the EHR's own plans; nothing here blocks the note.
  useEffect(() => {
    let live = true;
    const ehrP = api
      .get<TreatmentPlan[]>(`/contacts/${contactId}/treatment-plans`)
      .catch(() => [] as TreatmentPlan[]);
    const listP = api
      .get<TpPlanList>(`/contacts/${contactId}/tp-plans`)
      .catch(() => null as TpPlanList | null);
    const sumP = api
      .get<TpPlanSummary>(`/contacts/${contactId}/tp-summary`)
      .catch(() => null as TpPlanSummary | null);
    void ehrP.then((tp) => live && setPlans(tp));
    void Promise.all([listP, sumP]).then(([list, summary]) => live && setTp({ list, summary }));
    void Promise.all([ehrP, listP, sumP]).then(([tp, list, summary]) => {
      // A copy keeps its source note's plan and goals (loaded below); a pick already made stays.
      // ehr#724: an Admin / phone note links no plan (V7: no plan anywhere on it)
      if (!live || editId !== null || copyFrom || planPicked.current || adminOnly) return;
      // ehr#855: the newest signed plan; a draft only when there is no signed plan (and it says so)
      const pick = defaultLinkablePlan(mergeLinkablePlans(tp.map(ehrLinkable), list, summary));
      if (pick) {
        setLinkedPlanId(pick.plan.id);
        setGoals(pick.plan.goals);
        setGoalStatuses([]);
        setUnsignedDefault(pick.unsigned ? pick.plan.id : null);
      }
    });
    return () => {
      live = false;
    };
  }, [contactId, editId, copyFrom]);
  // The picker's choices: TP plans first, then the EHR's own (ehr#526).
  const linkable = useMemo(
    () => mergeLinkablePlans(plans.map(ehrLinkable), tp.list, tp.summary, linkedPlanId),
    [plans, tp, linkedPlanId],
  );
  const linkedPlan = linkable.find((p) => p.id === linkedPlanId);

  // fbs#97: "Copy to new note" - start from a saved note's clinical content. The date,
  // time, appointment, status, signatures and review thread are this note's own, so they
  // start fresh; NotePilot's unedited-draft marks don't carry over.
  useEffect(() => {
    if (editId !== null || !copyFrom) return;
    let live = true;
    api
      .get<ProgressNote>(`/contacts/${contactId}/progress-notes/${copyFrom.id}`)
      .then((n) => {
        if (!live) return;
        setFormat(asFormat(n.format));
        const b = n.body || {};
        setCredify(readCredify(b));
        setDiagnoses(readDiagnoses(b.diagnoses));
        setApproaches(strList(b.approaches));
        loadRough(str(b.sessionSummary));
        const sections: Record<string, string> = {};
        for (const [, key] of FORMAT_SECTIONS.SOAP.concat(
          FORMAT_SECTIONS.DAP,
          FORMAT_SECTIONS.BIRP,
        ))
          sections[key] = sectionText(b, key);
        setPbody(sections);
        // ehr#437: Interventions used and Why treatment continues are clinical content (a copy
        // keeps them); Safety, times and place belong to the session (read on edit only).
        setInterventionsUsed(strList(b.interventionsUsed));
        setNecessity(str(b.necessity));
        setNoteKind(typeof b.noteType === 'string' ? b.noteType : null);
        setExtras(readExtras(b.extras));
        setQuick(quickList(b.quick));
        quickInit.current = true;
        setSessionType(b.session?.type ?? 'Individual');
        setSessionDuration(savedLength(b.session)); // ehr#666: never 50 by default
        setLinkedPlanId(b.linkedPlanId ?? null);
        // ehr#571: a copy starts with no statuses (they were that session's)
        if (b.goalsAddressed && b.goalsAddressed.length)
          setGoals(b.goalsAddressed.map(withoutStatus));
        setLoaded(true);
      })
      .catch(
        (e: unknown) =>
          live && setErr(e instanceof ApiError ? e.message : 'Could not load the note to copy.'),
      );
    return () => {
      live = false;
    };
  }, [editId, contactId, copyFrom]);

  // The practice's clinical-notes settings (dictation is off unless turned on); re-read when Data &
  // settings saves them (ehr#458: an admin turns dictation on from the mic's window).
  useEffect(() => {
    let live = true;
    const load = () =>
      api
        .get<{
          dictationEnabled: boolean;
          serviceCodes?: Record<string, string>;
          interventions?: string[];
          interventionsHidden?: InterventionHide;
          lateEntryHours?: number;
          requireAddressedFocus?: boolean;
        }>('/practice/clinical')
        .then((c) => {
          if (!live) return;
          if (typeof c.lateEntryHours === 'number') setLateLimit(c.lateEntryHours);
          setRequireFocus(requireAddressedFocusOf(c)); // ehr#682
          setDictationEnabled(Boolean(c.dictationEnabled));
          setServiceCodes(c.serviceCodes ?? {});
          if (Array.isArray(c.interventions)) setPracticeInterventions(c.interventions);
          setPracticeNotes(readPracticeNoteSettings(c));
          setInterHide(c.interventionsHidden ?? EMPTY_INTERVENTION_HIDE);
        })
        .catch(() => undefined);
    void load();
    window.addEventListener(CLINICAL_SETTINGS_CHANGED, load);
    return () => {
      live = false;
      window.removeEventListener(CLINICAL_SETTINGS_CHANGED, load);
    };
  }, []);
  // reminders#4: a client who opted out of AI listening gets no dictation in their notes.
  // Fails closed: until the opt-outs answer, or if they can't be read (e.g. a group lead or
  // reviewer who can't open the chart gets the contact 404), dictation stays off.
  const [aiOptOut, setAiOptOut] = useState(true);
  // ehr#458: a mic in every box, on the practice's dictation switch (off for a client opted out of AI listening)
  const { user: me } = useSession();
  const canTurnOnDictation = canManage(me?.role);
  const dictate = { enabled: dictationEnabled, aiOptOut, canTurnOn: canTurnOnDictation };
  useEffect(() => {
    let live = true;
    setAiOptOut(true);
    api
      .get<{ cells: { ai?: { state: string } } }>(`/consent/contacts/${contactId}/opt-outs`)
      .then((v) => live && setAiOptOut(Boolean(v.cells.ai && v.cells.ai.state !== 'on')))
      .catch(() => live && setAiOptOut(true));
    return () => {
      live = false;
    };
  }, [contactId]);

  // Load the note when editing.
  // ehr#682: reloaded (reloadTick) when this copy takes the note over from another tab, or the
  // copy editing it saves while this one is read-only.
  const [reloadTick, setReloadTick] = useState(0);
  useEffect(() => {
    const loadId = reloadTick > 0 ? (noteId ?? editId) : editId;
    if (loadId === null) return;
    let live = true;
    api
      .get<ProgressNote>(`/contacts/${contactId}/progress-notes/${loadId}`)
      .then((n) => {
        if (!live) return;
        setDate(String(n.date).slice(0, 10));
        setFormat(asFormat(n.format));
        const b = n.body || {};
        setCredify(readCredify(b));
        setDiagnoses(readDiagnoses(b.diagnoses));
        setApproaches(strList(b.approaches));
        loadRough(str(b.sessionSummary));
        setNpDrafted(strList(b.npDrafted));
        const sections: Record<string, string> = {};
        for (const [, key] of FORMAT_SECTIONS.SOAP.concat(
          FORMAT_SECTIONS.DAP,
          FORMAT_SECTIONS.BIRP,
        ))
          sections[key] = sectionText(b, key);
        setPbody(sections);
        setInterventionsUsed(strList(b.interventionsUsed));
        setNecessity(str(b.necessity));
        setNoteKind(typeof b.noteType === 'string' ? b.noteType : null);
        setExtras(readExtras(b.extras));
        setSavedPeerCred(str(b.peerCredential)); // ehr#727
        setQuick(quickList(b.quick));
        setMyChanges(Array.isArray(b.myChanges) ? (b.myChanges as MyChange[]) : []);
        quickInit.current = true;
        setSessionTime(b.session?.time ?? '');
        // ehr#666: no end saved (notes from before End time, the demo notes, a group's notes):
        // the start plus the length, saved with the note's next save
        // ehr#713: never on a no-show (or an absent member's no-show note): only its own End
        setSessionEnd(
          b.noteType === NO_SHOW_KIND || absentOf(b)
            ? (b.session?.end ?? '')
            : sessionEndOf(b.session),
        );
        setFollowUps(Array.isArray(b.followUps) ? b.followUps : []);
        {
          const st = (b.attendance as { status?: string } | undefined)?.status;
          if (st && (ATTENDANCE_STATUSES as readonly string[]).includes(st))
            setAttendance(st as (typeof ATTENDANCE_STATUSES)[number]);
          setPartnerSafety((b.partnerSafety as PartnerSafety | undefined) ?? null);
          const pa = b.participation as string | undefined;
          if (pa && (GROUP_PARTICIPATION as readonly string[]).includes(pa))
            setParticipation(pa as (typeof GROUP_PARTICIPATION)[number]);
          const gm = b.groupMood as string | undefined;
          if (gm && (GROUP_MOODS as readonly string[]).includes(gm))
            setGroupMood(gm as (typeof GROUP_MOODS)[number]);
        }
        setCopied(
          b.copied && typeof b.copied === 'object'
            ? (b.copied as Record<string, CopiedRecord[]>)
            : {},
        );
        setPlace(b.session?.place ?? 'In person');
        // ehr#478: the session's billing picks
        setCodePick(typeof b.session?.codePick === 'string' ? b.session.codePick : undefined);
        setSavedModifier(b.session?.modifier ?? '');
        setComplexity(Boolean(b.session?.complexity));
        setIcReason(b.session?.icReason ?? '');
        setLocState(b.session?.locState ?? '');
        setClientAt(b.session?.clientAt === 'Other' ? 'Other' : 'Home');
        setSafety(b.safety ?? null);
        // ehr#864: a group note reads Group Therapy, even one saved with the old Individual default
        setSessionType(groupSessionType(n.isGroup, b.session?.type) ?? 'Individual');
        setSessionDuration(savedLength(b.session)); // ehr#666: never 50 by default
        setLinkedPlanId(b.linkedPlanId ?? null);
        setNoPlanReason(b.noPlanReason ?? ''); // ehr#708
        if (b.goalsAddressed && b.goalsAddressed.length) setGoals(b.goalsAddressed);
        setGoalStatuses(readGoalLevels(b.goalStatuses));
        // ehr#682: a note saved before the section reads its goal rows' "addressed" ticks
        setFocusOn(addressedFocusOf(b));
        setFocusOff(strList(b.focusOff));
        setStatus(n.status);
        setAuthorId(n.authorId);
        setAssignedCosignerId(n.assignedCosignerId);
        setApiCanReview(typeof n.canReview === 'boolean' ? n.canReview : null);
        setTakeBackUntil(n.status === 'changes' ? (n.takeBackUntil ?? null) : null);
        setLogKept(readReviewLog((b as { reviewLog?: unknown }).reviewLog));
        setLogNew([]);
        logNoted.current = {};
        {
          const rb = (b as { reviewBase?: unknown }).reviewBase;
          setReviewBase(rb && typeof rb === 'object' ? (rb as Record<string, string>) : null);
        }
        setFcLoaded(readFieldChanges((b as { fieldChanges?: unknown }).fieldChanges));
        setFcComments({});
        {
          const th = readReviewThreads((b as { reviewThreads?: unknown }).reviewThreads);
          setStoredThreads(th);
          setLiveThreads(th);
          setOwnRed([]);
          const rt = (b as { returnedTexts?: unknown }).returnedTexts;
          setReturnedTexts(rt && typeof rt === 'object' ? (rt as Record<string, string>) : null);
          draftToRestore.current = n.status === 'pending' && n.reviewDraft ? n.reviewDraft : null;
          setDraftRestored(false);
          draftSavedKey.current = null;
        }
        fieldBase.current = null;
        setSubmittedAt(n.submittedAt);
        setCosignedAt(n.cosignedAt);
        setReviewRound(n.reviewRound ?? 0);
        setSuggestions(readSuggestions(b.suggestions));
        setLateEntry(lateEntryOf(b.lateEntry));
        const cf = (b as { copiedFrom?: { noteId?: string; date?: string } }).copiedFrom;
        setCopiedFrom(cf?.noteId ? { noteId: cf.noteId, date: cf.date } : null);
        setNoteId(n.id);
        setAppointmentKey(n.appointmentKey ?? null);
        setIsGroupNote(Boolean(n.groupSessionId));
        setNoteFamilySession(n.groupSessionId && !n.isGroup ? n.groupSessionId : null);
        setNoteGroupSession(n.groupSessionId && n.isGroup ? n.groupSessionId : null);
        setLoaded(true);
        api
          .get<ClinicalReviewEventView[]>(`/contacts/${contactId}/progress-notes/${n.id}/review`)
          .then((t) => live && setThread(t))
          .catch(() => undefined);
      })
      .catch(
        (e: unknown) => live && setErr(e instanceof ApiError ? e.message : 'Could not load note.'),
      );
    return () => {
      live = false;
    };
  }, [editId, contactId, reloadTick]);

  const clinicianSigned = status === 'pending' || status === 'cosigned' || status === 'signed';
  const supervisorSigned = Boolean(cosignedAt) || status === 'cosigned';
  const isAuthor = noteId === null || authorId === currentUserId;
  // ehr#727 (PEER_SUPPORT_NOTE_10-30-V8, 13a): on a Peer support note, the author's own peer
  // specialist credential (their record) while they write it; else the one it was saved with
  const peer = usePeerSupport(kind === PEER_SUPPORT_KIND);
  const peerCredWriting = isAuthor && !viewOnly && (status === 'draft' || status === 'changes');
  const peerCredential = peerCredWriting ? (peer.mine ?? savedPeerCred) : savedPeerCred;
  // ehr#189: never the note's own author (the API refuses it too).
  // ehr#571 (reopen 10-27): the API's answer when it gave one: it also counts the author's primary
  // supervisor as it is now and a supervisor ticked to co-sign their progress notes (ehr#651),
  // where the editor only knew the co-signer stamped on the note at submit.
  const canReview =
    authorId !== currentUserId &&
    (apiCanReview ??
      canReviewNote({ authorId, currentUserId, assignedCosignerId, canReviewNotes }));

  // ── ehr#544 (V81 115 A): addenda on a signed note ─────────────────────────────────────────
  // Shown under the note and in the printout; added by its author, its reviewer or an admin.
  const finalNote = status === 'signed' || status === 'cosigned';
  const [addenda, setAddenda] = useState<NoteAddendumView[]>([]);
  const [addendumOpen, setAddendumOpen] = useState(false);
  useEffect(() => {
    if (!noteId || !finalNote) {
      setAddenda([]);
      return;
    }
    let live = true;
    api
      .get<NoteAddendumView[]>(`/contacts/${contactId}/progress-notes/${noteId}/addenda`)
      .then((a) => live && setAddenda(Array.isArray(a) ? a : []))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [noteId, finalNote, contactId]);
  const canAddendum =
    Boolean(noteId) &&
    finalNote &&
    (authorId === currentUserId || assignedCosignerId === currentUserId || canReviewNotes);

  // ── ehr#478 (V81 Type, place and billing): the code the note bills ─────────────────────────
  // The type and length make it (autoCode); a family or group note's is the session's, anything
  // else can be picked by hand. +90785 goes on the codes it fits.
  // ehr#437 part 3: the picker's "No billing" kinds (No-show, Admin, Treatment plan review,
  // Discharge summary). ehr#708: no code at all on them, picked or automatic (read-only box).
  const noBilling = isNoBillingKind(kind);
  const autoCode = family
    ? familyCode
    : (group && attendance === 'Not present') || noBilling
      ? ''
      : billingCode(sessionType, sessionDuration, kind, serviceCodes);
  const pickable = !family && !group && !noBilling;
  const pick = pickable ? codePick : undefined;
  const autoBase = baseCode(autoCode, undefined);
  const base = baseCode(autoCode, pick);
  const code = noteCode({ auto: autoCode, pick, minutes: sessionDuration, complexity });
  // ehr#437 Payor rules (V81 billingLine): telehealth and phone carry the payor's modifier (95 /
  // 93 when it has none set). ehr#543: saved on the billing line (body.session.modifier) too.
  const modifier = code ? billingModifier(place, payorRules) : '';
  // billed: needs a code to sign (not a No billing kind, a family member, or a member not there)
  const billedNote =
    !noBilling && (family ? family.billed : true) && !(group && attendance === 'Not present');

  type PayloadOver = {
    pbody?: Record<string, string>;
    npDrafted?: string[];
    suggestions?: NoteSuggestions | null;
    lateEntry?: LateEntry | null;
    extras?: Record<string, string | string[]>;
    necessity?: string;
  };
  // ehr#725: a case management note while the practice shows it no plan goals
  const goalsHidden = kind === CASE_MANAGEMENT && !caseGoals;
  const buildPayload = (over: PayloadOver = {}) => {
    const pb = over.pbody ?? pbody;
    const drafted = over.npDrafted ?? npDrafted;
    const sug = over.suggestions === undefined ? suggestions : over.suggestions;
    const late = over.lateEntry === undefined ? lateEntry : over.lateEntry;
    const ex = over.extras ?? extras;
    const keepReason = noPlanReasonToSave({
      reason: noPlanReason,
      linkedPlanId,
      goalRows: goals.length,
    }); // ehr#708
    const body: ProgressBody = {
      ...pb,
      linkedPlanId,
      ...(keepReason ? { noPlanReason: keepReason } : {}), // ehr#708
      session: {
        time: sessionTime || undefined,
        type: sessionType,
        durationMin: sessionDuration > 0 ? sessionDuration : undefined, // ehr#713: none on a no-show
        end: sessionEnd || undefined,
        place,
        code: code || undefined,
        // ehr#543: the modifier the note shows ("90834 · 95"), on the saved billing line
        ...(modifier ? { modifier } : {}),
        // ehr#478: what was picked in Type, place and billing
        ...(pick !== undefined ? { codePick: pick } : {}),
        ...(complexity && !noBilling
          ? { complexity: true, ...(icReason ? { icReason } : {}) }
          : {}),
        ...(place !== 'In person' ? { ...(locState ? { locState } : {}), clientAt } : {}),
      },
      // ehr#682: the rows' "addressed" follows the chips, for what still reads the old tick
      // ehr#725 (V9 22 A): a case management note with plan goals off records none for the plan
      goalsAddressed: goalsHidden ? [] : withAddressedRows(goals, focusOn),
      goalStatuses: goalsHidden ? [] : goalStatuses.filter((g) => g.status),
      focusAddressed: goalsHidden ? [] : focusOn,
      ...(focusOff.length ? { focusOff } : {}),
      ...credify,
      diagnoses,
      approaches,
      sessionSummary:
        roughNotes === roughFrom.current.text ? roughFrom.current.html : textToSummary(roughNotes),
      npDrafted: drafted.filter((k) => (pb[k] ?? '').trim()),
      interventionsUsed,
      necessity: over.necessity ?? necessity,
      ...(safety ? { safety } : {}),
      // ehr#437 part 3: the note's kind and its layout's own parts.
      noteType: kind,
      ...(kind === PEER_SUPPORT_KIND && peerCredential ? { peerCredential } : {}), // ehr#727
      ...(family || group ? { attendance: { status: attendance } } : {}),
      ...((group || family) && participation ? { participation } : {}),
      ...((group || family) && groupMood ? { groupMood } : {}),
      ...(family && partnerSafety ? { partnerSafety } : {}),
      ...(followUps.length ? { followUps } : {}),
      ...(Object.keys(copied).length ? { copied } : {}),
      ...(Object.keys(ex).length ? { extras: ex } : {}),
      ...(quick.length ? { quick } : {}),
      ...(myChanges.length ? { myChanges } : {}),
      ...(copiedFrom ? { copiedFrom } : {}),
      ...(late ? { lateEntry: late } : {}),
      // ehr#277: suggestions not yet accepted or rejected stay with the note on a save.
      ...(sug ? { suggestions: sug } : {}),
      // ehr#571 (reopen 10-27, ask 2): the field-change cards stay with the note; back with its
      // author, their own changes since it came back join them (the server stamps who and when)
      ...(authorCards.current.length ? { fieldChanges: authorCards.current } : {}),
      // ehr#741: the review cards' conversations, with this person's replies and ticks (the server
      // stamps the new replies and keeps the rest as stored)
      ...(liveThreads.length ? { reviewThreads: liveThreads } : {}),
    };
    return {
      date,
      sessionLabel: sessionType || 'Session',
      // Other notes are written on the SOAP base their layouts rename.
      format: otherKind ? 'SOAP' : format,
      isGroup: sessionTypeFrom(sessionType) === 'Group', // ehr#266: e.g. "Group Therapy"
      body,
    };
  };

  const persist = async (built = buildPayload()): Promise<string | null> => {
    // ehr#682 (V141 V86): the author's Comment history entries not saved yet go with the save
    const payload =
      logNew.length && status === 'changes'
        ? { ...built, body: { ...(built.body as object), reviewLog: logNew } as typeof built.body }
        : built;
    // ehr#727: what the note now holds, so it still shows once the note is signed
    if (kind === PEER_SUPPORT_KIND) setSavedPeerCred(str(built.body.peerCredential));
    if (family) {
      // ehr#437: an attendee's note is saved through the session (on their own chart)
      const saved = await api.put<ProgressNote>(
        `/family-notes/${family.sessionId}/attendees/${contactId}/note`,
        { body: payload.body, format: payload.format, date: payload.date },
      );
      setNoteId(saved.id);
      family.notifySaved?.(); // ehr#682: a read-only copy of the session reloads
      return saved.id;
    }
    if (group) {
      const saved = await api.put<ProgressNote>(
        `/group-notes/${group.sessionId}/attendees/${contactId}/note`,
        { body: payload.body },
      );
      setNoteId(saved.id);
      group.onPersist?.(); // ehr#688: a read-only copy of the group in another tab reloads
      return saved.id;
    }
    if (noteId) {
      await api.patch(`/contacts/${contactId}/progress-notes/${noteId}`, payload);
      tabLock.notifySaved(); // ehr#682: a read-only copy in another tab reloads
      return noteId;
    }
    const created = await api.post<ProgressNote>(`/contacts/${contactId}/progress-notes`, {
      ...payload,
      ...(appointmentKey ? { appointmentKey } : {}),
    });
    setNoteId(created.id);
    return created.id;
  };

  // ehr#571: what was saved becomes what "unsaved changes" compares with; `then` is where to go
  // next (the list, or where the person was leaving to).
  // ehr#681 follow-up: Save Draft reads "Saving…" for at least 2 s, then goes back to the list,
  // where "Draft saved" shows (onDraftSaved). The note counts as saved as soon as the server
  // answers. A failure keeps the red pop-up (ehr#274).
  const saveDraft = (then: () => void = onSaved) => {
    const payload = buildPayload();
    setBusy(true);
    setDraftSaving(true);
    setDraftNote(null);
    setErr('');
    atLeast(
      persist(payload).then(() => {
        const key = JSON.stringify(payload);
        leaveBase.current = key;
        baseline.current = key; // ehr#416: saved here, so nothing to autosave
      }),
      SAVE_MIN_MS,
    )
      .then(() => {
        if (onDraftSaved) onDraftSaved();
        else setDraftNote(okNotice(NOTE_DRAFT_SAVED, { at: 'draft' }));
        then();
      })
      .catch((e: unknown) => setErr(noteDraftErrText(e)))
      .finally(() => {
        setBusy(false);
        setDraftSaving(false);
      });
  };

  // calendar#46 step 5: the structured fields NotePilot drafts from.
  const npContext = (): NpContext => {
    const cred = format === 'CREDIFY';
    const withOtherList = (picks: string[], other: string) =>
      cred ? [...picks, ...(other.trim() ? [other.trim()] : [])] : [];
    return {
      session: `${sessionDuration > 0 ? `${sessionDuration}-minute ` : ''}${sessionType.toLowerCase()} session`,
      goals: withAddressedRows(goals, focusOn).map((g) => ({
        goal: g.goalText,
        objective: g.objectiveText,
        checked: g.addressed,
        progress: g.progress,
      })),
      concerns: withOtherList(credify.concerns, credify.concernsOther),
      interventions: withOtherList(credify.interventions, credify.interventionsOther),
      nextSteps: withOtherList(credify.nextSteps, credify.nextStepsOther),
      response: cred ? credify.clientResponse || credify.clientResponseOther.trim() : '',
      approaches,
      diagnoses,
    };
  };
  // ehr#571 (case management round, V20 13 A): a Service note with no rough notes drafts facts only
  const serviceNote = (SERVICE_KINDS as readonly string[]).includes(kind);
  const runServiceDraft = (): string => {
    const shown = new Set(blocks.filter((b) => b.kind === 'text').map((b) => b.id));
    const fields = ['subjective', 'plan'].filter((f) => shown.has(f));
    const r = mergeDrafts(
      serviceDraft({
        kind,
        place,
        start: sessionTime,
        end: sessionEnd,
        minutes: sessionMinutes(sessionTime, sessionEnd),
        lastNext: lastNote ? sectionText(lastNote.body, 'plan') : '',
        goal: goals[0]?.goalText ?? '',
      }),
      fields,
      pbody,
      // ehr#682 (V141 V83.5): empty boxes only
      new Set(),
    );
    setPbody((b) => ({ ...b, ...r.writes }));
    setNpDrafted((d) => Array.from(new Set([...d, ...Object.keys(r.writes)])));
    const names = Object.keys(r.writes).map((k) => boxLabel(k));
    return serviceGeneratedMsg(names, r.skipped.map(boxLabel));
  };
  // ehr#571: a box's name as the note shows it ("What happened" on a Service note)
  const boxLabel = (key: string) =>
    blocks.find((b) => b.id === key)?.label ??
    FORMAT_SECTIONS[format].find(([, k]) => k === key)?.[0] ??
    key;
  const runDraft = (summary: string): string => {
    if (!summary) return serviceNote ? runServiceDraft() : 'Type or dictate the rough notes first.';
    // ehr#437 part 3: only the boxes this note's layout shows.
    const shown = new Set(blocks.filter((b) => b.kind === 'text').map((b) => b.id));
    const fields = (DRAFT_FIELDS[format] ?? []).filter((f) => shown.has(f));
    // #690 follow-up: a group member's note drafts strict, as Draft everyone does (a family
    // attendee's note has a group session too, but is not a group member's: unchanged, #689 Q5).
    const groupMember = Boolean(group) || (isGroupNote && !noteFamilySession && !family);
    const r = mergeDrafts(
      // ehr#724 (V7 V4): an Admin / phone note's Contact note is what was typed, tidied
      adminOnly
        ? adminContactDraft(summary)
        : draftNoteSections(
            summary,
            ...noteDraftArgs(npContext(), { groupMember, clientName: contactName }),
          ),
      fields,
      pbody,
      // ehr#682 (V141 V83.5): fills only the empty boxes. A box with any text (the clinician's, or
      // generated text not reviewed yet) is left alone; Clear note clears the note to start over.
      new Set(),
    );
    setPbody((b) => ({ ...b, ...r.writes }));
    setNpDrafted((d) => Array.from(new Set([...d, ...Object.keys(r.writes)])));
    return generatedMsg({
      drafted: r.drafted,
      skipped: r.skipped.map(boxLabel),
      empty: r.empty.map(boxLabel),
    });
  };
  // ehr#571 (reopen 10-23): the author typing in a box the supervisor edited keeps the
  // supervisor's other edits pending against the new text (lib/trackChanges rebaseSuggestion).
  const rebaseOn = (key: string, v: string) => {
    const sug = suggestions?.sections[key as keyof NoteSuggestions['sections']];
    if (!suggestions || typeof sug !== 'string') return;
    const next = rebaseSuggestion(pbody[key] ?? '', v, sug);
    setSuggestions({ ...suggestions, sections: { ...suggestions.sections, [key]: next } });
  };
  const editSection = (key: string, v: string) => {
    rebaseOn(key, v);
    setPbody((b) => ({ ...b, [key]: v }));
    setNpDrafted((d) => (d.includes(key) ? d.filter((k) => k !== key) : d));
  };
  const replaceSection = (key: string, v: string) => {
    rebaseOn(key, v);
    setPbody((b) => ({ ...b, [key]: v }));
  };
  const noteField = ([label, key, hint]: [string, string, string], className: string) => {
    // ehr#571 (reopen 10-23): in review the Credify Note's boxes work in place too; their margin
    // sits under the box (the template's chips share one card).
    const rv = reviewBox(key, label);
    return (
      <div
        key={key}
        data-pn-target={`sec-${key}`}
        className={rv?.onEdit ? 'tc-editing' : undefined}
      >
        <NoteField
          label={label}
          field={key}
          hint={promptFor(key, hint)}
          value={rv ? rv.value : (pbody[key] ?? '')}
          drafted={!rv?.onEdit && npDrafted.includes(key) && Boolean((pbody[key] ?? '').trim())}
          onAccept={() => setNpDrafted((d) => d.filter((k) => k !== key))}
          className={className}
          context={npContext}
          onEdit={rv?.onEdit ?? ((v) => editSection(key, v))}
          onReplace={rv?.onEdit ?? ((v) => replaceSection(key, v))}
          dictate={dictate}
          showAdjust={!rv?.onEdit}
          boxLabel={rv?.edits ? <EditLabel /> : undefined}
          missing={!rv && missRed.has(key)}
          headExtra={
            rv ? (
              <>
                <EditChip text={editChip(rv.edits)} />
                {rv.icon}
              </>
            ) : undefined
          }
          afterBox={rv?.below}
          marks={rv?.marks}
          onSelectText={rv?.onSelectText}
        />
        {rv?.margin}
      </div>
    );
  };
  // ehr#274: OK on the red pop-up closes it and takes the clinician to the part of the note the
  // message is about (scrolled into view, its first field focused, briefly highlighted).
  // ehr#437: the sign-off bar's "left to sign" links go the same way.
  const closeErrAndGo = () => {
    setErrOpen(false);
    const target = errTarget ?? noteErrTarget(err, FORMAT_SECTIONS[format]);
    setErrTarget(null);
    if (target) goTo(target);
  };
  const goTo = (target: string) => {
    // ehr#478: billing's items are inside Type, place and billing, which opens to show them
    // ehr#682: and so does the diagnosis, which lives there (V141)
    if (target === 'billing' || (target === 'dx' && dxInline)) setSxOpen(true);
    window.requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(`[data-pn-target="${target}"]`);
      if (!el) return;
      // ehr#437: a folded section opens to show what it's about.
      const sec = el.closest<HTMLElement>('[data-pn-sec]');
      if (sec?.dataset.pnSec)
        setFolded((f) => {
          if (!f.has(sec.dataset.pnSec!)) return f;
          const n = new Set(f);
          n.delete(sec.dataset.pnSec!);
          return n;
        });
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      el.querySelector<HTMLElement>('input, textarea, select, button')?.focus({
        preventScroll: true,
      });
      el.classList.add('pn-flash');
      window.setTimeout(() => el.classList.remove('pn-flash'), 1800);
    });
  };
  // ehr#571 (Chase 10-21, ask 3): a comment shows the section it is about, highlighted for a few
  // seconds: the section in the review view when it is open, else the note's own section.
  const showSection = (key: string) => {
    window.requestAnimationFrame(() => {
      const el =
        document.querySelector<HTMLElement>(`[data-tc-sec="${key}"]`) ??
        document.querySelector<HTMLElement>(`[data-pn-target="sec-${key}"]`);
      if (!el) return;
      const sec = el.closest<HTMLElement>('[data-pn-sec]');
      if (sec?.dataset.pnSec)
        setFolded((f) => {
          if (!f.has(sec.dataset.pnSec!)) return f;
          const n = new Set(f);
          n.delete(sec.dataset.pnSec!);
          return n;
        });
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      el.classList.remove('pn-flash-ref');
      void el.offsetWidth; // restart the highlight on a second click
      el.classList.add('pn-flash-ref');
      window.setTimeout(() => el.classList.remove('pn-flash-ref'), 3000);
    });
  };
  // ehr#437 (V81): what is still missing before sign-off, for the bar and for Sign Off.
  // ehr#732 (MEDICATION_MANAGEMENT_NOTE_10-30-V5 V3): on that note the End is also checked for
  // the same as Start, over 4 hours and later than now (lib/endTimeChecks)
  const endIssue = endProblem({
    kind,
    date,
    start: sessionTime,
    end: sessionEnd,
    now: new Date(),
  });
  const endBeforeStart = endIssue === 'before' || endIssue === 'same';
  // ehr#663: the reviewer's check reads their own copy of the boxes (pb, nec)
  const signChecks = (pb: Record<string, string> = pbody, nec: string = necessity) => {
    const parts = leftToSign({
      blocks,
      kind: layoutKind,
      text: pb,
      interventionsUsed,
      necessity: nec,
      safety,
      diagnoses: diagnoses.length,
      start: sessionTime,
      end: sessionEnd,
      // ehr#708: a discharge summary's "Status for ..." items (the goals section's rows)
      goalRows: goalStatusRows(goals, goalStatuses),
      // ehr#708 (Jay 10-30): the practice's Goal status required at sign-off (Q3) and, with no
      // plan on file, the reason why (Q2)
      requireGoalStatus: practiceNotes.requireGoalStatus,
      noPlan: { linkedPlanId, reason: noPlanReason },
      // ehr#682 (V141 V133): at least one picked, when the practice asks, on an individual note
      // (the author's check only: a reviewer co-signs as the server does, without it)
      focus: {
        require: requireFocus && !group && !family && !suggestMode,
        picked: focusOn.length,
        planGoals: caseGoals, // ehr#725: a case management note's, with plan goals shown
      },
      // ehr#727: a Peer support note's credential (the author's check; the server co-signs without)
      ...(isAuthor && !suggestMode ? { peerCredential } : {}),
      // ehr#858: a session dated after today waits (the author's check; the server's day is the
      // practice's). A reviewer co-signs as the server does, without it.
      ...(suggestMode ? {} : { date, today: localToday() }),
      // ehr#853: a Credify Note's Objective and Assessment are required too
      format: otherKind ? 'SOAP' : format,
    });
    // ehr#478: billing's own (V81 order: the times, then billing, then the rest)
    const bill = billingSignChecks({
      billed: billedNote,
      base,
      minutes: sessionDuration,
      complexity,
      icReason,
      // a family or group member who wasn't there has no location to give
      place: (family || group) && attendance === 'Not present' ? 'In person' : place,
      locState,
    });
    return [
      ...signChecksHead(pb),
      ...parts.filter((c) => c.target === 'session'),
      ...endSignChecks(endIssue), // ehr#732
      ...bill,
      ...parts.filter((c) => c.target !== 'session'),
    ];
  };
  const signChecksHead = (pb: Record<string, string>) => [
    ...(group
      ? groupSignChecks({
          present: attendance !== 'Not present',
          participation,
          mood: groupMood,
          text: pb,
          members: group.members,
          summaryWritten: group.summaryWritten,
          own: { firstName: contactName.split(' ')[0] ?? contactName, shared: group.shared },
        })
      : []),
    ...(family
      ? familySignChecks({
          type: family.type,
          present: attendance !== 'Not present',
          partnerSafety,
          billed: family.billed,
          othersAway: family.othersAway,
          summaryWritten: family.summaryWritten,
          firstName: contactName.split(' ')[0] ?? contactName,
        })
      : []),
  ];
  const doSubmit = (over: PayloadOver = {}, confirmed = false) => {
    const pb = over.pbody ?? pbody;
    const drafted = over.npDrafted ?? npDrafted;
    // ehr#571 (reopen 10-23, V141 C2): a returned note resubmits once each supervisor edit is
    // kept or reverted; OK on the pop-up goes to the first one still waiting.
    const sugNow = over.suggestions === undefined ? suggestions : over.suggestions;
    const waiting = status === 'changes' ? pendingEdits(reviewKeys, textsOf(pb), sugNow) : [];
    // ehr#682 (V141 V136): a role the practice doesn't ask to review each edit resubmits with
    // every open edit kept
    if (waiting.length && sugNow && !keepGate) {
      const kept = splitTexts(applySuggestions(textsOf(pb), sugNow));
      doSubmit(
        {
          ...over,
          pbody: kept.pb,
          extras: { ...(over.extras ?? extras), ...kept.bx },
          necessity: kept.nec ?? over.necessity ?? necessity,
          npDrafted: drafted.filter((k) => !(k in sugNow.sections)),
          suggestions: sugNow.comments.length ? { ...sugNow, sections: {} } : null,
        },
        confirmed,
      );
      return;
    }
    if (waiting.length) {
      setErrTarget(waiting[0]!.section === NEC ? 'necessity' : `sec-${waiting[0]!.section}`);
      setErr(
        `Keep or Revert each of ${userName(sugNow?.by ?? null)}'s edits first: ${waiting.length} left.`,
      );
      return;
    }
    // ehr#437 (V81): every required part filled in first; OK on the pop-up goes to the first.
    const left = signChecks(pb).filter((c) => c.target !== 'dx'); // the server checks the diagnosis
    if (left.length) {
      setErrTarget(left[0]!.target);
      // ehr#713: "Fill in the blank (__) in Contact note", not "Fill in Fill in the blank ..."
      const what = left.map((c) => (c.blank ? c.label.replace(/^Fill in /, '') : c.label));
      // ehr#682: nothing picked in Problems, goals, and objectives addressed asks on its own
      setErr(
        left.length === 1 && left[0]!.focus
          ? 'Pick at least one problem, goal, or objective addressed before signing off the note.'
          : `Fill in ${what.join(', ')} before signing off the note.`,
      );
      return;
    }
    // ehr#437 part 7: the warnings (copied text, no plan) are asked about, never blocking.
    if (!confirmed) {
      const w = signWarnings();
      if (w.length) {
        setSignWarn(w);
        return;
      }
    }
    const uneditedDrafts = blocks
      .filter((b) => b.kind === 'text' && drafted.includes(b.id) && (pb[b.id] ?? '').trim())
      .map((b) => b.label);
    if (uneditedDrafts.length) {
      setErr(uneditedAiMsg(uneditedDrafts));
      return;
    }
    // ehr#571 (V141): past the practice's late-entry hours after the session, the reason first
    const ended = sessionEndAt(date, sessionEnd, sessionTime);
    const hours = ended ? hoursAfterSession(ended, new Date()) : 0;
    const late = ended && isLateEntry(hours, lateLimit);
    const reason = (over.lateEntry ?? lateEntry)?.reason.trim() ?? '';
    if (late && !reason) {
      setLateReason('');
      setLateErr('');
      setLateAsk(hours);
      return;
    }
    const entry = late ? { hours, reason } : null;
    // ehr#708 (Q4): a discharge summary says what signing it will change, and waits for OK
    if (isDischarge && !dischargeOk.current) {
      setDischargeAsk({
        label: primaryLabel,
        run: () => {
          dischargeOk.current = true;
          doSubmit(over, true);
        },
      });
      return;
    }
    dischargeOk.current = false;
    setBusy(true);
    setErr('');
    persist(buildPayload({ ...over, lateEntry: entry }))
      .then((id) =>
        id
          ? api.post(`/contacts/${contactId}/progress-notes/${id}/submit`, {
              cosignerId: reviewerId || undefined,
            })
          : null,
      )
      .then((r) => {
        notifyDischarge(r);
        onSaved();
        // ehr#571 round 2 (V22 30 A): the session page opens the next attendee
        (group ?? family)?.onSigned?.();
      })
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.message : 'Could not sign off the note.'),
      )
      .finally(() => setBusy(false));
  };

  // ehr#708 (Q4): what the EHR did once the discharge summary was final, for the list's card
  const notifyDischarge = (r: unknown) => {
    const d = (r as { discharge?: DischargeOutcome } | null)?.discharge;
    if (d) onDischarged?.(dischargeOutcomeText(d), dischargeOutcomeDone(d));
  };
  const doCosign = () => {
    if (!noteId) return;
    setCosignAsk(false);
    // ehr#663: never with a required part blank (the API refuses it too); Chase 10-30, point 1:
    // the list of what is blank opens instead
    if (cosignLeft.length) {
      setLeftListOpen(true);
      return;
    }
    // ehr#708 (Q4): co-signing a discharge summary finishes it: what it will change, first
    if (isDischarge && !dischargeOk.current) {
      setDischargeAsk({
        label: 'Co-sign and finalize',
        run: () => {
          dischargeOk.current = true;
          doCosign();
        },
      });
      return;
    }
    dischargeOk.current = false;
    setBusy(true);
    setErr('');
    // ehr#663 (point 4): what the reviewer typed in the boxes is kept with the co-signature
    const sections = Object.keys(draftSections).length ? { sections: draftSections } : {};
    api
      .post(`/contacts/${contactId}/progress-notes/${noteId}/cosign`, {
        ...reviewerFields(),
        ...sections,
        ...(logNew.length ? { log: logNew } : {}), // ehr#682: the Comment history
      })
      .then((r) => {
        tabLock.notifySaved(); // ehr#682: a read-only copy of the review reloads
        family?.notifySaved?.();
        notifyDischarge(r);
        onSaved();
      })
      .catch((e: unknown) => setErr(e instanceof ApiError ? e.message : 'Could not co-sign.'))
      .finally(() => setBusy(false));
  };

  // calendar#46: a note with the reviewer (pending) or co-signed is locked (the API
  // refuses edits too); returned notes (changes requested) are editable again.
  // ehr#270: notes now open here from the list, so someone else's draft is read-only too (only its
  // author writes it), as is a group attendee's note (written in the group workspace).
  const baseLocked =
    viewOnly ||
    !!group?.readOnly || // ehr#688: the group session is open in another tab of this browser
    status === 'pending' ||
    status === 'cosigned' ||
    status === 'signed' ||
    (!isAuthor && (status === 'draft' || status === 'changes'));
  // ehr#682 (V131 "one open copy per note"): the author's editable copy of a saved note holds it
  // in this browser; a second tab or window of the same note opens read-only until it is free
  // (lib/noteTabLock). Inside a family or group session page the page holds the session's lock.
  // A family attendee's note opened on its own holds its session's key (family:<sessionId>), a
  // group member's its group's (group:<sessionId>, the key the group page holds), and the
  // reviewer's copy of a pending note holds review:<noteId> (lockKeyFor).
  const reviewTurn = !viewOnly && reviewerEditing({ status, canReview });
  const tabLock = useNoteTabLock(
    lockKeyFor({
      noteId,
      loaded,
      editable: !baseLocked,
      reviewing: reviewTurn,
      familySessionId: noteFamilySession,
      groupSessionId: noteGroupSession,
      inSessionPage: !!family || !!group,
    }),
    {
      // another copy took the note: save what this one has unsaved first
      onLost: async () => {
        // the reviewer's edits and comments are only sent with Request changes or Co-sign (the
        // review flow, unchanged): nothing to save; this copy keeps them for Open here instead
        if (reviewTurn) return false;
        const key = payloadKey();
        if (!loaded || key === baseline.current) return false;
        try {
          await persist();
          baseline.current = key;
          leaveBase.current = key;
          return true;
        } catch {
          return false;
        }
      },
      onRegained: (reason) => {
        // a pending note doesn't change while its review is open (a sent review reloads the
        // read-only copies: onRemoteSaved), so the reviewer's unsent work stays as it was
        if (!reviewTurn) {
          baseline.current = null;
          leaveBase.current = null;
          setReloadTick((t) => t + 1);
        }
        setNotice(
          reason === 'other-closed'
            ? 'The other copy closed. This copy can be edited now.'
            : 'This copy can be edited now.',
        );
      },
      onRemoteSaved: () => {
        baseline.current = null;
        leaveBase.current = null;
        setReloadTick((t) => t + 1);
      },
    },
  );
  // ehr#682: the family session page passes its own lock's read-only state down
  const tabReadOnly = tabLock.readOnly || !!family?.tabReadOnly;
  const reviewTabReadOnly = reviewTurn && tabReadOnly;
  const locked = baseLocked || tabReadOnly;
  // ehr#682: the copy editing a note keeps it in the URL, so a reload reopens it (and keeps it)
  // ehr#741 (Chase 10-30, point 5): the reviewer's copy of a pending note too, so a reload as the
  // supervisor keeps them on the note
  const holdsNote =
    tabLock.state === 'editable' && !!noteId && loaded && (!baseLocked || reviewTurn);
  useEffect(() => {
    if (!holdsNote || !noteId || family || group || noteFamilySession) return;
    return holdNoteInUrl(contactId, noteId);
  }, [holdsNote, noteId, contactId]);

  // ehr#416: just before an auto logout, unsaved changes are saved as the draft. "Unsaved" is
  // measured against the note as it opened (or was last saved here); a new note is saved only
  // when something was written in it.
  const baseline = useRef<string | null>(null);
  // ehr#571 (reopen 10-27, ask 2): the note as it opened for this turn (reviewer or author), and
  // the field-change cards the author's saves carry
  const fieldBase = useRef<{ payload: ReturnType<typeof buildPayload>; side: 's' | 'a' } | null>(
    null,
  );
  const authorCards = useRef<NoteFieldChange[]>([]);
  const payloadKey = () => JSON.stringify(buildPayload());
  useEffect(() => {
    if (loaded && baseline.current === null) baseline.current = payloadKey();
  });
  const hasWriting = () =>
    Object.values(pbody).some((v) => v.trim()) ||
    roughNotes.trim() !== '' ||
    JSON.stringify(credify) !== JSON.stringify(EMPTY_CREDIFY) ||
    diagnoses.length > 0 ||
    goals.length > 0 ||
    interventionsUsed.length > 0 ||
    necessity.trim() !== '' ||
    safety !== null ||
    partnerSafety !== null ||
    Object.values(extras).some((v) => (Array.isArray(v) ? v.length > 0 : v.trim() !== ''));
  useEffect(() => {
    const onAutosave = (e: Event) => {
      if (locked || !loaded || busy) return;
      const key = payloadKey();
      if (key === baseline.current || (!noteId && !hasWriting())) return;
      (e as CustomEvent<AutosaveDetail>).detail?.waitFor.push(
        persist().then(() => {
          baseline.current = key;
          return 'progress note';
        }),
      );
    };
    window.addEventListener(AUTOSAVE_EVENT, onAutosave);
    return () => window.removeEventListener(AUTOSAVE_EVENT, onAutosave);
  });
  // ehr#437: the family page saves this attendee's changes before it opens another attendee.
  useEffect(() => {
    (family ?? group)?.bindSave(async () => {
      if (locked || !loaded) return;
      const key = payloadKey();
      if (key === baseline.current || (!noteId && !hasWriting())) return;
      await persist();
      baseline.current = key;
      leaveBase.current = key; // ehr#571
    });
  });
  // FAMILY_COUPLES_NOTE 60 A: a Yes on Safety between partners sends a couples session back to Family.
  useEffect(() => {
    if (quickInit.current || editId || copyFrom || !phrases || !loaded) return;
    quickInit.current = true;
    if ((NOTE_KINDS_WITHOUT_DX as readonly string[]).includes(kind)) return;
    // ehr#728, ehr#729, ehr#732: a Collateral contact, Skills / rehab or Medication management note
    // has no Quick add, so none of its items start on it
    if (!railCardOn(kind, 'quick')) return;
    const items = quickOnNoteFor(phrases.quick ?? EMPTY_QUICK_PREFS, contactId).filter(
      (q) => q.id !== 'r_relsat' || kind === 'Couples',
    );
    if (items.length) setQuick(items);
  }, [phrases, loaded]);
  const openAll = ns.mode.openAll;
  useEffect(() => {
    if (openAll && loaded) setFolded(new Set());
  }, [openAll, loaded]);
  useEffect(() => {
    // ehr#732: a Medication management note never generates on open (Apply to note only)
    if (autoDrafted.current || !loaded || locked || ns.ai !== 'open' || !generatesOnOpen(kind))
      return;
    autoDrafted.current = true;
    const text = roughNotes.trim();
    const empty = !npDrafted.length && Object.values(pbody).every((v) => !v.trim());
    if (text && empty) runDraft(text);
    // ehr#571 (V20 V126): a new Service note opens with the facts drafted
    else if (!text && empty && serviceNote && editId === null && !copyFrom) runDraft('');
  }, [ns.ai, loaded]);
  const pvsYesOpen = partnerSafetyOpen(partnerSafety);
  useEffect(() => {
    if (family && family.type === 'Couples' && pvsYesOpen && loaded) family.onPartnerYes();
  }, [pvsYesOpen, family?.type, loaded]);
  // ehr#189: the author's own draft follows their Supervision setting; a note already with
  // a supervisor (or co-signed) shows the co-signature, a solo-signed note never does.
  // ehr#731: an associate's no-show note goes to the supervisor only when the practice asks for it
  // or a flag is ticked (as the server decides)
  const toSupervisor =
    !!supervision?.required &&
    supervisorReviews({
      kind: layoutKind,
      absentMember: layoutKind !== kind,
      sessionNote: !!group || !!family,
      noShowSignoff: practiceNotes.noShowSupervisorSignoff,
      flags: noShowFlagsOf({ extras }),
      returned: status === 'changes',
    });
  const supervisionOn = isAuthor && supervision ? toSupervisor : true;
  const showCosign =
    status === 'pending' || status === 'cosigned' || (status !== 'signed' && supervisionOn);

  // ehr#277: the template's text sections; what the reviewer has edited or commented on.
  const textSections = FORMAT_SECTIONS[format].map(
    ([label, key]) => [label, key] as [string, string],
  );
  // ehr#571 (reopen 10-24, V141): the reviewer edits a pending note as soon as it opens; it no
  // longer waits behind Request Changes under the author's "Locked until returned" banner.
  // ehr#682: a second tab of the same review is read-only until it is free
  const suggestMode = reviewerEditing({ status, canReview }) && !reviewTabReadOnly;
  // ehr#571 (reopen 10-27): the reviewer changes the other fields too (V141 V92: "the supervisor
  // can now edit everything during their review turn"); applied as they co-sign or send it back.
  const fieldsLocked = locked && !suggestMode;
  const tracked = status === 'changes' && suggestions !== null;
  // ehr#571 (reopen 10-27): the layout's own boxes (a New Client note's Presenting problem and
  // Relevant history, a Quick add box) are reviewed like the template's: their text lives in
  // body.extras, so the review reads and writes it there.
  const boxIds = blocks.filter((b) => b.kind === 'box').map((b) => b.id);
  const isBox = (k: string) => boxIds.includes(k);
  const boxText = (k: string) => {
    const v = extras[k];
    return typeof v === 'string' ? v : '';
  };
  // ehr#663 (Chase 10-30, point 3): Why treatment continues (body.necessity, under Assessment on a
  // therapy note) is a review box too: the reviewer edits it, the author keeps or reverts it.
  const necOn =
    (THERAPY_KINDS as readonly string[]).includes(kind) &&
    blocks.some((b) => b.id === 'assessment');
  const mineOf = (k: string) => (k === NEC ? necessity : isBox(k) ? boxText(k) : (pbody[k] ?? ''));
  const textsOf = (pb: Record<string, string>): Record<string, string> => ({
    ...pb,
    ...Object.fromEntries(boxIds.map((k) => [k, boxText(k)])),
    ...(necOn ? { [NEC]: necessity } : {}),
  });
  const reviewTexts = textsOf(pbody);
  const splitTexts = (m: Record<string, string>) => {
    const pb: Record<string, string> = {};
    const bx: Record<string, string> = {};
    let nec: string | undefined;
    // a box only when its text changed (a box the note never had stays out of body.extras)
    for (const [k, v] of Object.entries(m))
      if (k === NEC) nec = v;
      else if (!isBox(k)) pb[k] = v;
      else if (v !== boxText(k)) bx[k] = v;
    return { pb, bx, nec };
  };
  const reviewSections: [string, string][] = [
    ...textSections,
    ...blocks
      .filter(
        (b) => (b.kind === 'text' || b.kind === 'box') && !textSections.some(([, k]) => k === b.id),
      )
      .map((b) => [b.label, b.id] as [string, string]),
    ...(necOn ? [[NEC_LABEL, NEC] as [string, string]] : []),
  ];
  const reviewKeys = reviewSections.map(([, k]) => k);

  // ── ehr#571 (reopen 10-27, Chase ask 2; V141 V94 / V96 / V101): field-change cards ──────────
  // After the first submission, a change to a field that isn't a text box shows a card beside it.
  // The reviewer's turn (pending) and the author's (sent back) each start from the note as it
  // opened; the cards already on the note come with it.
  const fcSide: 's' | 'a' | null = suggestMode
    ? 's'
    : status === 'changes' && isAuthor && !locked
      ? 'a'
      : null;
  const planName = (id: string) => linkable.find((p) => p.id === id)?.label ?? 'A treatment plan';
  const shotOf = (p: { date: string; body: ProgressBody }) =>
    fieldSnapshot(p as never, blocks, { planLabel: planName, codeLabel: cptLabel });
  const nowPayload = loaded && fcSide ? buildPayload() : null;
  if (loaded && fcSide && nowPayload && fieldBase.current === null)
    fieldBase.current = { payload: nowPayload, side: fcSide };
  const fbase = fieldBase.current && fieldBase.current.side === fcSide ? fieldBase.current : null;
  const liveChanges =
    fbase && nowPayload ? liveFieldChanges(shotOf(fbase.payload), shotOf(nowPayload)) : [];
  const fieldCards: NoteFieldChange[] = fcSide
    ? mergeFieldChanges(
        fcLoaded,
        liveChanges,
        fcSide,
        { by: currentUserId ?? '', round: reviewRound },
        fcComments,
        // ehr#741: each card keeps the field's value before the change, for its red X
        (k) => (fbase ? fieldRaw(k, fbase.payload as never) : undefined),
      )
    : fcLoaded;
  authorCards.current = fcSide === 's' ? fcLoaded : fieldCards;
  // ehr#890 (review): a list card's Added and Removed items are read against the labels the field
  // holds (the note now, the note as it opened), so an item with a comma in it stays one item
  const cardsPayload = nowPayload ?? (fieldCards.length ? buildPayload() : null);
  const partsOfCard = (c: NoteFieldChange) => {
    const field = c.key.slice(2);
    const known = rawLabels(
      cardsPayload ? fieldRaw(field, cardsPayload as never) : undefined,
      fbase ? fieldRaw(field, fbase.payload as never) : undefined,
    );
    return cardParts(c, fieldChangeText, known);
  };
  // ehr#890 (review): the person's own red card goes for good once its field is changed again (a
  // new card with its id), so undoing that later change can't bring back a stale red card
  const liveCardIds = fieldCards
    .flatMap((c) => partsOfCard(c))
    .map((x) => x.id)
    .join('|');
  useEffect(() => {
    const ids = new Set(liveCardIds.split('|'));
    setOwnRed((r) => (r.some((f) => ids.has(f.id)) ? r.filter((f) => !ids.has(f.id)) : r));
  }, [liveCardIds]);
  const fieldCount = liveChanges.length;

  // ── ehr#741 (Chase 10-30, points 1A, 1C, 6, 7): the cards' check, X, replies and photos ──────
  /** Replies and ticks are open to whoever has the note now: its reviewer, or its author sent back. */
  const cardsOpen = suggestMode || fcSide === 'a';
  const cardMe = { by: currentUserId ?? '', round: reviewRound };
  // ehr#741 (Chase 10-31, point 6): a card closed with the red X counts as never made
  const threadOf = (id: string) => liveThreads.find((t) => t.id === id && !t.closed);
  /** ehr#682 (V141 V86): the name the Comment history gives a note part. */
  const secName = (k: string) =>
    k === GENERAL
      ? 'General'
      : k === NEC
        ? NEC_LABEL
        : (reviewSections.find(([, id]) => id === k)?.[0] ??
          blocks.find((b) => b.id === k)?.label ??
          fieldCards.find((c) => c.sec === k)?.label ??
          k);
  /** ehr#682 (V141 V86): one comment action into the Comment history, with its words. */
  const addLog = (kind: ReviewLogKind, section: string | undefined, text?: string) => {
    const id = `l${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    logNoted.current[id] = new Date().toISOString();
    const e = newLogEntry(kind, {
      id,
      by: currentUserId ?? '',
      round: reviewRound,
      ...(section ? { section } : {}),
      ...(text ? { text } : {}),
    });
    setLogNew((l) => [...l, e]);
  };
  /** What a card is about, for the history: a comment's words, or the field change. */
  const cardWords = (id: string) => {
    const t = liveThreads.find((x) => x.id === id);
    if (t?.text) return t.text;
    const c = fieldCards.find((x) => cardThreadId(x.key) === id);
    return c ? `${c.label}: ${c.from || 'blank'} to ${c.to || 'blank'}` : undefined;
  };
  const replyOn = (t: { id: string; section: string }, text: string) => {
    if (text.trim()) addLog('reply', secName(t.section), text.trim());
    return setLiveThreads((l) =>
      withReply(
        l,
        t,
        cardMe,
        text,
        `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
      ),
    );
  };
  const tick = (t: { id: string; section: string }, quiet = false) => {
    if (!quiet)
      addLog(threadOf(t.id)?.resolved ? 'reopen' : 'resolve', secName(t.section), cardWords(t.id));
    setLiveThreads((l) => withTick(l, t, cardMe));
  };
  const photoOf = (id: string | null | undefined) => (id ? photos[id] : undefined);
  const repliesOf = (t: ReviewThread | undefined) =>
    (t?.replies ?? []).map((r, i) => ({
      key: r.id ?? `${i}`,
      who: userName(r.by || currentUserId),
      photo: photoOf(r.by || currentUserId),
      when: r.at ? timeOf(r.at) : undefined,
      text: r.text,
    }));
  const photoIds = [
    ...new Set(
      [
        authorId,
        assignedCosignerId,
        currentUserId,
        suggestions?.by,
        ...fieldCards.map((c) => c.by),
        ...liveThreads.flatMap((t) => [t.by, ...t.replies.map((r) => r.by)]),
      ].filter((x): x is string => !!x),
    ),
  ]
    .sort()
    .join(',');
  useEffect(() => {
    if (!photoIds) return;
    let live = true;
    api
      .get<Record<string, string>>(`/me/team-photos?ids=${encodeURIComponent(photoIds)}`)
      .then((m) => live && setPhotos(m && typeof m === 'object' ? m : {}))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [photoIds]);
  const draftSections = Object.fromEntries(
    Object.entries(reviewDraft.sections).filter(([k, v]) => v !== mineOf(k)),
  );
  // ehr#571 (reopen 10-24, V141): the comments posted so far, plus one still being typed (so
  // Request changes never drops it); General comments go as the note to the author.
  const typing: DraftComment[] =
    compose && composeText.trim()
      ? [{ id: 'typing', ...compose, text: composeText, at: new Date().toISOString() }]
      : [];
  const { comments: draftComments, reason: reviewReason } = reviewPayload([
    ...reviewDraft.comments,
    ...typing,
  ]);
  const draftCount =
    Object.keys(draftSections).length + reviewDraft.comments.length + typing.length;
  // ehr#571 (reopen 10-23): each supervisor edit still waiting on the author's Keep or Revert
  const waitingEdits = pendingEdits(reviewKeys, reviewTexts, tracked ? suggestions : null);
  const editCount = waitingEdits.length;
  const commentCount = suggestions?.comments.length ?? 0;
  const inReview = suggestMode || tracked;
  // ehr#663: the reviewer's Co-sign & Finalize waits for the same parts as signing off (the API
  // refuses it too); what is blank is listed the same way, and Request changes sends it back.
  // ehr#663 (Chase 10-30, point 4): read with the reviewer's own edits: a box they filled in is not
  // blank (Co-sign keeps their text), so it no longer stays on the list.
  const mineNow = reviewerTexts(pbody, draftSections, necessity, NEC);
  const cosignLeft = suggestMode && loaded ? signChecks(mineNow.pb, mineNow.nec) : [];
  /** ehr#663: comments not sent yet (Co-sign drops them; it keeps the edits). */
  const unsentComments = reviewDraft.comments.length + typing.length;
  const supName = userName(suggestions?.by ?? null);
  const bannerComments = commentBullets(reviewSections, suggestions?.comments ?? []);
  const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;

  /**
   * ehr#277: the author takes (Keep all) or drops (Revert all) every edit the reviewer made.
   * ehr#571 (reopen 10-23): the reviewer's comments stay on the note until it is resubmitted.
   */
  const resolveSuggestions = (accept: boolean) => {
    if (!suggestions) return;
    for (const key of Object.keys(suggestions.sections))
      addLog(accept ? 'keep' : 'revert', secName(key), 'every edit in it');
    // ehr#571 (reopen 10-27): a layout box's text goes back to body.extras
    const {
      pb: nextBody,
      bx,
      nec,
    } = splitTexts(accept ? applySuggestions(reviewTexts, suggestions) : reviewTexts);
    const nextNec = nec ?? necessity;
    const nextExtras = Object.keys(bx).length ? { ...extras, ...bx } : extras;
    // A section the reviewer rewrote is no longer an unedited NotePilot draft.
    const nextDrafted = accept ? npDrafted.filter((k) => !(k in suggestions.sections)) : npDrafted;
    const nextSug = suggestions.comments.length ? { ...suggestions, sections: {} } : null;
    const over: PayloadOver = {
      pbody: nextBody,
      npDrafted: nextDrafted,
      suggestions: nextSug,
      extras: nextExtras,
      necessity: nextNec,
    };
    const settle = () => {
      setPbody(nextBody);
      setExtras(nextExtras);
      setNecessity(nextNec);
      setNpDrafted(nextDrafted);
      setSuggestions(nextSug);
    };
    setBusy(true);
    setErr('');
    persist(buildPayload(over))
      .then(settle)
      .catch((e: unknown) => setErr(e instanceof ApiError ? e.message : 'Save failed.'))
      .finally(() => setBusy(false));
  };

  /** ehr#571 (reopen 10-23, V141 C3): Keep or Revert one supervisor edit. */
  const reviewEdit = (key: string, i: number, keep: boolean) => {
    if (!suggestions) return;
    markTouched();
    {
      const sug = suggestions.sections[key as keyof NoteSuggestions['sections']];
      const h =
        typeof sug === 'string' ? editHunks(mineOf(key), sug).find((x) => x.i === i) : undefined;
      addLog(keep ? 'keep' : 'revert', secName(key), h ? editText(h) : undefined);
    }
    const r = (keep ? keepEdit : revertEdit)(reviewTexts, suggestions, key, i);
    if (keep) {
      const { pb, bx, nec } = splitTexts(r.body);
      setPbody(pb);
      if (nec !== undefined) setNecessity(nec);
      if (Object.keys(bx).length) setExtras((x) => ({ ...x, ...bx }));
      setNpDrafted((d) => d.filter((k) => k !== key));
    }
    setSuggestions(r.suggestions);
  };
  /** ehr#571 (reopen 10-23): the supervisor's copy of one box; back to the author's text drops it. */
  const setDraftSection = (key: string, v: string) =>
    setReviewDraft((d) => {
      const sections = { ...d.sections };
      if (v === mineOf(key)) delete sections[key];
      else sections[key] = v;
      return { ...d, sections };
    });
  /**
   * ehr#571 (reopen 10-24, point 3, V141): open the comment card (Cancel / Comment) on a section,
   * on words selected in it, or General. One card at a time, as in V141.
   */
  const openComposer = (section: string, quote?: string, pos?: number) => {
    setCompose(quote ? { section, quote, pos } : { section });
    setComposeText('');
    setSelPick(null);
  };
  /** The conversation icon: a comment on the whole section (a second click closes an empty one). */
  const toggleComment = (key: string) => {
    if (compose?.section === key && !compose.quote && !composeText.trim()) {
      setCompose(null);
      return;
    }
    openComposer(key);
  };
  const cancelComment = () => {
    setCompose(null);
    setComposeText('');
  };
  /** Comment: the card joins the margin; it goes to the author with Request changes. */
  const postComment = () => {
    const text = composeText.trim();
    if (!compose || !text) return;
    const at = new Date().toISOString();
    addLog('comment', secName(compose.section), text);
    setReviewDraft((d) => ({
      ...d,
      comments: [
        ...d.comments,
        { id: `c${Date.now().toString(36)}-${d.comments.length}`, ...compose, text, at },
      ],
    }));
    cancelComment();
  };
  /** The red X on a comment not sent yet (V141). */
  const deleteComment = (id: string) => {
    const c = reviewDraft.comments.find((x) => x.id === id);
    if (c) addLog('delete', secName(c.section), c.text);
    setReviewDraft((d) => ({ ...d, comments: d.comments.filter((x) => x.id !== id) }));
  };
  /** ehr#571 (V141 V92): clicking a comment on some words selects them in the box. */
  const showWords = (key: string, quote: string, pos?: number) => {
    const ta = document.querySelector<HTMLTextAreaElement>(
      `[data-pn-target="sec-${key}"] .pn-ta-wrap textarea`,
    );
    const at = ta && findAnchor(ta.value, quote, pos);
    if (!ta || !at) return;
    ta.scrollIntoView({ block: 'center', behavior: 'smooth' });
    ta.focus({ preventScroll: true });
    ta.setSelectionRange(at.start, at.end);
  };
  /** The comment card being typed, in the margin it belongs to. */
  const composer = (section: string, label?: string) =>
    compose?.section === section ? (
      <CommentComposer
        key={`${section}|${compose.quote ?? ''}|${compose.pos ?? ''}`}
        who={userName(currentUserId)}
        photo={photoOf(currentUserId)}
        label={label}
        section={section}
        quote={compose.quote}
        value={composeText}
        onChange={setComposeText}
        onCancel={cancelComment}
        onPost={postComment}
        focus
        dictate={dictate}
      />
    ) : null;
  /** A posted comment not sent yet: the reviewer can still delete it. */
  const draftCard = (c: DraftComment, label?: string) => (
    <CommentCard
      key={c.id}
      who={userName(currentUserId)}
      photo={photoOf(currentUserId)}
      label={label}
      text={c.text}
      quote={c.quote}
      when={timeOf(c.at)}
      onDelete={() => deleteComment(c.id)}
      onShow={c.quote ? () => showWords(c.section, c.quote!, c.pos) : undefined}
    />
  );
  /**
   * ehr#571 (reopen 10-24, point 5, V141 V115): General comments, beside the note's first section
   * in place of the old intro (key, instructions and the note to the author).
   */
  const generalHost = suggestMode && blocks[0]?.kind === 'text' ? blocks[0].id : null;
  const generalComments = () =>
    suggestMode ? (
      <div className="tc-general" data-tc-general>
        {reviewDraft.comments.filter((c) => c.section === GENERAL).map((c) => draftCard(c))}
        {compose?.section === GENERAL ? (
          composer(GENERAL)
        ) : (
          <button
            type="button"
            className="pn-link tc-gen-add"
            onClick={() => openComposer(GENERAL)}
          >
            + General comment
          </button>
        )}
      </div>
    ) : null;
  /** The author's conversation icon: shows the section's comments in the margin. */
  const showMargin = (key: string) => {
    window.requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(`[data-tc-margin="${key}"]`);
      if (!el) return;
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      el.classList.remove('pn-flash-ref');
      void el.offsetWidth;
      el.classList.add('pn-flash-ref');
      window.setTimeout(() => el.classList.remove('pn-flash-ref'), 3000);
    });
  };
  /**
   * ehr#571 (reopen 10-23, PROGRESS-NOTE V141 V83): what a text box shows while the note is in
   * review. The supervisor edits their copy in place, with "What <author> will see" under it and
   * their comment in the margin; back with the author, the box is theirs, the changes show under
   * it and the margin holds a Keep / Revert card per edit and the supervisor's comments.
   */
  /**
   * ehr#741 (Chase 10-30, points 1 and 6; V141 comment card): a comment sent with the note, beside
   * its section: the green check resolves it, and either side replies under it while the note is
   * theirs. A comment from before threads were kept shows without them.
   */
  const threadCard = (t: ReviewThread, key: string, label: string) => {
    const kept = threadOf(t.id);
    return (
      <CommentCard
        key={t.id}
        who={userName(t.by)}
        photo={photoOf(t.by)}
        label={label}
        text={t.text ?? ''}
        quote={t.quote}
        when={t.at ? timeOf(t.at) : undefined}
        resolved={!!kept?.resolved}
        onResolve={cardsOpen && kept ? () => tick(t) : undefined}
        replies={repliesOf(kept)}
        onReply={cardsOpen && kept ? (x) => replyOn(t, x) : undefined}
        onShow={t.quote ? () => showWords(key, t.quote!, t.pos) : undefined}
      />
    );
  };
  /**
   * ehr#741 (Chase 10-30, point 8): back with the author, their own changes to a box since it came
   * back show under it, as the reviewer's do ("What <reviewer> will see").
   */
  const authorBack = status === 'changes' && isAuthor && !locked && returnedTexts !== null;
  const ownChanged = (key: string) => authorBack && (returnedTexts?.[key] ?? '') !== mineOf(key);
  const ownDiff = (key: string) =>
    ownChanged(key) ? (
      <ChangesBox
        before={returnedTexts?.[key] ?? ''}
        after={mineOf(key)}
        title={`What ${userName(lastChanges?.actorId ?? assignedCosignerId).split(' ')[0]} will see`}
        live
      />
    ) : null;
  /**
   * ehr#682 (V141 V92): Final text hides the changes under each box and the edit counts; on a
   * signed or co-signed note, Show changes shows what changed in review under each box.
   */
  const reviewBox = (key: string, label: string) => {
    const rv = reviewBoxOf(key, label);
    return rv && reviewView === 'final' ? { ...rv, below: null, edits: 0 } : rv;
  };
  const reviewBoxOf = (key: string, label: string) => {
    if (finalNote && reviewBase && !suggestMode) {
      const before = reviewBase[key] ?? '';
      const mine = mineOf(key);
      if (before === mine || reviewView === 'final') return null;
      return {
        value: mine,
        onEdit: null,
        edits: 0,
        marks: undefined,
        onSelectText: undefined,
        below: <ChangesBox before={before} after={mine} title="Changes in review" />,
        icon: null,
        margin: null,
      };
    }
    if (suggestMode) {
      const mine = mineOf(key);
      const value = reviewDraft.sections[key] ?? mine;
      const posted = reviewDraft.comments.filter((c) => c.section === key);
      // ehr#741: comments from earlier rounds, with the author's replies
      const earlier = commentThreadsFor(liveThreads, null, key);
      const commenting = compose?.section === key;
      const general = key === generalHost ? generalComments() : null;
      const edits = value === mine ? 0 : editHunks(mine, value).length;
      return {
        value,
        onEdit: (v: string) => setDraftSection(key, v),
        edits,
        // ehr#571 (reopen 10-24, point 4, V141 V92): select words to comment on them; the words
        // being commented on and those already commented on are highlighted in the box
        marks: anchorMarks(value, [
          ...(commenting && compose?.quote
            ? [{ quote: compose.quote, pos: compose.pos, cls: 'tc-mk-new' }]
            : []),
          ...posted.map((c) => ({ quote: c.quote, pos: c.pos, cls: 'tc-mk' })),
        ]),
        onSelectText: (sel: { quote: string; pos: number; x: number; y: number } | null) =>
          setSelPick(sel ? { section: key, ...sel } : null),
        below: (
          <ChangesBox
            before={mine}
            after={value}
            title={`What ${userName(authorId).split(' ')[0]} will see`}
            live
          />
        ),
        icon: (
          <CommentButton
            label={label}
            count={posted.length + earlier.filter((t) => !threadOf(t.id)?.resolved).length}
            on={commenting}
            onClick={() => toggleComment(key)}
          />
        ),
        margin:
          posted.length || commenting || general || earlier.length ? (
            <aside className="tc-margin" data-tc-margin={key} aria-label={`Comments on ${label}`}>
              {general}
              {/* ehr#741 (Chase 10-31, point 7): by the words they are on, not by time */}
              {orderByPlace([
                ...earlier.map((t) => ({
                  quote: t.quote,
                  pos: t.pos,
                  el: threadCard(t, key, label),
                })),
                ...posted.map((c) => ({ quote: c.quote, pos: c.pos, el: draftCard(c, label) })),
              ]).map((x) => x.el)}
              {composer(key, label)}
            </aside>
          ) : null,
      };
    }
    if (!tracked || !suggestions) {
      // ehr#741: a note sent back with no edits: the author's own changes and earlier comments
      const earlier = status === 'changes' ? commentThreadsFor(liveThreads, null, key) : [];
      if (!ownChanged(key) && !earlier.length) return null;
      return {
        value: mineOf(key),
        onEdit: null,
        edits: 0,
        marks: anchorMarks(
          mineOf(key),
          earlier.map((t) => ({ quote: t.quote, pos: t.pos, cls: 'tc-mk' })),
        ),
        onSelectText: undefined,
        below: ownDiff(key),
        icon: earlier.length ? (
          <CommentButton label={label} count={earlier.length} onClick={() => showMargin(key)} />
        ) : null,
        margin: earlier.length ? (
          <aside className="tc-margin" data-tc-margin={key} aria-label={`Review of ${label}`}>
            {orderByPlace(earlier).map((t) => threadCard(t, key, label))}
          </aside>
        ) : null,
      };
    }
    const sug = suggestions.sections[key as keyof NoteSuggestions['sections']];
    const mine = mineOf(key);
    const hunks = typeof sug === 'string' ? editHunks(mine, sug) : [];
    const notes = commentThreadsFor(liveThreads, suggestions, key);
    const canAct = isAuthor && !locked;
    return {
      value: mine,
      onEdit: null,
      edits: hunks.length,
      // ehr#571 (V141 V92): the words each comment is on, highlighted in the author's box
      marks: anchorMarks(
        mine,
        notes.map((c) => ({ quote: c.quote, pos: c.pos, cls: 'tc-mk' })),
      ),
      onSelectText: undefined,
      below:
        typeof sug === 'string' ? (
          <ChangesBox before={mine} after={sug} title={`Changes from ${supName}`} />
        ) : (
          ownDiff(key)
        ),
      icon: notes.length ? (
        <CommentButton label={label} count={notes.length} onClick={() => showMargin(key)} />
      ) : null,
      margin:
        hunks.length || notes.length ? (
          <aside className="tc-margin" data-tc-margin={key} aria-label={`Review of ${label}`}>
            {hunks.map((h) => (
              <EditCard
                key={`e${h.i}`}
                who={supName}
                photo={photoOf(suggestions.by)}
                label={label}
                hunk={h}
                {...(canAct
                  ? {
                      onKeep: () => reviewEdit(key, h.i, true),
                      onRevert: () => reviewEdit(key, h.i, false),
                    }
                  : {})}
              />
            ))}
            {orderByPlace(notes).map((t) => threadCard(t, key, label))}
          </aside>
        ) : null,
    };
  };

  /**
   * ehr#571 (reopen 10-27): what the reviewer changed outside the text boxes (only the parts that
   * differ from the note as it opened) and the field-change cards, for Co-sign and Request changes.
   */
  const reviewerFields = () => {
    if (!fbase || fcSide !== 's') return {};
    const now = buildPayload();
    const body = changedBodyParts(
      fbase.payload.body as Record<string, unknown>,
      now.body as Record<string, unknown>,
      REVIEW_FIELD_KEYS,
    );
    const dateMoved = now.date !== fbase.payload.date;
    const labelMoved = now.sessionLabel !== fbase.payload.sessionLabel;
    const fields =
      Object.keys(body).length || dateMoved || labelMoved
        ? {
            fields: {
              body,
              ...(dateMoved ? { date: now.date } : {}),
              ...(labelMoved ? { sessionLabel: now.sessionLabel } : {}),
            },
          }
        : {};
    const cardsMoved = JSON.stringify(fieldCards) !== JSON.stringify(fcLoaded);
    // ehr#741: the reviewer's replies and ticks on the cards
    const threadsMoved = JSON.stringify(liveThreads) !== JSON.stringify(storedThreads);
    return {
      ...fields,
      ...(cardsMoved ? { fieldChanges: fieldCards } : {}),
      ...(threadsMoved ? { threads: liveThreads } : {}),
    };
  };
  const doRequestChanges = () => {
    const reason = reviewReason;
    const threadsMoved = JSON.stringify(liveThreads) !== JSON.stringify(storedThreads);
    if (!noteId || (!reason && !draftCount && !fieldCount && !threadsMoved)) return;
    setBusy(true);
    setErr('');
    api
      .post(`/contacts/${contactId}/progress-notes/${noteId}/changes`, {
        ...(reason ? { reason } : {}),
        ...(draftCount
          ? { suggestions: { sections: draftSections, comments: draftComments } }
          : {}),
        ...reviewerFields(),
        ...(logNew.length ? { log: logNew } : {}), // ehr#682: the Comment history
      })
      .then(() => {
        tabLock.notifySaved(); // ehr#682: a read-only copy of the review reloads
        family?.notifySaved?.();
        // ehr#682 (V141 V83.4): on its own page the note stays open, sent back, with Take it back
        // in the footer for a few minutes; a family or group session page goes on as before
        if (family || group) onSaved();
        else {
          setReviewDraft({ sections: {}, comments: [] });
          reloadHere();
        }
      })
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.message : 'Could not send the note back.'),
      )
      .finally(() => setBusy(false));
  };
  const takeBackOn = !!takeBackUntil && status === 'changes' && !isAuthor && !viewOnly;
  // ehr#682 (V141 V92): Note settings' Supervisor review section, on a note that went to review
  const reviewHistoryOn =
    !!noteId &&
    (status === 'pending' ||
      status === 'changes' ||
      status === 'cosigned' ||
      reviewBase !== null ||
      logKept.length + logNew.length > 0 ||
      thread.some((e) => e.action === 'submit'));
  /** ehr#682: the note read again from the API, here (nothing of the old copy kept). */
  const reloadHere = () => {
    baseline.current = null;
    leaveBase.current = null;
    setReloadTick((t) => t + 1);
  };
  /**
   * ehr#682 (V141 V83.4): Take it back. The note comes back into review with the reviewer's work
   * as their draft (the draft restore says so); the API refuses once the author has started on it
   * or the few minutes are up, and the button goes.
   */
  const doTakeBack = () => {
    if (!noteId) return;
    setBusy(true);
    setErr('');
    api
      .post(`/contacts/${contactId}/progress-notes/${noteId}/take-back`, {})
      .then(() => {
        tabLock.notifySaved();
        tookBack.current = true;
        setTakeBackUntil(null);
        reloadHere();
      })
      .catch((e: unknown) => {
        setTakeBackUntil(null);
        setErr(e instanceof ApiError ? e.message : 'Could not take the note back.');
      })
      .finally(() => setBusy(false));
  };

  // ── ehr#741 (Chase 10-30, point 4): the reviewer's work is saved as they go ─────────────────
  // Their copy of the boxes, comments, field changes, replies and ticks are kept with the note
  // (PUT .../review-draft) a moment after each change, and with Save Draft, so closing the tab or
  // reloading keeps them. Request changes or Co-sign clears the draft (server side).
  const hadDraft = useRef(false);
  const draftBase =
    suggestMode && loaded && draftRestored && noteId
      ? draftToSave({
          sections: draftSections,
          comments: reviewDraft.comments,
          fields: (reviewerFields() as { fields?: Record<string, unknown> }).fields,
          threads: liveThreads,
          storedThreads,
        })
      : null;
  // ehr#682 (V141 V86): the reviewer's Comment history entries ride with their draft
  const draftNow = draftBase && {
    empty: draftBase.empty && !logNew.length,
    draft: { ...draftBase.draft, ...(logNew.length ? { log: logNew } : {}) },
  };
  const draftKey = draftNow ? JSON.stringify(draftNow.draft) : null;
  const putReviewDraft = (draft: NonNullable<typeof draftNow>['draft']) =>
    api.put(`/contacts/${contactId}/progress-notes/${noteId}/review-draft`, draft);
  // a saved draft comes back once the note has opened for its reviewer (after the field baseline,
  // so the restored field changes show as their cards)
  useEffect(() => {
    if (draftRestored || !loaded || !suggestMode) return;
    if (!fieldBase.current || fieldBase.current.side !== 's') return;
    const d = draftToRestore.current;
    draftToRestore.current = null;
    if (d) {
      hadDraft.current = true;
      setReviewDraft({ sections: d.sections ?? {}, comments: d.comments ?? [] });
      if (d.threads) setLiveThreads(readReviewThreads(d.threads));
      if (d.log) setLogKept((k) => [...k, ...readReviewLog(d.log)]);
      const given = (d.fields?.body ?? {}) as Record<string, unknown>;
      if (Object.keys(given).length || d.fields?.date) {
        const now = buildPayload().body as Record<string, unknown>;
        const parts: Record<string, unknown> = { ...given };
        if (given.extras && typeof given.extras === 'object')
          parts.extras = { ...((now.extras ?? {}) as object), ...(given.extras as object) };
        loadReviewParts(parts, d.fields?.date ? String(d.fields.date).slice(0, 10) : undefined);
      }
      setNotice(
        tookBack.current
          ? 'Took it back. The note is with you again.'
          : `Your review from ${timeOf(d.at)} is back. It saves as you go until you co-sign or request changes.`,
      );
      tookBack.current = false;
    }
    setDraftRestored(true);
  });
  useEffect(() => {
    if (!draftNow || !draftKey || draftKey === draftSavedKey.current) return;
    // nothing of theirs yet, and nothing saved before: nothing to save
    if (draftNow.empty && !hadDraft.current) {
      draftSavedKey.current = draftKey;
      return;
    }
    const draft = draftNow.draft;
    const t = window.setTimeout(() => {
      putReviewDraft(draft)
        .then(() => {
          draftSavedKey.current = draftKey;
          hadDraft.current = true;
        })
        .catch(() => undefined);
    }, 1200);
    return () => window.clearTimeout(t);
  }, [draftKey]);
  // leaving the page (tab closed, reload): what is waiting goes now
  const flushDraft = useRef<() => void>(() => undefined);
  flushDraft.current = () => {
    if (!draftNow || !draftKey || draftKey === draftSavedKey.current) return;
    if (draftNow.empty && !hadDraft.current) return;
    draftSavedKey.current = draftKey;
    void putReviewDraft(draftNow.draft).catch(() => undefined);
  };
  useEffect(() => {
    const go = () => flushDraft.current();
    window.addEventListener('pagehide', go);
    return () => {
      window.removeEventListener('pagehide', go);
      go();
    };
  }, []);
  /** ehr#741 (point 4): Save Draft beside Co-sign & Finalize, then back to the list, as the author's. */
  const saveReviewDraftNow = () => {
    if (!draftNow) return;
    const key = draftKey;
    setReviewSaving(true);
    setDraftNote(null);
    setErr('');
    atLeast(
      putReviewDraft(draftNow.draft).then(() => {
        draftSavedKey.current = key;
        hadDraft.current = true;
      }),
      SAVE_MIN_MS,
    )
      .then(() => {
        if (onDraftSaved) onDraftSaved();
        else setDraftNote(okNotice(NOTE_DRAFT_SAVED, { at: 'draft' }));
        onSaved();
      })
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.message : 'Could not save your review draft.'),
      )
      .finally(() => setReviewSaving(false));
  };

  const doReply = () => {
    const message = replyText.trim();
    if (!noteId || !message) return;
    setBusy(true);
    setErr('');
    api
      .post<ClinicalReviewEventView[]>(`/contacts/${contactId}/progress-notes/${noteId}/reply`, {
        message,
      })
      .then((t) => {
        setThread(t);
        setReplyText('');
        setReplyOpen(false);
      })
      .catch((e: unknown) => setErr(e instanceof ApiError ? e.message : 'Could not send reply.'))
      .finally(() => setBusy(false));
  };

  // Footer primary action follows the note's state + the viewer's role.
  let primaryLabel = 'Sign Off Note';
  let primaryBadge = false; // A-3: red "1" on Resubmit — one open reviewer note
  let primaryDisabled = false;
  let primaryFn: () => void = () => doSubmit();
  if (status === 'cosigned') {
    primaryLabel = 'Co-signed';
    primaryDisabled = true;
    primaryFn = () => undefined;
  } else if (status === 'signed') {
    primaryLabel = 'Signed';
    primaryDisabled = true;
    primaryFn = () => undefined;
  } else if (status === 'pending') {
    if (canReview) {
      primaryLabel = 'Co-sign & Finalize';
      primaryFn = doCosign;
      primaryDisabled = cosignLeft.length > 0; // ehr#663
    } else {
      primaryLabel = 'Submitted for Review';
      primaryDisabled = true;
      primaryFn = () => undefined;
    }
  } else if (isAuthor) {
    // calendar#46 (A-3): a returned note resubmits as the next round.
    // ehr#571 (reopen): with Supervision on, the button says where the note goes.
    primaryLabel =
      (group || family) && !(status === 'changes' && supervisionOn)
        ? // ehr#571 round 2 (V22 / V11 footer): "Sign Marcus" ("Submit Marcus" with Supervision)
          `${supervision?.required ? 'Submit' : 'Sign'} ${contactName.split(' ')[0] ?? contactName}`
        : authorSignLabel({
            status,
            supervisionRequired: supervision ? toSupervisor : null, // ehr#731
            round: reviewRound,
          });
    primaryBadge = status === 'changes';
    primaryFn = () => doSubmit();
  } else {
    // calendar#46 (A-15): a reviewer opening someone else's unsubmitted draft.
    if (canReview) primaryLabel = 'Waiting on Clinician Signature';
    primaryDisabled = true;
    primaryFn = () => undefined;
  }

  // ehr#571 (reopen, item 1): unsaved changes (never saved, or edited since Save Draft) ask before
  // leaving: Sign Off Note (Submit to Supervisor) / Save Draft / Don't save / Go back. Untouched
  // notes and locked or read-only ones leave at once. Closing or reloading the tab gets the
  // browser's own prompt, only while something is unsaved.
  const markTouched = () => {
    if (leaveBase.current === null && loaded) leaveBase.current = JSON.stringify(buildPayload());
  };
  const unsaved = () =>
    isUnsaved({
      base: leaveBase.current,
      now: JSON.stringify(buildPayload()),
      locked,
      viewOnly,
    });
  const requestLeave = (go: () => void, saved: () => void = go) => {
    if (unsaved()) setLeaveAsk({ go, saved });
    else go();
  };
  const unsavedRef = useRef(unsaved);
  unsavedRef.current = unsaved;
  // ehr#662: the parent asks the same question before it opens another note over this one
  const leaveRef = useRef(requestLeave);
  leaveRef.current = requestLeave;
  useEffect(() => {
    bindLeave?.((go) => leaveRef.current(go));
    return () => bindLeave?.(null);
  }, []);
  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (!unsavedRef.current()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, []);
  // ehr#689 F4 (V15 "warns once on an open comment"): the reviewer's comments on this note not sent
  // yet, for the family page's Co-sign all ready notes (it warns once before co-signing over them)
  const draftCommentCount = suggestMode ? reviewDraft.comments.length + typing.length : 0;
  const reviewDraftRef = useRef(family?.onReviewDraft);
  reviewDraftRef.current = family?.onReviewDraft;
  useEffect(() => {
    reviewDraftRef.current?.(draftCommentCount);
  }, [draftCommentCount]);
  useEffect(() => () => reviewDraftRef.current?.(0), []);
  const canSignHere =
    isAuthor &&
    !primaryDisabled &&
    (status === 'draft' || status === 'changes') &&
    !suggestMode &&
    !(tracked && editCount > 0 && keepGate);

  const reviewerOptions = users
    .filter((u) => REVIEW_ROLES.has(u.role ?? '') && u.id !== (authorId ?? currentUserId))
    .map((u) => ({
      value: u.id,
      label: [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email,
    }));

  const pill = STATUS_LABEL[status] ?? 'Draft';
  const reviewerName = userName(assignedCosignerId);
  const lastChanges = [...thread].reverse().find((e) => e.action === 'changes');
  const replies = lastChanges
    ? thread.filter(
        (e) =>
          e.action === 'reply' &&
          new Date(e.createdAt).getTime() >= new Date(lastChanges.createdAt).getTime(),
      )
    : [];
  const timeOf = (iso: string | Date) => new Date(iso).toLocaleTimeString('en-US', timeOpts());
  const defaultReviewerId =
    users.find((u) => u.id === (authorId ?? currentUserId))?.supervisorId ?? null;

  // ehr#278: the signers' saved signatures (their profiles, About > Signature), shown once they signed.
  const clinicianSigId = clinicianSigned ? authorId : null;
  const supervisorSigId = supervisorSigned ? assignedCosignerId : null;
  const [sigImgs, setSigImgs] = useState<Record<string, string | null>>({});
  useEffect(() => {
    let live = true;
    for (const id of [clinicianSigId, supervisorSigId])
      if (id)
        void signatureImg(id).then((img) => {
          if (live) setSigImgs((m) => (m[id] === img ? m : { ...m, [id]: img }));
        });
    return () => {
      live = false;
    };
  }, [clinicianSigId, supervisorSigId]);
  const clinicianImg = clinicianSigId ? (sigImgs[clinicianSigId] ?? null) : null;
  const supervisorImg = supervisorSigId ? (sigImgs[supervisorSigId] ?? null) : null;

  const cosignerName = assignedCosignerId
    ? userName(assignedCosignerId)
    : supervision?.supervisorId && isAuthor
      ? supervision.supervisorName || userName(supervision.supervisorId)
      : userName(reviewerId || defaultReviewerId);
  // ehr#270: Download PDF / print, laid out like this editor, from what it shows.
  const printNote = () => {
    const p = buildPayload();
    const html = progressPrintHtml({
      contactName,
      status,
      format: p.format,
      date: p.date,
      time: storedTime(sessionTime), // ehr#407: the print follows the person's 12/24-hour setting
      sessionType,
      durationMin: sessionDuration,
      startOnly: layoutKind === NO_SHOW_KIND, // ehr#713
      planLabel: linkedPlan?.label ?? '',
      body: p.body as Record<string, unknown>,
      clinician: {
        name: userName(clinicianSigned ? authorId : currentUserId),
        signed: clinicianSigned,
        signedAt: submittedAt,
        img: clinicianImg,
      },
      supervisor: showCosign
        ? { name: cosignerName, signed: supervisorSigned, signedAt: cosignedAt, img: supervisorImg }
        : null,
      addenda, // ehr#544
    });
    const w = window.open('', '_blank', 'width=900,height=1100');
    if (!w) return;
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 300);
  };
  // ── ehr#437 (PROGRESS_NOTE_10-14-V81): the note as a full page ─────────────────────────────
  // Section cards (title, Required, fold) in a note column, NotePilot in the rail, and a sign-off
  // bar listing what is still missing. The review views (ehr#277) and signatures are unchanged.
  const therapy = (THERAPY_KINDS as readonly string[]).includes(kind);
  const v81 = format !== 'CREDIFY' || otherKind;
  // ehr#437 Payor rules (V81 billingLine / billingChecks): telehealth and phone carry the payor's
  // modifier (95 / 93 when it has none set); the checks follow "Billing checks on the note".
  // ehr#478: with the code's own checks (kind, length, +90785, units), each with its fix.
  // ehr#543: the modifier is worked out with the code (above), so the note saves it too.
  // A signed note shows the modifier it was signed with (the payor's rules may change later).
  const shownModifier = locked && savedModifier ? savedModifier : modifier;
  // ehr#724 (V7 V3): no billing checks on an Admin / phone note ("doesn't pay phone sessions")
  const billChecks =
    !locked && !adminOnly && ns.mode.inlineWarn !== false
      ? [
          ...codeChecks({ kind, base, autoBase, minutes: sessionDuration, complexity }),
          ...payorChecks(code, place, payorRules).map((text) => ({ text, fix: '' })),
        ]
      : [];
  const fixCode = (fix: string) =>
    setCodePick(fix === 'none' ? '' : fix === autoBase ? undefined : fix);
  const foldBtn = (id: string, title: string) => (
    <button
      type="button"
      className="pn-fold"
      aria-expanded={!folded.has(id)}
      aria-label={`${folded.has(id) ? 'Open' : 'Fold'} ${title}`}
      onClick={() => toggleFold(id)}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>
  );
  const reqTag = <span className="pn-tag">Required</span>;
  const secCard = (
    id: string,
    title: string,
    opts: { summary?: string; required?: boolean; target?: string; action?: ReactNode },
    body: ReactNode,
  ) => (
    <section
      key={id}
      className={`pn-sec${folded.has(id) ? ' coll' : ''}`}
      data-pn-sec={id}
      data-pn-target={opts.target ?? id}
    >
      <div className="pn-sec-h">
        <h3 onClick={() => toggleFold(id)}>{title}</h3>
        {opts.summary && <span className="pn-sec-sum">{opts.summary}</span>}
        <span className="pn-sp" />
        {opts.action}
        {opts.required && reqTag}
        {foldBtn(id, title)}
      </div>
      <div className="pn-sec-b">{body}</div>
    </section>
  );
  // ── ehr#437 part 6 (V81 Last session, History, copy forward) ─────────────────────────────
  const pastSigned = signedNotes(history as unknown as PastNote[], noteId);
  // The last signed note: of the same kind; for a therapy note with none, the last therapy note
  // that isn't a group note (a group note is written for the whole group).
  const lastNote =
    pastSigned.find((n) => pastKind(n) === kind) ??
    (otherKind
      ? null
      : (pastSigned.find((n) => !isOtherKind(pastKind(n)) && pastKind(n) !== 'Group') ?? null));
  // ehr#727 (PEER_SUPPORT_NOTE_10-30-V8, 12a): a new Peer support note starts with the last signed
  // Peer support note's Recovery goal, tagged "From MM/DD/YYYY" until it is edited
  const lastRecovery =
    kind === PEER_SUPPORT_KIND && lastNote
      ? { text: recoveryGoalOf(lastNote.body), date: String(lastNote.date) }
      : null;
  const recoveryCarried = useRef(false);
  useEffect(() => {
    if (recoveryCarried.current || !loaded || editId !== null || copyFrom || !lastRecovery?.text)
      return;
    recoveryCarried.current = true;
    if (extras[RECOVERY_GOAL_ID] === undefined) setExtra(RECOVERY_GOAL_ID, lastRecovery.text);
  }, [loaded, lastRecovery?.text]);
  // ehr#437 part 4 (V81 Quick add): what each box offers. Your own signed notes for this client
  // give "From your notes"; a box a layout renamed has no stock phrases. ehr#571 (case management
  // round, V20 V57): only notes of this kind on an Other note, only therapy notes on a therapy note.
  const mySigned = history
    .filter(
      (n) =>
        n.id !== noteId &&
        n.authorId === currentUserId &&
        (n.status === 'signed' || n.status === 'cosigned') &&
        phraseKindOk(pastKind(n as unknown as PastNote), kind),
    )
    .sort((a, b) => String(b.date).localeCompare(String(a.date)));
  // ehr#571 (V20 lastPlanText): {last plan} and "+ Last session's plan" read the note Last session
  // shows (the last signed note of this kind)
  const lastPlanFull = lastNote ? sectionText(lastNote.body, 'plan').trim() : '';
  const lastPlan = lastPlanFull.replace(/\.\s*$/, '');
  const bringBox = bringBoxOf(blocks);
  const dots = phrases ? dotListOf(phrases.practice, phrases.mine) : [];
  const fill = (t: string) =>
    dotFill(t, {
      client: contactName.split(' ')[0],
      date: date ? fmtMDY(`${date}T12:00:00`) : '',
      length: sessionDuration > 0 ? `${sessionDuration} min` : '',
      lastPlan,
    });
  const quickFor = (key: string, label: string): QuickAdd | undefined => {
    if (locked) return undefined;
    const stock = (FORMAT_SECTIONS_ANY[otherKind ? 'SOAP' : format] ?? []).find(
      ([, k]) => k === key,
    );
    return {
      // ehr#713: the no-show note's Contact note gets the V3 no-show pills
      phrases: pillsOn
        ? stockPillsFor({ key, label, stockLabel: stock?.[0], kind: layoutKind })
        : [],
      mine: pillsOn
        ? myPhrasesFrom(
            mySigned.map((n) => sectionText(n.body, key)).filter(Boolean),
            contactName.split(/\s+/),
          )
        : [],
      dots,
      fill,
      // ehr#571 (V141 + Add phrase): your own pills for this box, this client's first
      added: pillsOn ? boxPhrasesFor(phrases?.boxPhrases, contactId, key) : [],
      onAddPhrase: () => setPhraseAsk({ key, label }),
      // ehr#724 (V7): no therapy phrase packs (CBT, DBT, ...) on an Admin / phone note
      ...(adminOnly ? { noPacks: true } : {}),
      // ehr#571 (V20): "+ Last session's plan" under the first box
      ...(pillsOn && lastPlanFull && key === bringBox
        ? { lastPlan: lastPlanLine(lastPlanFull), boxLabel: label }
        : {}),
    };
  };
  const lastTicks =
    lastNote && Array.isArray(lastNote.body.interventionsUsed)
      ? (lastNote.body.interventionsUsed as unknown[]).filter(
          (x): x is string => typeof x === 'string',
        )
      : [];
  const targets = blocks
    .filter((b) => b.kind === 'text')
    .map((b) => ({ key: b.id, label: b.label }));
  const unfold = (id: string) =>
    setFolded((f) => {
      if (!f.has(id)) return f;
      const n = new Set(f);
      n.delete(id);
      return n;
    });
  const copyText = (text: string, key: string, from: string): string => {
    const t = targets.find((x) => x.key === key);
    if (!t) return '';
    const cur = pbody[key] ?? '';
    if (cur.includes(text)) return `Already in ${t.label}.`;
    editSection(key, appendText(cur, text));
    noteCopied(key, from, text);
    unfold(key);
    return `Copied into ${t.label}.`;
  };
  const copyTicks = (items: string[]): string => {
    const add = items.filter((x) => !interventionsUsed.includes(x));
    if (add.length) setInterventionsUsed((l) => [...l, ...add.filter((x) => !l.includes(x))]);
    unfold('interventions');
    return add.length
      ? `Ticked ${add.length} in Interventions used.`
      : 'Interventions used already has those ticks.';
  };
  const interventionsOn = blocks.some((b) => b.kind === 'interventions');
  const copyAll = (n: PastNote): string => {
    let done = 0;
    let dup = 0;
    let skipped = 0;
    const next = { ...pbody };
    for (const s of pastSections(n)) {
      if (!targets.some((t) => t.key === s.key)) {
        skipped++;
        continue;
      }
      if ((next[s.key] ?? '').includes(s.text)) dup++;
      else {
        next[s.key] = appendText(next[s.key] ?? '', s.text);
        noteCopied(s.key, n.date, s.text);
        done++;
      }
    }
    setPbody(next);
    setNpDrafted((d) => d.filter((k) => next[k] === pbody[k]));
    const ticks = Array.isArray(n.body.interventionsUsed)
      ? (n.body.interventionsUsed as unknown[]).filter((x): x is string => typeof x === 'string')
      : [];
    if (interventionsOn && ticks.length) copyTicks(ticks);
    setFolded(new Set());
    return [
      `Copied ${done} box${done === 1 ? '' : 'es'}.`,
      dup ? `${dup} already had that text.` : '',
      skipped
        ? `${skipped} had no matching box: copy ${skipped === 1 ? 'it' : 'them'} one at a time.`
        : '',
    ]
      .filter(Boolean)
      .join(' ');
  };
  const copyApi: CopyApi = {
    targets,
    interventionsShown: interventionsOn,
    locked,
    copyText,
    copyTicks,
    copyAll,
  };
  const fmtDay = (iso: string) => fmtMDY(iso);
  // V81 measureBoxId: Objective (SOAP), Data (DAP), Behavior (BIRP) when the note shows it, else
  // its first box.
  const measureBox =
    targets.find(
      (t) =>
        t.key ===
        ({ SOAP: 'objective', DAP: 'data', BIRP: 'behavior' } as Record<string, string>)[
          otherKind ? 'SOAP' : format
        ],
    ) ??
    targets[0] ??
    null;
  const addLine = (line: string): string => {
    if (!measureBox || !line) return '';
    const cur = pbody[measureBox.key] ?? '';
    if (cur.includes(line)) return `Already in ${measureBox.label}.`;
    editSection(measureBox.key, withPhrase(cur, line));
    unfold(measureBox.key);
    return `Added to ${measureBox.label}.`;
  };
  // V81 leftovers: the last plan's "Next session: …" lines, one question each above Plan.
  const followItems = lastNote ? planFollowUps(sectionText(lastNote.body, 'plan')) : [];
  const answerFollowUp = (i: number, ans: 'yes' | 'no', why = '') => {
    const item = followItems[i]!;
    const prev = followUps.find((f) => f.item === item);
    const line = followUpLine(item, ans, why);
    editSection('plan', withFollowUp(pbody.plan ?? '', prev?.text ?? '', line));
    setFollowUps((l) => [
      ...l.filter((f) => f.item !== item),
      { item, ans, ...(why ? { why } : {}), text: line },
    ]);
  };
  const followUpBox =
    !locked && followItems.length > 0 ? (
      <div className="pn-follow" data-pn-target="followups">
        <div className="pn-follow-h">From last session&rsquo;s plan</div>
        {followItems.map((item, i) => {
          const a = followUps.find((f) => f.item === item);
          return (
            <div key={item} className="pn-follow-row">
              <span className="pn-follow-q">
                Did you: {item.charAt(0).toLowerCase() + item.slice(1)}?
              </span>
              <span className="pn-seg" role="group" aria-label={`Did you: ${item}`}>
                {(['yes', 'no'] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    aria-pressed={a?.ans === v}
                    onClick={() => answerFollowUp(i, v, v === 'no' ? (a?.why ?? '') : '')}
                  >
                    {v === 'yes' ? 'Yes' : 'No'}
                  </button>
                ))}
              </span>
              {a?.ans === 'no' && (
                <input
                  className="pn-follow-why"
                  value={a.why ?? ''}
                  placeholder="Why not? (optional)"
                  aria-label="Why not"
                  onChange={(e) => answerFollowUp(i, 'no', e.target.value)}
                />
              )}
            </div>
          );
        })}
      </div>
    ) : null;
  // ehr#437 part 7: a sentence copied forward and still word for word, with questions that select
  // it in the box so it can be made about today.
  const copyMarks = (key: string) => (locked ? [] : unchangedCopied(copied[key], pbody[key] ?? ''));
  const selectSentence = (key: string, t: string) => {
    const el = document.querySelector<HTMLTextAreaElement>(`[data-pn-sec="${key}"] textarea`);
    if (!el) return;
    const i = el.value.indexOf(t);
    el.focus();
    if (i >= 0) el.setSelectionRange(i, i + t.length);
  };
  const copyStrip = (key: string) => {
    const marks = copyMarks(key);
    if (!marks.length || !ns.mode.copyQs) return null;
    const from = marks.map((m) => m.from).sort()[0]!;
    return (
      <div className="pn-cp" role="note">
        <b>Copied from {fmtMDY(`${from}T12:00:00`)}</b>
        <span>
          {marks.length} sentence{marks.length === 1 ? ' is' : 's are'} still word for word. Make
          {marks.length === 1 ? ' it' : ' them'} about today:
        </span>
        <ul>
          {copyQuestions(key, marks).map((q) => (
            <li key={q.q}>
              <button type="button" className="pn-link" onClick={() => selectSentence(key, q.t)}>
                {q.q}
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  };
  /** V81 signoffWarnings: never blocking; shown before signing, "Sign anyway" goes on. */
  const signWarnings = (): string[] => {
    const w: string[] = [];
    if (therapy && !linkedPlanId) w.push('No signed treatment plan is linked to this note.');
    for (const b of blocks) {
      if (b.kind !== 'text' || !ns.mode.copyQs) continue;
      const m = copyMarks(b.id);
      if (m.length)
        w.push(
          `${b.label} still has copied text from ${fmtMDY(`${m.map((x) => x.from).sort()[0]}T12:00:00`)} that wasn't changed.`,
        );
    }
    return w;
  };
  // ehr#571 (reopen 10-23): while the supervisor edits in place, everything in a box's card but
  // the box itself (Plan's questions, the copied-text check, Why treatment continues) stays locked.
  const lockedPart = (node: ReactNode) =>
    suggestMode && node ? (
      <fieldset className="pn-lock pn-lock-contents" disabled>
        {node}
      </fieldset>
    ) : (
      node
    );
  const textSec = (
    [label, key, hint]: [string, string, string],
    required = true,
    /** ehr#571 (reopen 10-27): a layout box's own text (body.extras), when it isn't in pbody. */
    src?: { value: string; set: (v: string) => void },
  ) => {
    const rv = reviewBox(key, label);
    // ehr#663 (point 3): Why treatment continues, under Assessment, is reviewed like a box
    const nec = key === 'assessment' && therapy;
    const nrv = nec ? reviewBox(NEC, NEC_LABEL) : null;
    const necValue = nrv ? nrv.value : necessity;
    const setNec = nrv?.onEdit ?? setNecessity;
    return (
      <Fragment key={key}>
        <section
          className={`pn-sec pn-sec-text${folded.has(key) ? ' coll' : ''}${rv?.onEdit ? ' tc-editing' : ''}`}
          data-pn-sec={key}
          data-pn-target={`sec-${key}`}
        >
          <NoteField
            label={label}
            field={key}
            hint={promptFor(key, hint)}
            value={rv ? rv.value : src ? src.value : (pbody[key] ?? '')}
            drafted={!rv?.onEdit && npDrafted.includes(key) && Boolean((pbody[key] ?? '').trim())}
            onAccept={() => setNpDrafted((d) => d.filter((k) => k !== key))}
            className="pn-field"
            context={npContext}
            onEdit={rv?.onEdit ?? src?.set ?? ((v) => editSection(key, v))}
            onReplace={rv?.onEdit ?? src?.set ?? ((v) => replaceSection(key, v))}
            dictate={dictate}
            marks={rv?.marks}
            onSelectText={rv?.onSelectText}
            quick={rv?.onEdit || src ? undefined : quickFor(key, label)}
            showAdjust={!rv?.onEdit}
            boxLabel={rv?.edits ? <EditLabel /> : undefined}
            missing={!rv && missRed.has(key) && !src?.value.trim()}
            beforeBox={key === 'plan' ? lockedPart(followUpBox) : undefined}
            afterBox={
              <>
                {rv?.below}
                {lockedPart(copyStrip(key))}
              </>
            }
            headExtra={
              <>
                <span className="pn-sp" />
                {/* ehr#571 (reopen 10-24, V141): "1 edit", Required, the conversation icon */}
                {rv && <EditChip text={editChip(rv.edits)} />}
                {required && reqTag}
                {rv?.icon}
                {foldBtn(key, label)}
              </>
            }
          />
          {nec && (
            <div className={`pn-nec${nrv?.onEdit ? ' tc-editing' : ''}`} data-pn-target="necessity">
              <div className="pn-nec-h">
                <span>{NEC_LABEL}</span>
                {nrv && <EditChip text={editChip(nrv.edits)} />}
                {reqTag}
                {nrv?.icon}
              </div>
              {nrv?.edits ? <EditLabel /> : null}
              <div className="pn-ta-wrap">
                <textarea
                  value={necValue}
                  placeholder={promptFor('necessity', 'Why this level of care is still needed.')}
                  aria-label={NEC_LABEL}
                  onChange={(e) => setNec(e.target.value)}
                />
                <DictateMic dictate={dictate} label={NEC_LABEL} value={necValue} onText={setNec} />
              </div>
              {nrv?.below}
              <div className="pn-pills" hidden={!pillsOn}>
                {NECESSITY_PHRASES.map((ph) => (
                  <button
                    key={ph}
                    type="button"
                    className="pn-pill"
                    disabled={necValue.includes(ph)}
                    onClick={() => setNec(necValue.trim() ? `${necValue.trim()} ${ph}` : ph)}
                  >
                    {ph}
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>
        {rv?.margin}
        {nrv?.margin}
      </Fragment>
    );
  };
  // The practice's list (V81 INTERVENTION_ITEMS until an admin changes it), plus any saved pick the
  // list no longer has.
  const interventionItems = [
    ...practiceInterventions,
    ...interventionsUsed.filter((x) => !practiceInterventions.includes(x)),
  ];
  // ehr#571 (V141 V127): a new intervention is ticked, and goes on the practice's list when the
  // person may change it (admins and managers); for anyone else it is on this note only.
  const addIntervention = (text: string) => {
    setInterventionsUsed((l) => (l.includes(text) ? l : [...l, text]));
    if (!editsPracticeLists) {
      setNotice(
        `Added "${text}" to this note. An admin or manager can put it on the practice list in Note settings > Interventions.`,
      );
      return;
    }
    if (practiceInterventions.length >= INTERVENTION_LIST_MAX) {
      setNotice(`Added "${text}" to this note. The practice list is full (60).`);
      return;
    }
    const next = [...practiceInterventions, text];
    api
      .put('/practice/clinical', { interventions: next })
      .then(() => {
        setPracticeInterventions(next);
        setNotice(`Added "${text}" to the list and ticked it.`);
      })
      .catch((e: unknown) =>
        setErr(
          e instanceof ApiError
            ? e.message
            : `Ticked "${text}", but it couldn't be added to the practice list.`,
        ),
      );
  };
  // ehr#682 (V141 V127): hidden ones stay off the checklist unless ticked. Ticking one from search
  // brings it back for this client when the person may change the practice's settings.
  const hiddenNow = hiddenFor(interHide, contactId);
  const tickFromSearch = (it: string) => {
    setInterventionsUsed((l) => (l.includes(it) ? l : [...l, it]));
    if (!editsPracticeLists || !hiddenNow.includes(it)) return;
    const next = unhideFor(interHide, contactId, it);
    api
      .put('/practice/clinical', { interventionsHidden: next })
      .then(() => {
        setInterHide(next);
        setNotice(
          `Ticked "${it}". It shows for ${contactName.split(' ')[0] || 'this client'} again.`,
        );
      })
      .catch(() => undefined);
  };
  const interventionsSec = secCard(
    'interventions',
    'Interventions used',
    {
      required: true,
      summary: interventionsUsed.length ? `${interventionsUsed.length} picked` : undefined,
      // V81 (6 A): one click ticks last session's interventions.
      action:
        !fieldsLocked && lastTicks.length ? (
          <button type="button" className="pn-link pn-sec-act" onClick={() => copyTicks(lastTicks)}>
            Same as last session
          </button>
        ) : undefined,
    },
    <div className="pn-checks">
      {visibleInterventions(interventionItems, hiddenNow, interventionsUsed).map((it) => (
        <label key={it} className="pn-check">
          <input
            type="checkbox"
            checked={interventionsUsed.includes(it)}
            onChange={(e) =>
              setInterventionsUsed((l) =>
                e.target.checked ? [...l, it] : l.filter((x) => x !== it),
              )
            }
          />
          {it}
        </label>
      ))}
      {/* ehr#571 (V141 V127): search the practice's list, or add one that isn't on it */}
      {!fieldsLocked && (
        <InterventionSearchBox
          list={interventionItems}
          ticked={interventionsUsed}
          hidden={hiddenNow}
          onTick={tickFromSearch}
          onAdd={addIntervention}
        />
      )}
    </div>,
  );
  // ── FAMILY_COUPLES_NOTE 60 A / 71 A: Safety between partners, on this partner's note only ──
  const first = contactName.split(' ')[0] ?? contactName;
  const meName = (() => {
    const u = users.find((x) => x.id === currentUserId);
    return u ? [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email : '';
  })();
  const setPvs = (i: number, v: 'Yes' | 'No') => {
    const a = [...(partnerSafety?.a ?? [null, null, null])];
    while (a.length < 3) a.push(null);
    a[i] = v;
    // a changed answer is reviewed again
    setPartnerSafety({ a, at: new Date().toISOString(), by: meName });
  };
  const pvsSec =
    family && partnerSafetyShown(family.type, attendance !== 'Not present', partnerSafety)
      ? secCard(
          'pvs',
          'Safety between partners',
          {
            required: family.type === 'Couples' && !partnerSafetyDone(partnerSafety),
            action: partnerSafety?.notPartner ? (
              <span className="pn-tag">Not a partner</span>
            ) : (
              <>
                <span className="pn-tag">Asked alone</span>
                {pvsYesOpen && <span className="pn-tag pn-tag-bad">Needs review</span>}
              </>
            ),
          },
          partnerSafety?.notPartner ? (
            <p className="pn-pvs-note">
              {first} isn&rsquo;t a partner, so wasn&rsquo;t asked.{' '}
              {!fieldsLocked && (
                <button
                  type="button"
                  className="pn-link"
                  onClick={() => setPartnerSafety({ a: [null, null, null] })}
                >
                  Ask now
                </button>
              )}
            </p>
          ) : (
            <>
              {PARTNER_SAFETY_QUESTIONS.map((q, i) => (
                <div key={q} className="pn-pvs-row">
                  <span>{q}</span>
                  <div className="pn-seg" role="group" aria-label={q}>
                    {(['Yes', 'No'] as const).map((v) => (
                      <button
                        key={v}
                        type="button"
                        aria-pressed={partnerSafety?.a?.[i] === v}
                        disabled={fieldsLocked}
                        onClick={() => setPvs(i, v)}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {partnerSafety?.review && (
                <p className="pn-pvs-note">
                  <b>Reviewed</b> by {partnerSafety.review.by}, {fmtMDY(partnerSafety.review.at)}:{' '}
                  {partnerSafety.review.text}
                </p>
              )}
              {pvsYesOpen && !fieldsLocked && pvsReview === null && (
                <button type="button" className="btn" onClick={() => setPvsReview('')}>
                  Mark reviewed
                </button>
              )}
              {pvsYesOpen && pvsReview !== null && (
                <div className="pn-pvs-review">
                  <label>
                    <span>What was done</span>
                    <textarea
                      autoFocus
                      value={pvsReview}
                      placeholder="Seen alone, safety plan made"
                      onChange={(e) => {
                        setPvsReview(e.target.value);
                        setPvsErr('');
                      }}
                    />
                  </label>
                  {pvsErr && <em className="pn-inline-err">{pvsErr}</em>}
                  <div className="pn-pvs-review-act">
                    <button type="button" className="btn" onClick={() => setPvsReview(null)}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn primary"
                      onClick={() => {
                        const t = pvsReview.replace(/\s+/g, ' ').trim();
                        if (!t) return setPvsErr('Write what was done.');
                        setPartnerSafety((ps) => ({
                          ...(ps ?? {}),
                          review: {
                            by: meName,
                            at: new Date().toISOString(),
                            text: t.slice(0, 300),
                          },
                        }));
                        setPvsReview(null);
                      }}
                    >
                      Mark reviewed
                    </button>
                  </div>
                </div>
              )}
              <p className="pn-pvs-foot">
                Ask {first} alone. Kept on {first}&rsquo;s note only, never sent to NotePilot.
              </p>
            </>
          ),
        )
      : null;
  // ehr#571 (V141 V91): nothing is picked on open; the clinician picks it
  const safetyStatus = safety?.status;
  const setRisk = (patch: Partial<NoteSafety>) =>
    setSafety((s) => ({ ...(s ?? { status: 'Concerns reported' }), ...patch }));
  const safetySec = secCard(
    'safety',
    'Safety',
    { summary: safetyStatus },
    <>
      <div className="pn-seg" role="group" aria-label="Safety">
        {SAFETY_STATUSES.map((o) => (
          <button
            key={o}
            type="button"
            aria-pressed={safetyStatus === o}
            onClick={() =>
              setSafety(o === 'Concerns reported' ? { ...safety, status: o } : { status: o })
            }
          >
            {o}
          </button>
        ))}
      </div>
      {safety?.status === 'Concerns reported' && (
        <div className="pn-risk">
          <div className="pn-risk-f">
            <span>Risk level</span>
            <div className="pn-seg" role="group" aria-label="Risk level">
              {RISK_LEVELS.map((l) => (
                <button
                  key={l}
                  type="button"
                  aria-pressed={safety.level === l}
                  onClick={() => setRisk({ level: l })}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="pn-risk-f">
            <span>Actions taken</span>
            <div className="pn-checks">
              {RISK_ACTIONS.map((a) => (
                <label key={a} className="pn-check">
                  <input
                    type="checkbox"
                    checked={(safety.actions ?? []).includes(a)}
                    onChange={(e) =>
                      setRisk({
                        actions: e.target.checked
                          ? [...(safety.actions ?? []), a]
                          : (safety.actions ?? []).filter((x) => x !== a),
                      })
                    }
                  />
                  {a}
                </label>
              ))}
            </div>
          </div>
          <label className="pn-risk-f">
            <span>Who was notified</span>
            <textarea
              className="one"
              value={safety.notified ?? ''}
              onChange={(e) => setRisk({ notified: e.target.value })}
            />
          </label>
          <label className="pn-risk-f">
            <span>Follow-up plan</span>
            <textarea
              value={safety.followup ?? ''}
              onChange={(e) => setRisk({ followup: e.target.value })}
            />
          </label>
        </div>
      )}
    </>,
  );
  const goalsLabel = blocks.find((b) => b.id === 'goals')?.label ?? 'Treatment plan goals';
  // ehr#478 (V81 sessSummary): the folded row's pills, one per item and one per diagnosis.
  // Folded unless Open every section is on; a locked note (its buttons are off) shows it open.
  const sxShown = locked || (sxOpen ?? ns.mode.openAll);
  const pos = posOf(place, clientAt);
  const codeEmpty = family
    ? 'Not billed (family member)'
    : group && attendance === 'Not present'
      ? 'Not billed (not present)'
      : noBilling
        ? 'No billing'
        : 'None yet';
  const complexityFits = (COMPLEXITY_OK as readonly string[]).includes(base);
  const sxPills: { t: string; cls?: string; title?: string }[] = [
    sessionTypeReadOnly(kind) || typeLocked(kind) // ehr#708, ehr#726, ehr#729: read-only, the kind
      ? { t: kind }
      : sessionType
        ? { t: sessionType }
        : { t: 'No type', cls: 'none' },
    { t: place === 'In person' ? place : `${place}${locState ? `, ${locState}` : ''}` },
    linkedPlanId ? { t: 'Plan linked' } : { t: 'No plan', cls: 'none' },
    code
      ? { t: code }
      : { t: billedNote ? 'No code' : noBilling ? 'No billing' : 'Not billed', cls: 'none' },
    ...(diagnoses.length
      ? diagnoses.map((d) => ({ t: `Dx ${d.code}`, cls: 'dx', title: d.label }))
      : [{ t: 'No dx', cls: 'none' }]),
    { t: `POS ${pos.split(' ')[0]}` },
    ...(shownModifier ? [{ t: shownModifier }] : []),
  ];
  // ehr#724 (V7 V3, 12a): an Admin / phone note's row shows only the kind and where (no plan,
  // code, dx or POS pills)
  const rowPills = adminOnly ? [{ t: kind }, sxPills[1]!] : sxPills;
  // The codes a note can pick (V81 THERAPY_CPT; DRAWER_CPT on the other notes), the automatic
  // one first, and any code on the note that isn't on the list (a practice's service code).
  // ehr#732: a Medication management note lists its E/M codes only
  const cptList = kindCodeList(kind, otherKind ? OTHER_CPT : THERAPY_CPT);
  const extraCodes = [autoBase, pick ?? ''].filter(
    (c, i, l) => c && !cptList.some(([x]) => x === c) && l.indexOf(c) === i,
  );
  const codeOptions = [
    {
      value: 'auto',
      label: autoBase ? `${cptLabel(autoBase)} (automatic)` : 'Automatic (none yet)',
    },
    ...cptList.map(([c]) => ({ value: c, label: cptLabel(c) })),
    ...extraCodes.map((c) => ({ value: c, label: cptLabel(c) })),
    { value: 'none', label: 'No code' },
  ];
  const sessionSec = secCard(
    'session',
    'Session details',
    { target: 'session' },
    <>
      <div className="pn-sd-client">
        <span>Client</span> <b>{contactName}</b>
        {appointmentKey && (
          // ehr#113: opens that appointment in the Calendar (its ?appt=<eventId>@<date> link).
          <a
            className="pn-appt-pill"
            href={`https://calendar.credifyfast.com/?appt=${encodeURIComponent(appointmentKey)}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Open this appointment in the Calendar"
          >
            From calendar appointment ↗
          </a>
        )}
      </div>
      {(family || group) && (
        <div className="pn-sd-f">
          <span>{contactName.split(' ')[0]}&rsquo;s attendance</span>
          <div className="pn-seg" role="group" aria-label="Attendance">
            {ATTENDANCE_STATUSES.map((a) => (
              <button
                key={a}
                type="button"
                aria-pressed={attendance === a}
                onClick={() => setAttendance(a)}
              >
                {group ? attendanceLabel(a) : a}
              </button>
            ))}
          </div>
        </div>
      )}
      {(group || family) && attendance !== 'Not present' && (
        <div className="pn-grp-fields" data-pn-target="groupfields">
          {(
            [
              ['Participation', GROUP_PARTICIPATION, participation, setParticipation],
              ['Mood', GROUP_MOODS, groupMood, setGroupMood],
            ] as const
          ).map(([label, opts, v, set]) => (
            <div className="pn-sd-f" key={label}>
              <span>
                {label} {group ? reqTag : null}
              </span>
              <div className="pn-seg pn-seg-wrap" role="group" aria-label={label}>
                {opts.map((o) => (
                  <button
                    key={o}
                    type="button"
                    aria-pressed={v === o}
                    disabled={fieldsLocked}
                    onClick={() => (set as (x: string) => void)(v === o ? '' : o)}
                  >
                    {o}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <div className="pn-sd-grid">
        <div className="pn-sd-f">
          <span>Date</span>
          <DateField value={date} onChange={setDate} onInvalid={snapBack('Date')} />
        </div>
        {/* ehr#266: the Credify time picker (type a time or pick one); saved as "3:00 PM". */}
        <div className={`pn-sd-f${endBeforeStart ? ' bad' : ''}`}>
          <span>Start</span>
          <TimeField
            value={to24h(sessionTime)}
            onChange={setStart}
            placeholder="Start"
            ariaLabel="Start time"
            onInvalid={snapBack('Start time')}
          />
        </div>
        <div className={`pn-sd-f${endIssue ? ' bad' : ''}`}>
          <span>End</span>
          <TimeField
            value={to24h(sessionEnd)}
            onChange={setEnd}
            placeholder="End"
            ariaLabel="End time"
            onInvalid={snapBack('End time')}
          />
        </div>
        <div className="pn-sd-f">
          <span>Duration</span>
          <div className={`pn-sd-dur${endIssue ? ' bad' : ''}`} aria-live="polite">
            {endIssue
              ? END_PROBLEM_TEXT[endIssue]
              : sessionDuration > 0
                ? `${sessionDuration} min`
                : ''}
          </div>
        </div>
      </div>
      <div className="pn-sd-f">
        <span>Length</span>
        <div className="pn-seg pn-seg-full" role="group" aria-label="Length">
          {LENGTHS.map((l) => (
            <button
              key={l}
              type="button"
              aria-pressed={sessionDuration === l}
              onClick={() => setLength(l)}
            >
              {l} min
            </button>
          ))}
        </div>
      </div>
      {/* ehr#478 (V81 renderSession): Type, place and billing folds to a row of pills */}
      <div className="pn-sx" data-pn-target="billing">
        <button
          type="button"
          className="pn-sx-tog"
          aria-expanded={sxShown}
          aria-controls="pn-sx-body"
          aria-label={`${typePlaceLabel(kind)}: ${rowPills.map((p) => p.t).join(', ')}${billChecks.length ? `. ${billChecks.length} billing check${billChecks.length === 1 ? '' : 's'}` : ''}`}
          onClick={() => setSxOpen(!sxShown)}
        >
          {/* ehr#724 (V7 V3): Type and place on an Admin / phone note */}
          <b className="pn-sd-k">{typePlaceLabel(kind)}</b>
          {!sxShown && (
            <span className="pn-sx-sum">
              {rowPills.map((p, i) => (
                <span key={i} className={`pn-sx-pill${p.cls ? ` ${p.cls}` : ''}`} title={p.title}>
                  {p.t}
                </span>
              ))}
            </span>
          )}
          <span className="pn-sp" />
          {billChecks.length > 0 && (
            <span className="pn-sx-warn">
              {billChecks.length} billing check{billChecks.length === 1 ? '' : 's'}
            </span>
          )}
          <svg
            className="pn-sx-chev"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        {/* ehr#724 (V7 V3, 12a): opened, Session type (read-only) and Where; no billing */}
        {sxShown && adminOnly && (
          <AdminTypePlace
            kind={kind}
            place={place}
            onPlace={setPlace}
            locState={locState}
            onLocState={setLocState}
            clientAt={clientAt}
            onClientAt={setClientAt}
            disabled={fieldsLocked}
          />
        )}
        {sxShown && !adminOnly && (
          <div className="pn-sx-body" id="pn-sx-body">
            <div className="pn-sx-grid">
              <div className="pn-sd-f">
                <span>Session type</span>
                {sessionTypeReadOnly(kind) || typeLocked(kind) || isCollateral(kind) ? (
                  // ehr#708 (DISCHARGE_SUMMARY_NOTE_10-30-V5), ehr#726 (PSYCH_TESTING_NOTE V4):
                  // the kind, read-only on this note; ehr#729: the same on a Skills / rehab note (V7 V3)
                  // ehr#728 (COLLATERAL_CONTACT_NOTE_10-30-V5): the same on a Collateral contact note
                  <div className="pn-sd-dur" data-test="note-type" aria-label="Session type">
                    {kind}
                  </div>
                ) : (
                  <Dd
                    value={sessionType}
                    searchable={typeOptions.length > 8}
                    onChange={pickType}
                    options={typeOptions}
                    ariaLabel="Session type"
                    disabled={fieldsLocked}
                  />
                )}
              </div>
              <div className="pn-sd-f">
                <span>Where</span>
                <div className="pn-seg pn-seg-full" role="group" aria-label="Where">
                  {NOTE_PLACES.map((pl) => (
                    <button
                      key={pl}
                      type="button"
                      aria-pressed={place === pl}
                      disabled={fieldsLocked}
                      onClick={() => setPlace(pl)}
                    >
                      {pl}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {place !== 'In person' && (
              <div className="pn-sx-grid">
                <div className="pn-sd-f">
                  <span>Client location (state)</span>
                  <Dd
                    value={locState}
                    placeholder="Select state"
                    ariaLabel="Client location (state)"
                    onChange={setLocState}
                    options={US_STATES.map((s) => ({ value: s, label: s }))}
                    disabled={fieldsLocked}
                  />
                </div>
                <div className="pn-sd-f">
                  <span>Client is at</span>
                  <div className="pn-seg pn-seg-full" role="group" aria-label="Client is at">
                    {(['Home', 'Other'] as const).map((o) => (
                      <button
                        key={o}
                        type="button"
                        aria-pressed={clientAt === o}
                        disabled={fieldsLocked}
                        onClick={() => setClientAt(o)}
                      >
                        {o}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
            <div className="pn-sx-grid">
              <div className="pn-sd-f">
                <span>Linked plan</span>
                <Dd
                  value={linkedPlanId ?? ''}
                  searchable={false}
                  placeholder="No plan linked"
                  ariaLabel="Linked plan"
                  disabled={fieldsLocked}
                  onChange={(v) => {
                    planPicked.current = true;
                    setLinkedPlanId(v || null);
                    setGoals(linkable.find((p) => p.id === v)?.goals ?? []);
                    setGoalStatuses([]);
                  }}
                  options={[
                    { value: '', label: 'No plan linked' },
                    ...linkable.map((p) => ({ value: p.id, label: p.label })),
                  ]}
                />
                {unsignedDefault && linkedPlanId === unsignedDefault && (
                  <small className="muted" data-test="plan-unsigned-default">
                    No signed treatment plan yet, so the unsigned plan is linked.
                  </small>
                )}
              </div>
              <div className="pn-sd-f">
                <span>Billing code</span>
                {pickable ? (
                  <Dd
                    value={pick === undefined ? 'auto' : pick === '' ? 'none' : pick}
                    ariaLabel="Billing code"
                    onChange={(v) => setCodePick(v === 'auto' ? undefined : v === 'none' ? '' : v)}
                    options={codeOptions}
                    disabled={fieldsLocked}
                  />
                ) : noBilling ? (
                  // ehr#708 (DISCHARGE_SUMMARY_NOTE_10-30-V5): a No billing kind has no picker
                  <div className="pn-sd-dur" data-test="no-bill" aria-label="Billing code">
                    No billing
                  </div>
                ) : (
                  <div className="pn-sd-dur">{code ? cptLabel(base) : codeEmpty}</div>
                )}
              </div>
            </div>
            <div
              className="pn-sd-f pn-sx-dxf"
              data-pn-target={dxInline ? 'dx' : undefined}
              data-test="sx-dx"
            >
              <span>Diagnosis</span>
              {diagnoses.length ? (
                <ul className="pn-sx-dx" data-dx-note-list="">
                  {diagnoses.map((d, i) => (
                    <li key={d.code}>
                      <b>{d.code}</b> {d.label}
                      {i === 0 && <span className="pn-tag">Primary</span>}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="pn-sx-none">None on the note yet</p>
              )}
              {!fieldsLocked && (
                <button
                  type="button"
                  className="pn-link pn-sx-change"
                  data-dx-open=""
                  onClick={() => (dxInline ? setDxOpen(true) : goTo('dx'))}
                >
                  Change
                </button>
              )}
            </div>
            <div className="pn-sx-bill" data-test="billing-line">
              <span className="pn-sd-k">Billing</span>
              <span>
                Code: <b>{code || codeEmpty}</b>
              </span>
              {diagnoses.length > 0 && (
                <span>
                  Dx: <b>{diagnoses.map((d) => d.code).join(', ')}</b>
                </span>
              )}
              <span>
                POS: <b>{pos}</b>
              </span>
              {shownModifier && (
                <span
                  title={
                    payorRules?.name ? `The modifier ${payorRules.name} bills with` : undefined
                  }
                >
                  Modifier: <b>{shownModifier}</b>
                </span>
              )}
              {unitsText(base, sessionDuration) && (
                <span>
                  Units: <b>{unitsText(base, sessionDuration)}</b>
                </span>
              )}
            </div>
            {(complexity || complexityFits) && (
              <label className="pn-check pn-sx-ic">
                <input
                  type="checkbox"
                  checked={complexity}
                  disabled={fieldsLocked}
                  onChange={(e) => setComplexity(e.target.checked)}
                />
                Interactive complexity (+90785)
              </label>
            )}
            {complexity && complexityFits && (
              <div className="pn-sd-f">
                <span>Reason for interactive complexity</span>
                <Dd
                  value={icReason}
                  searchable={false}
                  placeholder="Select the reason"
                  ariaLabel="Reason for interactive complexity"
                  onChange={setIcReason}
                  options={IC_REASONS.map((r) => ({ value: r, label: r }))}
                  disabled={fieldsLocked}
                />
              </div>
            )}
          </div>
        )}
      </div>
      {billChecks.length > 0 && (
        <div className="pn-bchk" role="status" data-test="bill-checks">
          {billChecks.map((c) => (
            <div key={c.text} className="pn-bchk-row">
              <span>{c.text}</span>
              {c.fix && pickable && !fieldsLocked && (
                <button type="button" className="pn-link" onClick={() => fixCode(c.fix)}>
                  {c.fix === 'none' ? 'Clear code' : `Use ${c.fix}`}
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </>,
  );
  // ehr#571 (PROGRESS_NOTE_10-20-V141): each goal, then its objectives, with a status this session.
  // A goal with objectives keeps its own status in goalStatuses; a goal without any is one row.
  const goalGroups: { goalText: string; rows: number[]; single: boolean }[] = [];
  goals.forEach((g, i) => {
    const grp = goalGroups.find((x) => x.goalText === g.goalText);
    if (grp) grp.rows.push(i);
    else goalGroups.push({ goalText: g.goalText, rows: [i], single: false });
  });
  for (const grp of goalGroups)
    grp.single = grp.rows.length === 1 && !goals[grp.rows[0]!]!.objectiveText;
  const goalLevelOf = (goalText: string): RowStatus =>
    goalStatuses.find((x) => x.goalText === goalText) ?? {};
  const statusItems: { key: string; value: RowStatus }[] = goalGroups.flatMap((grp) =>
    grp.single
      ? [{ key: `r:${grp.rows[0]}`, value: goals[grp.rows[0]!]! }]
      : [
          { key: `g:${grp.goalText}`, value: goalLevelOf(grp.goalText) },
          ...grp.rows.map((i) => ({ key: `r:${i}`, value: goals[i]! })),
        ],
  );
  const setStatusOf = (key: string, next: RowStatus) => {
    const pick = (v: RowStatus) => ({
      status: v.status,
      statusDate: v.statusDate,
      note: v.note,
    });
    if (key.startsWith('r:')) {
      const at = Number(key.slice(2));
      setGoals((rows) => rows.map((r, j) => (j === at ? { ...r, ...pick(next) } : r)));
      return;
    }
    const goalText = key.slice(2);
    // ehr#890 (point 2): a goal's row keeps its place (a new one goes last), so its field-change
    // card (goalLevel.<n>) stays on that goal and its red X puts that goal back
    const row = {
      goalText,
      ...(next.planStatus ? { planStatus: next.planStatus } : {}),
      ...pick(next),
    };
    setGoalStatuses((l) =>
      l.some((x) => x.goalText === goalText)
        ? l.map((x) => (x.goalText === goalText ? row : x))
        : [...l, row],
    );
  };
  const statusRange = statusDateRange({
    planSignedAt: linkedPlan?.signedAt,
    sessionDate: date,
    today: localToday(),
  });
  const counts = statusCounts(statusItems.map((x) => x.value));
  const toConfirm = fieldsLocked ? [] : confirmable(statusItems.map((x) => x.value));
  const confirmOthers = () => {
    const keys = toConfirm.map((i) => statusItems[i]!);
    for (const it of keys)
      setStatusOf(it.key, {
        ...it.value,
        status: it.value.planStatus as RowStatus['status'],
        statusDate: defaultStatusDate({
          status: it.value.planStatus!,
          sessionDate: date,
          planStatus: it.value.planStatus,
          range: statusRange,
        }),
      });
    setNotice(`Confirmed ${keys.length} status${keys.length === 1 ? '' : 'es'}.`);
  };
  const statusCell = (key: string, value: RowStatus, text: string) => (
    <GoalStatusCell
      text={text}
      value={value}
      onChange={(v) => setStatusOf(key, v)}
      open={statusPop?.key === key ? statusPop.view : false}
      onOpen={(view) => setStatusPop(view ? { key, view } : null)}
      range={statusRange}
      sessionDate={date}
      locked={fieldsLocked}
    />
  );
  const statusNote = (key: string, value: RowStatus, text: string) => (
    <StatusNote
      text={text}
      value={value}
      locked={fieldsLocked}
      onEdit={() => setStatusPop({ key, view: 'note' })}
    />
  );
  // ehr#682 (V141): "addressed" moved out of this section into Problems, goals, and objectives
  // addressed (the focus block below it)
  const goalsSec = secCard(
    'goals',
    goalsLabel,
    {
      summary: goals.length ? undefined : 'No plan goals',
      action:
        toConfirm.length > 0 ? (
          <button type="button" className="pn-link" onClick={confirmOthers}>
            Confirm the other {toConfirm.length}
          </button>
        ) : undefined,
    },
    goals.length === 0 && (group || family) && !linkedPlan && linkable.length === 0 ? (
      // ehr#571 round 2 (V22 / V11 goalsHTML): an attendee with no plan needs nothing here
      <div className="pn-noplan" data-test="goal-noplan">
        <b>{contactName} has no treatment plan yet</b>
        <p>
          There are no goals to update for them this session. Nothing here is needed to sign their
          note.
        </p>
      </div>
    ) : goals.length === 0 && isDischarge && !linkedPlanId ? (
      // ehr#708 (Jay 10-30, Q2): no plan on file: the reason why, from the practice's list
      <NoPlanReasonField
        value={noPlanReason}
        reasons={practiceNotes.dischargeNoPlanReasons}
        locked={fieldsLocked}
        onChange={(v) => {
          markTouched();
          setNoPlanReason(v);
        }}
      />
    ) : goals.length === 0 ? (
      <p className="muted" style={{ margin: 0 }}>
        No linked plan goals. Pick a treatment plan in Session details, or add one in the Treatment
        Plans column.
      </p>
    ) : (
      <div className="gs-wrap">
        {GOAL_FINAL_KINDS.includes(layoutKind) && practiceNotes.requireGoalStatus && (
          // ehr#708: signing waits until each row below has a final status (leftToSign, server
          // 422); Jay 10-30 Q1: these four, a Discontinued one with its reason
          <p className="gs-final" data-test="goal-final">
            Discharge: give every goal and objective a final status. Met, Partially met, Not met or
            Discontinued (with a reason).
          </p>
        )}
        <div className="gs-meta">
          {group || family ? (
            // V22 goalMeta: whose plan it is, on a session with several attendees
            <span className="muted gs-who">
              <span className="pn-gav" aria-hidden>
                {initials(contactName)}
              </span>
              <b>{contactName}&rsquo;s plan</b> · {linkedPlan?.label ?? 'the linked plan'}
            </span>
          ) : (
            <span className="muted">From {linkedPlan?.label ?? 'the linked plan'}</span>
          )}
          <span className="gs-count" data-test="goal-count">
            <span className="gs-meter" aria-hidden>
              <i
                style={{
                  width: `${counts.total ? Math.round((counts.set / counts.total) * 100) : 0}%`,
                }}
              />
            </span>
            {locked
              ? `${counts.set} of ${counts.total} with a status`
              : `${counts.set} of ${counts.total} set`}
          </span>
        </div>
        {goalGroups.map((grp, gi) => {
          const head = grp.single ? `r:${grp.rows[0]}` : `g:${grp.goalText}`;
          const headVal = grp.single ? goals[grp.rows[0]!]! : goalLevelOf(grp.goalText);
          return (
            <div className="gs-card" key={`${gi}:${grp.goalText}`} data-test="goal-card">
              <div className="gs-row gs-goal">
                <span className="gs-num">{gi + 1}</span>
                <span className="gs-t">
                  <span className="gs-k goal">Goal</span>
                  <span className="gs-stmt">{grp.goalText || '(goal)'}</span>
                </span>
                {statusCell(head, headVal, grp.goalText || 'the goal')}
              </div>
              {statusNote(head, headVal, grp.goalText || 'the goal')}
              {!grp.single &&
                grp.rows.map((i, oi) => {
                  const o = goals[i]!;
                  const key = `r:${i}`;
                  return (
                    <Fragment key={key}>
                      <div className="gs-row gs-obj" data-test="obj-row">
                        <span className="gs-num-sp" aria-hidden />
                        <span className="gs-t">
                          <span className="gs-k">
                            <span className="gs-idx">
                              {gi + 1}.{oi + 1}
                            </span>{' '}
                            Objective
                          </span>
                          <span className="gs-stmt">{o.objectiveText}</span>
                        </span>
                        {statusCell(key, o, o.objectiveText)}
                      </div>
                      {statusNote(key, o, o.objectiveText)}
                    </Fragment>
                  );
                })}
            </div>
          );
        })}
      </div>
    ),
  );
  // ── ehr#682 (V141): Problems, goals, and objectives addressed ──────────────────────────────
  const focusPrefs = phrases?.focus ?? EMPTY_FOCUS_PREFS;
  const focusList: FocusChoice[] = focusChoices({
    plan: planFocusItems(linkedPlan?.problems, goals),
    prefs: focusPrefs,
    contactId,
    on: focusOn,
    off: focusOff,
  });
  const focusFirst = contactName.split(' ')[0] ?? contactName;
  const saveFocusPrefs = (next: typeof focusPrefs | null, done: string) => {
    if (!next) {
      setNotice(done);
      return;
    }
    api
      .put<NotePhrasesView>('/note-phrases/focus', next)
      .then((v) => {
        setPhrases(v);
        setNotice(done);
      })
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.message : 'Could not update your note settings.'),
      );
  };
  const focusState = { on: focusOn, off: focusOff };
  const setFocus = (s: { on: FocusItem[]; off: string[] }) => {
    markTouched();
    setFocusOn(s.on);
    setFocusOff(s.off);
  };
  const focusSec = (b: NoteBlock) =>
    secCard(
      b.id,
      b.label,
      {
        summary: focusOn.length ? `${focusOn.length} picked` : undefined,
      },
      <FocusSection
        choices={focusList}
        locked={fieldsLocked}
        firstName={focusFirst}
        onToggle={(c) => setFocus(toggleFocus(focusState, c))}
        onAdd={(label) => {
          const r = addFocus(focusState, focusList, label);
          setFocus(r);
          // V141 addFocusLabel: an item of your own stays on this client's notes
          saveFocusPrefs(
            r.item.kind === 'custom'
              ? focusPrefsWithAdded(focusPrefs, r.item.label, contactId)
              : null,
            r.wasOn ? `"${r.item.label}" is already in the list.` : `Added "${r.item.label}".`,
          );
        }}
        onRemove={(c, scope) => {
          setFocus(removeFocus(focusState, c.key, scope));
          saveFocusPrefs(
            scope === 'note' ? null : focusPrefsWithout(focusPrefs, c.key, scope, contactId),
            scope === 'note'
              ? 'Removed from this note.'
              : scope === 'client'
                ? `Removed for ${focusFirst} going forward.`
                : 'Removed for all clients going forward.',
          );
        }}
      />,
    );
  const dxSec = (
    <div data-pn-target="dx" className="pn-dx">
      <DiagnosisCodesCard
        contactId={contactId}
        contactName={contactName}
        value={diagnoses}
        onChange={setDiagnoses}
        concerns={format === 'CREDIFY' ? credify.concerns : []}
      />
    </div>
  );
  // ehr#437 part 3: a layout's own box (Presenting problem) and checklist (Mental status).
  const quickX = (b: NoteBlock) => {
    const it = quick.find((q) => q.id === b.id);
    return b.quick && it && !locked ? (
      <button
        type="button"
        className="pn-sec-x"
        aria-label={`Remove ${b.label}`}
        title="Remove from this note"
        onClick={() => setQuickAsk(it)}
      >
        ×
      </button>
    ) : undefined;
  };
  const extraRating = (b: NoteBlock) => {
    const v = extras[b.id];
    const lo = b.min ?? 0;
    const hi = b.max ?? 10;
    return secCard(
      b.id,
      b.label,
      { summary: typeof v === 'string' && v ? `${v} of ${hi}` : undefined, action: quickX(b) },
      <div className="pn-rate" role="group" aria-label={b.label}>
        {Array.from({ length: hi - lo + 1 }, (_, i) => String(lo + i)).map((n) => (
          <button
            key={n}
            type="button"
            aria-pressed={v === n}
            disabled={fieldsLocked}
            onClick={() => setExtra(b.id, v === n ? '' : n)}
          >
            {n}
          </button>
        ))}
      </div>,
    );
  };
  const extraBox = (b: NoteBlock) => {
    const v = extras[b.id];
    return secCard(
      b.id,
      b.label,
      { required: b.required, target: `sec-${b.id}`, action: quickX(b) },
      <div className="pn-ta-wrap">
        <textarea
          className="pn-extra-ta"
          value={typeof v === 'string' ? v : ''}
          placeholder={promptFor(b.id, b.hint ?? '')}
          aria-label={b.label}
          onChange={(e) => setExtra(b.id, e.target.value)}
        />
        <DictateMic
          dictate={dictate}
          label={b.label}
          value={typeof v === 'string' ? v : ''}
          onText={(t) => setExtra(b.id, t)}
        />
      </div>,
    );
  };
  const extraChecklist = (b: NoteBlock) => {
    const v = extras[b.id];
    const on = Array.isArray(v) ? v : [];
    const items = b.items ?? [];
    return secCard(
      b.id,
      b.label,
      {
        summary: on.length ? `${on.length} of ${items.length}` : undefined,
        action: quickX(b),
      },
      <div className="pn-checks">
        {items.map((it) => (
          <label key={it} className="pn-check">
            <input
              type="checkbox"
              checked={on.includes(it)}
              onChange={(e) =>
                setExtra(b.id, e.target.checked ? [...on, it] : on.filter((x) => x !== it))
              }
            />
            {it}
          </label>
        ))}
      </div>,
    );
  };
  // ehr#727 (PEER_SUPPORT_NOTE_10-30-V8): the Peer support layout's Recovery goal (12a) and
  // activities (11a), in body.extras like any layout part (components/PeerSupportFields)
  const recoveryGoalSec = (b: NoteBlock) => {
    const v = boxText(b.id);
    return secCard(
      b.id,
      b.label,
      { target: `sec-${b.id}` },
      <RecoveryGoalField
        value={v}
        onChange={(t) => setExtra(b.id, t)}
        fromTag={fieldsLocked ? '' : recoveryFromTag(v, lastRecovery)}
        dictate={dictate}
        disabled={fieldsLocked}
        label={b.label}
        placeholder={b.hint ?? ''}
      />,
    );
  };
  const peerActivitiesSec = (b: NoteBlock) => {
    const v = extras[b.id];
    const picked = Array.isArray(v) ? v : [];
    return secCard(
      b.id,
      b.label,
      { summary: picked.length ? `${picked.length} picked` : undefined },
      <PeerActivitiesField
        items={peer.lists.activities}
        picked={picked}
        onChange={(l) => setExtra(b.id, l)}
        onAdd={peer.addActivity}
        onNotice={setNotice}
        disabled={fieldsLocked}
        label={b.label}
      />,
    );
  };
  // ehr#571 (reopen 10-27): the reviewer edits every part. Before, only the template's text boxes
  // opened: a New Client note's Presenting problem, Relevant history and Mental status, a Quick
  // add section, Session details, goals, diagnosis, interventions and safety all sat in a disabled
  // fieldset, so on an intake note the reviewer could change nothing but Plan.
  const renderBlock = (b: NoteBlock): ReactNode => {
    if (b.kind === 'box' && reviewPartMode(b.kind, { status, canReview, tracked }) === 'review-box')
      return textSec([b.label, b.id, b.hint ?? ''], Boolean(b.required), {
        value: boxText(b.id),
        set: (v) => setExtra(b.id, v),
      });
    return renderPart(b);
  };
  /** ehr#571 (reopen 10-27, ask 2): the field-change cards beside a part. */
  const fcName = (c: NoteFieldChange) =>
    c.at === '' && c.by === (currentUserId ?? '') ? userName(currentUserId) : userName(c.by);
  const partLabel = (sec: string) => blocks.find((b) => b.id === sec)?.label ?? 'Session details';
  /**
   * ehr#741 (point 4 and point 1A): put the note's other parts (Session details, goals, diagnosis,
   * interventions, safety, checklists and ratings, as REVIEW_FIELD_KEYS names them) into the editor
   * from a body: a field put back by a card's red X, or the reviewer's saved draft.
   */
  const loadReviewParts = (parts: Record<string, unknown>, nextDate?: string) => {
    if (nextDate && nextDate !== date) setDate(nextDate);
    for (const [k, v] of Object.entries(parts)) {
      if (k === 'linkedPlanId') setLinkedPlanId(typeof v === 'string' ? v : null);
      else if (k === 'session') {
        const ss = (v ?? {}) as NonNullable<ProgressBody['session']>;
        setSessionTime(ss.time ?? '');
        setSessionEnd(ss.end ?? '');
        setSessionDuration(savedLength(ss));
        if (ss.type) setSessionType(ss.type);
        setPlace(ss.place ?? 'In person');
        setCodePick(typeof ss.codePick === 'string' ? ss.codePick : undefined);
        setComplexity(Boolean(ss.complexity));
        setIcReason(ss.icReason ?? '');
        setLocState(ss.locState ?? '');
        setClientAt(ss.clientAt === 'Other' ? 'Other' : 'Home');
      } else if (k === 'goalsAddressed') setGoals(Array.isArray(v) ? (v as GoalAddressed[]) : []);
      else if (k === 'goalStatuses') setGoalStatuses(readGoalLevels(v));
      else if (k === 'focusAddressed') setFocusOn(addressedFocusOf({ focusAddressed: v ?? [] }));
      else if (k === 'focusOff') setFocusOff(strList(v));
      else if (k === 'diagnoses') setDiagnoses(readDiagnoses(v));
      else if (k === 'interventionsUsed') setInterventionsUsed(strList(v));
      else if (k === 'safety') setSafety((v as ProgressBody['safety']) ?? null);
      else if (k === 'partnerSafety') setPartnerSafety((v as PartnerSafety | undefined) ?? null);
      else if (k === 'attendance') {
        const st = (v as { status?: string } | undefined)?.status;
        if (st && (ATTENDANCE_STATUSES as readonly string[]).includes(st))
          setAttendance(st as (typeof ATTENDANCE_STATUSES)[number]);
      } else if (k === 'participation') {
        if ((GROUP_PARTICIPATION as readonly unknown[]).includes(v))
          setParticipation(v as (typeof GROUP_PARTICIPATION)[number]);
      } else if (k === 'groupMood') {
        if ((GROUP_MOODS as readonly unknown[]).includes(v))
          setGroupMood(v as (typeof GROUP_MOODS)[number]);
      } else if (k === 'extras') setExtras(readExtras(v));
      else if (
        k === 'concerns' ||
        k === 'interventions' ||
        k === 'clientResponse' ||
        k === 'nextSteps'
      )
        setCredify((c) => readCredify({ ...c, [k]: v } as Record<string, unknown>));
    }
  };
  /**
   * ehr#741 (Chase 10-30, point 1A; V141 V100): the red X on a field-change card puts the field
   * back to what it was before the change. The person's own change not sent yet simply goes; the
   * other side's change is undone as a change of their own (its card records it).
   * ehr#741 (Chase 10-31, points 6 and 8; V132): the other side's card then shows red (not kept)
   * and stays; the person's own card goes with its replies and tick (and so does a card the put
   * back takes with it, the Duration of an End time), so a later change starts a new card and the
   * old ones never come back. That holds for a card already saved on the note too: its thread is
   * left closed and empty, and the server empties its copy.
   * ehr#890 (Chase 11-02, point 2): the person's own card no longer just goes: it stays red with its
   * X boxed for the rest of the sitting (ownRed), as the other side's does, until the field is
   * changed again. (point 4) On an Added or Removed card only that half is put back.
   */
  const revertCard = (p: CardPart) => {
    const c = p.card;
    const id = p.id;
    if (cardState(threadOfPart(p)) === 'denied') return;
    const field = c.key.slice(2);
    const own = fcSide !== null && c.key.startsWith(`${fcSide}:`);
    const live = own && c.at === '';
    const baseRaw = live && fbase ? fieldRaw(field, fbase.payload as never) : c.fromRaw;
    const nowRaw = fieldRaw(field, buildPayload() as never);
    // ehr#890 (point 4): an Added card takes out what it added, a Removed card puts back what it
    // removed; the other half stays as it is
    const raw =
      p.half && p.items
        ? halfRaw(field, nowRaw, p.items, p.half === 'add' ? 'drop' : 'put', baseRaw)
        : baseRaw;
    if (raw === undefined) {
      setNotice('That change is too old to put back from here. Change the field by hand.');
      return;
    }
    const closed = putField(field, raw);
    addLog('revert', c.label, p.half ? p.text : `put back ${c.from || 'blank'}`);
    if (!own) {
      setLiveThreads((l) =>
        withDecision(withPartSeeded(l, p, cardMe), { id, section: c.sec }, cardMe, 'denied'),
      );
      return;
    }
    // ehr#890 (point 2): the person's own card stays, red, until they change the field again
    const was = threadOfPart(p);
    setOwnRed((r) => [
      ...r.filter((x) => x.id !== id),
      { ...p, replies: repliesOf(was), restore: nowRaw },
    ]);
    if (p.half) setLiveThreads((l) => withClosedThreads(l, [id], cardMe));
    else if (!closed.includes(c.key)) setLiveThreads((l) => withClosedCards(l, [c.key], cardMe));
  };
  /**
   * ehr#741 (Chase 10-31, point 8; V132): the check on a field-change card keeps it (green); on a
   * kept card it takes the tick off. On a card not kept it switches to kept and puts the change
   * back, the field as it was before the red X.
   */
  const keepCard = (p: CardPart) => {
    const c = p.card;
    const t = { id: p.id, section: c.sec };
    const was = cardState(threadOfPart(p));
    if (was === 'denied' && fbase) {
      const field = c.key.slice(2);
      const baseRaw = fieldRaw(field, fbase.payload as never);
      const raw =
        p.half && p.items
          ? halfRaw(
              field,
              fieldRaw(field, buildPayload() as never),
              p.items,
              p.half === 'add' ? 'put' : 'drop',
              baseRaw,
            )
          : baseRaw;
      if (raw !== undefined) putField(field, raw);
    }
    addLog(
      was === 'kept' ? 'reopen' : 'resolve',
      secName(t.section),
      p.half ? p.text : cardWords(t.id),
    );
    setLiveThreads((l) =>
      withDecision(withPartSeeded(l, p, cardMe), t, cardMe, was === 'kept' ? 'pending' : 'kept'),
    );
  };
  /**
   * ehr#890 (point 2): the check on the person's own red card puts their change back in and keeps
   * it (green), as the check on the other side's red card does.
   */
  const keepOwnRed = (f: OwnRedCard) => {
    const field = f.card.key.slice(2);
    const raw =
      f.half && f.items
        ? halfRaw(
            field,
            fieldRaw(field, buildPayload() as never),
            f.items,
            f.half === 'add' ? 'put' : 'drop',
            f.restore,
          )
        : f.restore;
    if (raw === undefined) {
      setNotice('That change is too old to put back from here. Change the field by hand.');
      return;
    }
    putField(field, raw);
    setOwnRed((r) => r.filter((x) => x.id !== f.id));
    addLog('resolve', secName(f.card.sec), f.text);
    setLiveThreads((l) => withDecision(l, { id: f.id, section: f.card.sec }, cardMe, 'kept'));
  };
  /** A card's thread: a half of a list's card reads the combined card's on an older note. */
  const threadOfPart = (p: CardPart) =>
    partThread(liveThreads, p, partsOfCard(p.card)[0]?.id === p.id);
  /**
   * One tracked field set to a raw value (JSON) in the editor. The person's own cards it takes away
   * (saved or not) are closed for good; their keys come back.
   */
  const putField = (field: string, raw: string): string[] => {
    markTouched();
    const now = buildPayload();
    const next = withFieldRaw(
      field,
      { date: now.date, body: now.body as never },
      JSON.parse(raw) as unknown,
    );
    const was = now.body as Record<string, unknown>;
    const nb = next.body as Record<string, unknown>;
    const parts: Record<string, unknown> = {};
    for (const k of REVIEW_FIELD_KEYS)
      if (JSON.stringify(was[k] ?? null) !== JSON.stringify(nb[k] ?? null)) parts[k] = nb[k];
    loadReviewParts(parts, next.date);
    if (fbase && fcSide) {
      const after = liveFieldChanges(
        shotOf(fbase.payload),
        shotOf({ ...now, date: next.date, body: next.body } as never),
      );
      const left = mergeFieldChanges(fcLoaded, after, fcSide, cardMe, fcComments, (k) =>
        fieldRaw(k, fbase.payload as never),
      );
      const closed = closedCards(fieldCards, left, fcSide);
      if (closed.length) setLiveThreads((l) => withClosedCards(l, closed, cardMe));
      return closed;
    }
    return [];
  };
  // ehr#741 (Chase 10-31, point 7): the cards in the order of their fields on the note
  const fieldOrder = fieldCards.length ? Object.keys(shotOf(nowPayload ?? buildPayload())) : [];
  const inSec = (c: NoteFieldChange, sec: string) =>
    c.sec === sec || (sec === 'session' && !shownBlocks.some((b) => b.id === c.sec));
  // ehr#890 (point 4): a list's card shows as an Added card and a Removed card; (point 2) the
  // person's own red cards sit with them, in the order of their fields
  const partsOf = (sec: string): (CardPart | OwnRedCard)[] => {
    const live = fieldCards.filter((c) => inSec(c, sec)).flatMap((c) => partsOfCard(c));
    const ids = new Set(live.map((x) => x.id));
    const red = ownRed.filter((f) => inSec(f.card, sec) && !ids.has(f.id));
    const all: (CardPart | OwnRedCard)[] = [...live, ...red];
    const keys = [...new Set(all.map((x) => x.card.key))];
    const rank = (x: CardPart | OwnRedCard) =>
      keys.indexOf(x.card.key) * 2 + (x.half === 'rem' ? 1 : 0);
    return orderFieldCards(
      [...all].sort((a, b) => rank(a) - rank(b)).map((x) => ({ key: x.card.key, x })),
      fieldOrder,
    ).map((y) => y.x);
  };
  const fieldCardsOf = (sec: string) =>
    partsOf(sec).map((p) => {
      const c = p.card;
      if ('restore' in p)
        return (
          <FieldChangeCard
            key={p.id}
            who={fcName(c)}
            photo={photoOf(c.by || currentUserId)}
            label={partLabel(c.sec)}
            text={p.text}
            half={p.half}
            comment={c.comment}
            state="denied"
            onKeep={cardsOpen ? () => keepOwnRed(p) : undefined}
            onRevert={cardsOpen ? () => undefined : undefined}
            replies={p.replies}
          />
        );
      const t = { id: p.id, section: c.sec };
      const kept = threadOfPart(p);
      // ehr#741 (point 1): V141's card: no "not sent yet" line, the check and X in the corner,
      // replies and a Reply box; (10-31, point 8) its colour shows kept or not kept
      return (
        <FieldChangeCard
          key={p.id}
          who={fcName(c)}
          photo={photoOf(c.by || currentUserId)}
          label={partLabel(c.sec)}
          text={p.text}
          half={p.half}
          comment={c.comment}
          state={cardState(kept)}
          onKeep={cardsOpen ? () => keepCard(p) : undefined}
          onRevert={cardsOpen ? () => revertCard(p) : undefined}
          replies={repliesOf(kept)}
          onReply={cardsOpen ? (x) => replyOn(t, x) : undefined}
        />
      );
    });
  const renderPart = (b: NoteBlock): ReactNode => {
    switch (b.kind) {
      case 'session':
        return <Fragment key="session">{sessionSec}</Fragment>;
      case 'goals':
        return <Fragment key="goals">{goalsSec}</Fragment>;
      case 'focus':
        return <Fragment key="focus">{focusSec(b)}</Fragment>;
      case 'dx':
        return <Fragment key="dx">{dxSec}</Fragment>;
      case 'interventions':
        return <Fragment key="interventions">{interventionsSec}</Fragment>;
      case 'safety':
        return (
          <Fragment key="safety">
            {safetySec}
            {pvsSec}
          </Fragment>
        );
      case 'text':
        return textSec([b.label, b.id, b.hint ?? ''], Boolean(b.required));
      case 'box':
        return (
          <Fragment key={b.id}>
            {b.id === RECOVERY_GOAL_ID ? recoveryGoalSec(b) : extraBox(b)}
          </Fragment>
        );
      case 'checklist':
        return (
          <Fragment key={b.id}>
            {b.id === PEER_ACTIVITIES_ID ? peerActivitiesSec(b) : extraChecklist(b)}
          </Fragment>
        );
      case 'rating':
        return <Fragment key={b.id}>{extraRating(b)}</Fragment>;
    }
  };
  // ehr#571 (V141 V111): the rail cards on show that fold, for Collapse all / Expand all
  const railCards = (
    [
      // ehr#708 (Jay 10-30, Q6): a discharge summary has no Quick add, Measures or My changes
      ['quick', !locked && v81 && !quickHidden && !cardOffForKind(kind, 'quick')],
      [
        'measures',
        measures.length > 0 && !cardsOff.includes('measures') && !cardOffForKind(kind, 'measures'),
      ],
      ['last', (!!lastNote || otherKind) && !cardsOff.includes('last')],
      [
        'changes',
        !locked && v81 && !cardsOff.includes('changes') && !cardOffForKind(kind, 'changes'),
      ],
    ] as [RailCard, boolean][]
  )
    // ehr#728, ehr#729, ehr#732: a Collateral contact, Skills / rehab or Medication management
    // note's rail is Generate Notes and Last session only
    .filter(([c, shown]) => shown && railCardOn(kind, c) && railFold(c))
    .map(([c]) => c);
  const anyRailOpen = railCards.some((c) => railFold(c)?.open);
  // ehr#571 (reopen 10-23): review happens in the note itself; the rail gives way to the margin.
  const reviewing = suggestMode || (tracked && (editCount > 0 || commentCount > 0));
  // ehr#571 (reopen 10-27, ask 2): field-change cards open the margin too (a co-signed note keeps
  // them as its record)
  // ehr#741: and the comment threads kept on a returned note
  const marginOn =
    reviewing ||
    fieldCards.length > 0 ||
    ownRed.length > 0 ||
    (status === 'changes' && liveThreads.some((t) => t.id.startsWith('c:')));
  // ehr#741 (Chase 10-30, point 9): the side panel (Generate Notes, Quick add, ...) can be hidden,
  // and with the review margin open the page uses the room either side, so the note keeps its width
  const railShown = !reviewing && loaded && !railHidden;
  // ehr#571 (V141 aiReviewItems): each box still holding generated text it hasn't been reviewed
  // ... under the box's own name ("Review AI text: What happened" on a Service note)
  const aiLeft = blocks
    .filter((b) => b.kind === 'text' && npDrafted.includes(b.id) && (pbody[b.id] ?? '').trim())
    .map((b) => ({ label: aiReviewLabel(b.label), target: `sec-${b.id}` }));
  const left =
    isAuthor && !locked && loaded && !(suggestMode || tracked)
      ? [...signChecks(), ...aiLeft]
      : cosignLeft;
  // ehr#688 (GROUP_NOTE_10-29-V25, V21 22a): on a group member's note, each box still empty that
  // sign-off needs shows red as missing, while its writer can change it. Generate leaves a box empty
  // when the group's notes say nothing for it, so the red shows what is left to write. Group only:
  // whether the strict rules reach every note is #688 question 3.
  const missRed =
    group && isAuthor && !locked && loaded && !(suggestMode || tracked)
      ? missingBoxes(left, pbody)
      : new Set<string>();
  // ehr#862: on every template, Credify Note too (clearNote empties its fields as well)
  const canClear = isAuthor && !locked && loaded && !family && !group && !inReview;
  // V81 clearNote: everything typed or picked goes; the session's date, time, type and place, the
  // diagnosis codes, the linked plan, Last session and measures stay. Not saved until Save Draft.
  const clearNote = () => {
    setPbody({});
    setNpDrafted([]);
    setRoughNotes('');
    setApproaches([]);
    setCredify(EMPTY_CREDIFY);
    setInterventionsUsed([]);
    setNecessity('');
    setSafety(null);
    setExtras({});
    setMyChanges([]);
    setFollowUps([]);
    setCopied({});
    setCopiedFrom(null);
    setSessionEnd(
      sessionTime && sessionDuration > 0 ? addMinutes(sessionTime, sessionDuration) : '',
    );
    setGoals(linkedPlan?.goals ?? []);
    setGoalStatuses([]);
    setFocusOn([]); // ehr#682
    setFocusOff([]);
    setQuick(
      phrases &&
        !(NOTE_KINDS_WITHOUT_DX as readonly string[]).includes(kind) &&
        railCardOn(kind, 'quick')
        ? quickOnNoteFor(phrases.quick ?? EMPTY_QUICK_PREFS, contactId).filter(
            (q) => q.id !== 'r_relsat' || kind === 'Couples',
          )
        : [],
    );
    setFolded(new Set());
    setHistOpen(false);
    setErr('');
    setNpKey((k) => k + 1);
    autoDrafted.current = true; // a cleared note doesn't draft itself again
    setNotice('Note cleared. Save Draft to keep the blank note.');
    window.scrollTo(0, 0);
  };
  // V81 SHORTCUTS: the latest handlers, for the key listener below
  keysRef.current = {
    save: () => {
      if (!busy && loaded && !locked && !suggestMode) saveDraft();
    },
    sign: () => {
      if (!busy && loaded && !primaryDisabled && !suggestMode) primaryFn();
    },
    next: () => {
      if (left[0]) goTo(left[0].target);
    },
    list: () => setKeysOpen(true),
  };

  return (
    <div
      className="pn-page"
      role="dialog"
      aria-modal="true"
      aria-label="Progress note"
      onPointerDownCapture={markTouched}
      onKeyDownCapture={markTouched}
    >
      <div className="pn-page-top">
        <div className="pn-page-in pn-page-top-in">
          <button type="button" className="pn-back" onClick={() => requestLeave(onClose, onSaved)}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Back to chart
          </button>
          <h1>{noteTitle(family ? family.type : kind)}</h1>
          {/* ehr#478 (V81 note menu): the template, then the layout, beside the title */}
          {!otherKind && !group && (
            <NoteTemplateMenu
              format={format}
              formats={FORMATS.map((f) => ({ value: f, label: NOTE_FORMAT_LABELS[f] }))}
              layout={kind}
              layouts={
                !family && (kind === 'Individual' || kind === 'Crisis')
                  ? [
                      { value: 'Individual', label: 'Default layout' },
                      { value: 'Crisis', label: 'Crisis' },
                    ]
                  : []
              }
              disabled={locked}
              onFormat={(v) => {
                // ehr#571 (V141 V115): SOAP to DAP keeps the writing (Data gets Subjective +
                // Objective), DAP to SOAP puts Data into an empty Subjective
                const to = asFormat(v);
                setPbody((b) =>
                  carryTemplateText(
                    b,
                    FORMAT_SECTIONS[to].map(([, k]) => k),
                  ),
                );
                setFormat(to);
              }}
              onLayout={(v) => setNoteKind(v)}
            />
          )}
          {/* ehr#437 part 3: the kind of note when it isn't a plain progress note */}
          {kind !== 'Individual' && <span className="pn-kind">{kind}</span>}
          <span className={`pn-status-pill ${noteStatusClass(status)}`}>{pill}</span>
          <span className="pn-sp" />
          <span className="pn-signed-as">
            Signed in as <b>{userName(currentUserId)}</b>
          </span>
          {/* ehr#437 reopen (V81): Clear note and Note settings */}
          {canClear && (
            <button type="button" className="btn pn-clear" onClick={() => setClearAsk(true)}>
              Clear note
            </button>
          )}
          {/* ehr#682 (V141 V129): no Quick add button here; Show Quick add is in Note settings */}
          {!viewOnly && (
            // ehr#571 (V141 V123): one labelled "Note settings" button, the way into settings;
            // ehr#862: on every template, Credify Note too
            <button
              type="button"
              className="btn pn-ns-btn"
              disabled={!phrases}
              onClick={() => setNsOpen('note')}
            >
              <Cog />
              Note settings
            </button>
          )}
        </div>
      </div>

      <div
        className={`pn-page-in pn-page-grid${railShown ? '' : ' tc-wide'}${marginOn ? ' tc-roomy' : ''}`}
      >
        <div className="pn-page-main">
          {notice && (
            <div className="pn-banner copy pn-notice" role="status">
              {notice}
            </div>
          )}
          {/* ehr#682 (V131): a second copy of the note is read-only until it is free */}
          {tabLock.readOnly && (!baseLocked || reviewTabReadOnly) && (
            <div className="pn-banner lock pn-tablock" role="status">
              <span>
                {reviewTabReadOnly ? 'This review' : 'This note'} is open in another tab. This copy
                is read-only, so the two can&rsquo;t save over each other.
              </span>
              <button
                type="button"
                className="btn small primary"
                onClick={tabLock.takeOver}
                disabled={tabLock.state === 'opening'}
              >
                {tabLock.state === 'opening' ? 'Opening here...' : 'Open here instead'}
              </button>
            </div>
          )}
          {(family ?? group)?.bar}
          {family?.billed && family.othersAway && attendance !== 'Not present' && (
            <section
              className="pn-sec pn-fam-only"
              data-pn-sec="famOnly"
              data-pn-target="famOnly"
              aria-label="Only the client came"
            >
              <p>
                <b>Only {first} came.</b> With nobody else there it was an individual session, so it
                belongs on a Progress Note. It can&rsquo;t be signed off here.
              </p>
              {!locked && (
                <button type="button" className="btn primary" onClick={family.onHandOff}>
                  Open a Progress Note
                </button>
              )}
            </section>
          )}
          {/* fbs#97: a note copied from a saved one, until it is signed off */}
          {copiedFrom && (status === 'draft' || status === 'changes') && (
            <div className="pn-banner copy">
              Copied from the {copiedFrom.date ? fmtMDY(`${copiedFrom.date}T12:00:00`) : 'earlier'}{' '}
              note. Update it for this session: it can&rsquo;t be signed off while it is still
              word-for-word the same.
            </div>
          )}

          {/* calendar#46 (PROGRESS_NOTE_GROUP_A-3 banners) */}
          {status === 'changes' && (
            <div className="pn-banner sup">
              <div className="pn-banner-main">
                <span className="who">
                  {userName(lastChanges?.actorId ?? assignedCosignerId)}
                  {lastChanges ? ` · ${timeOf(lastChanges.createdAt)}` : ''}
                </span>
                <span>{lastChanges?.message || 'Returned for changes.'}</span>
                {isAuthor && !replyOpen && (
                  <button type="button" className="pn-link" onClick={() => setReplyOpen(true)}>
                    Reply
                  </button>
                )}
              </div>
              {/* ehr#571 (reopen 10-23, V141 C1): what is left before the note can go back */}
              {tracked && (editCount > 0 || commentCount > 0) && (
                <p className="tc-left">
                  {editCount > 0
                    ? keepGate
                      ? `${plural(editCount, 'edit')} ${editCount === 1 ? 'needs' : 'need'} your Keep or Revert before you can resubmit.`
                      : `${plural(editCount, 'edit')} still open. Resubmitting keeps ${editCount === 1 ? 'it' : 'them'}.`
                    : 'All edits reviewed.'}
                  {commentCount > 0 && ` ${plural(commentCount, 'comment')} to address.`}
                </p>
              )}
              {/* ehr#571 (Chase 10-21, ask 7): each open comment as a bullet that goes to its section */}
              {bannerComments.length > 0 && (
                <ul className="pn-banner-list" aria-label="Open comments">
                  {bannerComments.map((c) => (
                    <li key={c.n}>
                      <b>{c.label}:</b> &ldquo;{c.text}&rdquo;{' '}
                      <button
                        type="button"
                        className="pn-link"
                        onClick={() => showSection(c.section)}
                      >
                        Go to {c.label}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {replies.map((r) => (
                <div key={r.id} className="pn-banner-reply">
                  <span className="who">
                    {userName(r.actorId)} · {timeOf(r.createdAt)}
                  </span>
                  <span>{r.message}</span>
                </div>
              ))}
              {replyOpen && (
                <div className="pn-banner-compose">
                  <textarea
                    value={replyText}
                    placeholder={`Reply to ${userName(lastChanges?.actorId ?? assignedCosignerId)}`}
                    onChange={(e) => setReplyText(e.target.value)}
                  />
                  <div className="pn-banner-actions">
                    <button type="button" className="btn" onClick={() => setReplyOpen(false)}>
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn primary"
                      disabled={busy || !replyText.trim()}
                      onClick={doReply}
                    >
                      Send reply
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          {status === 'pending' && !suggestMode && !reviewTabReadOnly && (
            <div className="pn-banner lock">
              <span>
                With {reviewerName} since {submittedAt ? timeOf(submittedAt) : '—'}. Locked until
                returned.
              </span>
            </div>
          )}
          {status === 'cosigned' && (
            <div className="pn-banner cosign">
              <span>
                Co-signed by {reviewerName} at {cosignedAt ? timeOf(cosignedAt) : '—'}. Locked.
              </span>
            </div>
          )}
          {status === 'signed' && (
            <div className="pn-banner cosign">
              <span>
                Signed off by {userName(authorId)} at {submittedAt ? timeOf(submittedAt) : '—'}.
                Locked.
              </span>
            </div>
          )}

          {/* ehr#571 (reopen 10-24, point 5, V141): no intro above the note; General comments
              sit in the margin beside its first section */}
          {
            <fieldset
              className={`pn-lock pn-secs${marginOn ? ' tc-inplace' : ''}`}
              disabled={locked && !suggestMode}
            >
              {/* ehr#437 part 3: the note's parts in its layout's order (lib/noteLayouts). */}
              {suggestMode && !shownBlocks.length && (
                <aside className="tc-margin" aria-label="General comments">
                  {generalComments()}
                </aside>
              )}
              {shownBlocks.map((b, i) => {
                const general = i === 0 && suggestMode && !generalHost;
                // ehr#571 (reopen 10-27, ask 2): a text box keeps its own margin; any other part
                // gets one for its field-change cards
                const cards = b.kind === 'text' || b.kind === 'box' ? [] : fieldCardsOf(b.id);
                return general || cards.length ? (
                  <Fragment key={`m-${b.id}`}>
                    {renderBlock(b)}
                    <aside
                      className="tc-margin"
                      data-tc-margin={b.id}
                      aria-label={general ? 'General comments' : `Changes to ${b.label}`}
                    >
                      {general && generalComments()}
                      {cards}
                    </aside>
                  </Fragment>
                ) : (
                  renderBlock(b)
                );
              })}
              {!v81 && (
                <div className="pn-sec">
                  <CredifyNoteFields
                    value={credify}
                    onChange={setCredify}
                    renderText={(sec) => noteField(sec, 'pn-cred-section')}
                    lockChoices={suggestMode}
                  />
                </div>
              )}

              <div className="pn-card" data-pn-target="reviewer">
                <h3>Signatures</h3>
                <div className="pn-sig-row">
                  <div>
                    {clinicianImg && (
                      <img
                        className="pn-sig-img"
                        src={clinicianImg}
                        alt={`Signature of ${userName(authorId)}`}
                      />
                    )}
                    <div className="pn-sig-role">Clinician</div>
                    <div className="pn-sig-name">
                      {userName(clinicianSigned ? authorId : currentUserId)}
                    </div>
                    {!clinicianSigned && (
                      <div className="pn-sig-attest">
                        By signing, I attest this note accurately reflects the services provided
                        during this session.
                      </div>
                    )}
                  </div>
                  {clinicianSigned ? (
                    <div className="pn-sig-state signed">
                      <span className="pn-sig-check">✓</span> Signed{' '}
                      {submittedAt ? fmtWhen(submittedAt) : ''}
                    </div>
                  ) : (
                    <div className="pn-sig-state">Not yet signed</div>
                  )}
                </div>
                {/* ehr#727 (PEER_SUPPORT_NOTE_10-30-V8, 13a): under the signer */}
                {kind === PEER_SUPPORT_KIND && (
                  <PeerCredentialRow
                    credential={peerCredential}
                    choices={peer.lists.credentials}
                    editable={peerCredWriting && !locked}
                    onSave={peer.saveCredential}
                    onNotice={setNotice}
                  />
                )}
                {showCosign && (
                  <div className="pn-sig-row">
                    <div>
                      {supervisorImg && (
                        <img
                          className="pn-sig-img"
                          src={supervisorImg}
                          alt={`Signature of ${cosignerName}`}
                        />
                      )}
                      <div className="pn-sig-role">CO SIGNED BY Supervisor</div>
                      <div className="pn-sig-name">
                        {/* ehr#189: before sign-off, name who it will go to: the supervisor
                        picked in Provider Profile, else the chosen reviewer, else the
                        author's org-chart supervisor (the server's order). */}
                        {cosignerName}
                      </div>
                      {!supervisorSigned &&
                        (clinicianSigned ? (
                          <div className="pn-sig-attest">
                            I have reviewed and approve this note as an accurate clinical record.
                          </div>
                        ) : isAuthor && !noteId && !supervision?.supervisorId ? (
                          <div
                            className="pn-sig-attest"
                            style={{ display: 'flex', gap: 8, alignItems: 'center' }}
                          >
                            <span>Send to:</span>
                            <span className="pn-inline-field" style={{ minWidth: 200 }}>
                              <Dd
                                value={reviewerId}
                                onChange={setReviewerId}
                                placeholder="Reviewer (default: your supervisor)"
                                options={reviewerOptions}
                              />
                            </span>
                          </div>
                        ) : (
                          <div className="pn-sig-attest">
                            Waiting on the clinician signature first.
                          </div>
                        ))}
                    </div>
                    {supervisorSigned ? (
                      <div className="pn-sig-state signed">
                        <span className="pn-sig-check">✓</span> Signed{' '}
                        {cosignedAt ? fmtWhen(cosignedAt) : ''}
                      </div>
                    ) : (
                      <div className="pn-sig-state">Not yet signed</div>
                    )}
                  </div>
                )}
              </div>
            </fieldset>
          }
          {/* ehr#544: the note's addenda, under the note (outside its locked fieldset) */}
          {!reviewing && <NoteAddendaCard addenda={addenda} when={fmtWhen} />}
          {addendumOpen && noteId && (
            <AddAddendumModal
              contactId={contactId}
              noteId={noteId}
              onClose={() => setAddendumOpen(false)}
              onAdded={(all) => {
                setAddenda(all);
                setAddendumOpen(false);
              }}
            />
          )}

          {err && (
            <p className="pn-inline-err" role="alert">
              {err}
            </p>
          )}
        </div>

        {railShown && (
          <aside className="pn-page-rail" aria-label="Generate Notes">
            {ns.ai !== 'off' && !cardsOff.includes('gen') && (
              <DraftWithAiCard
                key={npKey}
                value={roughNotes}
                onChange={setRoughNotes}
                dictate={dictate}
                locked={locked}
                onDraft={runDraft}
                drafted={npDrafted.some((k) => (pbody[k] ?? '').trim())}
                onDelete={canClear ? () => setClearAsk(true) : undefined}
                placeholder={
                  kind === 'Discharge summary'
                    ? DISCHARGE_ROUGH_PLACEHOLDER
                    : kind === CASE_MANAGEMENT
                      ? CASE_ROUGH_PLACEHOLDER // ehr#725
                      : isCollateral(kind)
                        ? COLLATERAL_ROUGH_PLACEHOLDER
                        : adminOnly // ehr#724 (V7 V2)
                          ? ADMIN_ROUGH_PLACEHOLDER
                          : undefined
                }
              />
            )}
            {histOpen ? (
              <HistoryPanel
                notes={pastSigned}
                api={copyApi}
                fmtDate={fmtDay}
                author={userName}
                onClose={() => setHistOpen(false)}
                // ehr#724 (V7 V4) / ehr#725: no Measures tab on an Admin / phone or case management note
                measures={adminOnly || caseRail.includes('measures') ? [] : measures}
                initialTab={histAt.tab}
                measureKey={histAt.key}
                measureBox={measureBox?.label ?? ''}
                onAddLine={addLine}
              />
            ) : (
              <>
                {/* ehr#571 (V141 V111): one control folds or opens every card below at once */}
                {/* ehr#741 (point 9): Hide side panel, beside Collapse all */}
                {(railCards.length > 0 || marginOn) && (
                  <div className="pn-rail-all">
                    {marginOn && (
                      <button type="button" className="pn-link" onClick={() => hideRail(true)}>
                        Hide side panel
                      </button>
                    )}
                    {railCards.length > 0 && (
                      <button
                        type="button"
                        className="pn-link"
                        onClick={() =>
                          setRailOpen(Object.fromEntries(railCards.map((c) => [c, !anyRailOpen])))
                        }
                      >
                        {anyRailOpen ? 'Collapse all' : 'Expand all'}
                      </button>
                    )}
                  </div>
                )}
                {!locked && v81 && !quickHidden && railCardOn(kind, 'quick') && (
                  <QuickAddCard
                    view={phrases}
                    onChange={setPhrases}
                    contactId={contactId}
                    couples={kind === 'Couples'}
                    ownRatingsOnly={adminOnly}
                    onNote={quick.map((q) => q.id)}
                    onToggle={(it) =>
                      quick.some((q) => q.id === it.id) ? removeQuick(it) : addQuick(it)
                    }
                    onSettings={() => setNsOpen('qa')}
                    fold={railFold('quick')}
                  />
                )}
                {!cardsOff.includes('measures') && railCardOn(kind, 'measures') && (
                  <MeasuresCard
                    measures={measures}
                    boxLabel={measureBox?.label ?? ''}
                    locked={locked}
                    onAdd={addLine}
                    onOpen={(key) => {
                      setHistAt({ tab: 'measures', key });
                      setHistOpen(true);
                    }}
                    fold={railFold('measures')}
                  />
                )}
                {(lastNote || otherKind) && !cardsOff.includes('last') && (
                  <LastSessionCard
                    note={lastNote}
                    kind={kind}
                    api={copyApi}
                    fmtDate={fmtDay}
                    author={userName}
                    onPast={() => {
                      setHistAt({ tab: 'notes' });
                      setHistOpen(true);
                    }}
                    fold={railFold('last')}
                  />
                )}
                {!locked && v81 && !cardsOff.includes('changes') && railCardOn(kind, 'changes') && (
                  <MyChangesCard
                    changes={myChanges}
                    onUndo={undoChange}
                    fold={railFold('changes')}
                  />
                )}
              </>
            )}
          </aside>
        )}
      </div>

      <div className="pn-page-foot">
        <div className="pn-page-in pn-page-foot-in">
          {/* ehr#277: where the review stands. */}
          {/* ehr#663: with parts blank, the list of them says what to do instead */}
          {/* ehr#682 (V141 V83.4): just sent back, the reviewer may take it back for a few minutes */}
          {takeBackOn && <span className="tc-pending">{takeBackLine(userName(authorId))}</span>}
          {(suggestMode || tracked) &&
            !takeBackOn &&
            !(suggestMode && !draftCount && !fieldCount && cosignLeft.length > 0) && (
              <span className="tc-pending">
                {suggestMode
                  ? draftCount || fieldCount
                    ? `${[
                        draftCount ? plural(draftCount, 'suggestion') : '',
                        fieldCount ? plural(fieldCount, 'field change') : '',
                      ]
                        .filter(Boolean)
                        .join(' and ')} for ${userName(authorId)}`
                    : 'Pending review. Edit any box or comment on a section, then co-sign or send it back.'
                  : isAuthor
                    ? editCount
                      ? keepGate
                        ? `Keep or Revert ${plural(editCount, 'edit')} from ${supName} before you resubmit`
                        : `${plural(editCount, 'edit')} from ${supName} still open; resubmitting keeps them`
                      : `All edits reviewed${commentCount ? ` · ${plural(commentCount, 'comment')} from ${supName}` : ''}`
                    : `Waiting on ${userName(authorId)} to keep or revert the edits`}
              </span>
            )}
          {/* ehr#437 (V81): "7 left to sign: Subjective, Objective, Client response +4 more" */}
          {left.length > 0 && (ns.mode.whatsLeft || suggestMode) && (
            <span className="pn-left">
              {left.length} {suggestMode ? 'still blank' : 'left to sign'}:{' '}
              {left.slice(0, 3).map((c, i) => (
                <Fragment key={c.label}>
                  {i > 0 && ', '}
                  <button type="button" className="pn-link" onClick={() => goTo(c.target)}>
                    {c.label}
                  </button>
                </Fragment>
              ))}
              {/* ehr#571 (Chase 10-21, ask 7; V141 "Finish these before you sign"): the rest,
                  listed with a link to each */}
              {left.length > 3 && (
                <>
                  {' '}
                  <button type="button" className="pn-link" onClick={() => setLeftListOpen(true)}>
                    +{left.length - 3} more
                  </button>
                </>
              )}
              {/* ehr#571 (V141): every generated box accepted as it is, in one click */}
              {aiLeft.length > 0 && (
                <>
                  {' · '}
                  <button type="button" className="pn-link" onClick={() => setNpDrafted([])}>
                    Accept all AI
                  </button>
                </>
              )}
            </span>
          )}
          <span className="pn-sp" />
          {takeBackOn && takeBackUntil && (
            <TakeBackButton
              until={takeBackUntil}
              busy={busy}
              onTakeBack={doTakeBack}
              onExpired={() => setTakeBackUntil(null)}
            />
          )}
          {/* ehr#741 (point 9): the side panel back */}
          {!reviewing && loaded && railHidden && (
            <button type="button" className="pn-link" onClick={() => hideRail(false)}>
              Show side panel
            </button>
          )}
          {/* ehr#437 reopen (V81): History opens in the rail */}
          {!reviewing && loaded && pastSigned.length > 0 && (
            <button
              type="button"
              className="pn-link pn-foot-hist"
              aria-pressed={histOpen}
              onClick={() => {
                setHistAt({ tab: 'notes' });
                setHistOpen((o) => !o);
              }}
            >
              History
            </button>
          )}
          {/* ehr#270: what the preview window offered, now here. */}
          {noteId && (
            <button className="btn" onClick={printNote} disabled={!loaded}>
              Download PDF
            </button>
          )}
          {noteId && onCopy && !isGroupNote && (
            <button
              className="btn"
              onClick={() => requestLeave(() => onCopy(noteId, date))}
              disabled={!loaded || busy}
            >
              Copy to new note
            </button>
          )}
          {/* ehr#544 (V81 115 A): a dated, signed addendum; the note itself stays as signed */}
          {canAddendum && !reviewing && (
            <button className="btn" onClick={() => setAddendumOpen(true)} disabled={!loaded}>
              Add addendum
            </button>
          )}
          {suggestMode ? (
            (() => {
              // ehr#571 (reopen 10-24, V141): Co-sign and finalize / Request changes, right
              // away; the green one is Request changes once there is something to send back.
              // ehr#571 (reopen 10-27, V141 V94): a field change alone can be sent back too
              const sendBack =
                reviewPrimary({ draftCount: draftCount + fieldCount, message: reviewReason }) ===
                'changes';
              // ehr#663 (Chase 10-30, point 1): with parts blank, either button opens the list
              // of them ("Blank on this note"), instead of sitting greyed out
              const blank = cosignLeft.length > 0;
              const cosignBtn = (
                <button
                  key="cosign"
                  className={`btn${sendBack ? '' : ' primary'}${blank ? ' pn-btn-muted' : ''}`}
                  onClick={() => {
                    const act = cosignAction({ blank: cosignLeft.length, unsentComments });
                    if (act === 'list') setLeftListOpen(true);
                    else if (act === 'ask') setCosignAsk(true);
                    else doCosign();
                  }}
                  disabled={busy || !loaded}
                  title={
                    blank ? `Still blank: ${cosignLeft.map((c) => c.label).join(', ')}` : undefined
                  }
                >
                  Co-sign &amp; Finalize
                </button>
              );
              const changesBtn = (
                <button
                  key="changes"
                  className={`btn${sendBack ? ' primary' : ''}`}
                  onClick={() => {
                    const act = changesAction({ blank: cosignLeft.length, sendBack });
                    if (act === 'send') doRequestChanges();
                    else if (act === 'list') setLeftListOpen(true);
                  }}
                  disabled={
                    busy ||
                    !loaded ||
                    changesAction({ blank: cosignLeft.length, sendBack }) === null
                  }
                  title={sendBack ? undefined : 'Edit the note or comment on a section first'}
                >
                  Request changes
                </button>
              );
              // ehr#741 (Chase 10-30, point 4): Save Draft, next to Co-sign & Finalize
              const saveBtn = (
                <SaveButton
                  key="save"
                  className="btn"
                  label="Save Draft"
                  busy={reviewSaving}
                  disabled={busy || !loaded || !draftNow || reviewSaving}
                  notice={draftNote}
                  onCloseNotice={() => setDraftNote(null)}
                  onClick={saveReviewDraftNow}
                />
              );
              return sendBack ? [saveBtn, cosignBtn, changesBtn] : [saveBtn, changesBtn, cosignBtn];
            })()
          ) : (
            <>
              {/* ehr#571 (reopen 10-23): every edit at once; Resubmit waits for each to be reviewed */}
              {tracked && isAuthor && editCount > 0 && !tabReadOnly && (
                <>
                  <button
                    className="btn"
                    onClick={() => resolveSuggestions(false)}
                    disabled={busy || !loaded}
                  >
                    Revert all
                  </button>
                  <button
                    className="btn"
                    onClick={() => resolveSuggestions(true)}
                    disabled={busy || !loaded}
                  >
                    Keep all
                  </button>
                </>
              )}
              <SaveButton
                className="btn"
                label="Save Draft"
                busy={draftSaving}
                disabled={busy || !loaded || locked}
                notice={draftNote}
                onCloseNotice={() => setDraftNote(null)}
                onClick={() => saveDraft()}
              />
              {/* ehr#467 (Chase 10-16): with nothing to do here (Waiting on Clinician Signature,
                  Submitted for Review, Signed, Co-signed) the button stays muted and takes you to
                  Signatures */}
              <button
                className={`btn primary${primaryDisabled ? ' pn-btn-muted' : ''}`}
                onClick={primaryDisabled ? () => goTo('reviewer') : primaryFn}
                disabled={busy || !loaded || tabReadOnly}
                title={primaryDisabled ? 'Go to Signatures' : undefined}
              >
                {primaryBadge && <span className="pn-badge">1</span>}
                {primaryLabel}
              </button>
            </>
          )}
        </div>
      </div>

      {quickAsk && (
        <Modal
          title={`Remove "${quickLabel(quickAsk)}"?`}
          onClose={() => setQuickAsk(null)}
          className="pn-rm-modal"
          actions={
            <>
              <button type="button" className="btn" onClick={() => setQuickAsk(null)}>
                Cancel
              </button>
              {/* ehr#571 (V141 V131): three scopes; All clients going forward is red, far right */}
              <button
                type="button"
                className="btn"
                onClick={() => {
                  dropQuick(quickAsk);
                  setQuickAsk(null);
                }}
              >
                This note only
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  dropForward(quickAsk, 'client');
                  setQuickAsk(null);
                }}
              >
                This client going forward
              </button>
              <button
                type="button"
                className="btn danger"
                onClick={() => {
                  dropForward(quickAsk, 'all');
                  setQuickAsk(null);
                }}
              >
                All clients going forward
              </button>
            </>
          }
        >
          <p className="pn-modal-p">
            {quickFilled(quickAsk.id) ? 'What is filled in on this note is deleted. ' : ''}Saved and
            signed notes are not changed. This client going forward takes it out of{' '}
            {contactName.split(' ')[0]}&rsquo;s Quick add settings. All clients going forward also
            takes it out of Quick add settings, so new notes do not get it. Undo it on this note in
            My changes.
          </p>
        </Modal>
      )}
      {dxOpen && (
        <DiagnosisChangeModal
          planDx={tp.summary?.diagnoses ?? []}
          value={diagnoses}
          onSave={(next) => {
            setDiagnoses(next);
            setDxOpen(false);
          }}
          onClose={() => setDxOpen(false)}
        />
      )}
      {leftListOpen && left.length > 0 && (
        <Modal
          title={
            suggestMode
              ? 'Blank on this note: Request changes to send it back'
              : `Finish these before you ${supervisionOn ? 'submit' : 'sign'}`
          }
          onClose={() => setLeftListOpen(false)}
          className="pn-left-modal"
          actions={
            <>
              <button type="button" className="btn" onClick={() => setLeftListOpen(false)}>
                Close
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={() => {
                  setLeftListOpen(false);
                  goTo(left[0]!.target);
                }}
              >
                {suggestMode ? 'Go to the first one' : 'Fix the next one'}
              </button>
            </>
          }
        >
          <ul className="pn-left-list">
            {left.map((c) => (
              <li key={c.label}>
                <button
                  type="button"
                  className="pn-link"
                  onClick={() => {
                    setLeftListOpen(false);
                    goTo(c.target);
                  }}
                >
                  {c.label}
                </button>
              </li>
            ))}
          </ul>
        </Modal>
      )}
      {phraseAsk && (
        <Modal
          title={`Add a phrase to ${phraseAsk.label}`}
          onClose={() => setPhraseAsk(null)}
          className="pn-phrase-modal"
          actions={
            <>
              <button type="button" className="btn" onClick={() => setPhraseAsk(null)}>
                Cancel
              </button>
              <button type="button" className="btn primary" onClick={savePhrase}>
                Add phrase
              </button>
            </>
          }
        >
          <textarea
            className="pn-phrase-input"
            value={phraseText}
            maxLength={200}
            autoFocus
            placeholder="Client practiced __ between sessions."
            aria-label="Phrase"
            onChange={(e) => {
              setPhraseText(e.target.value);
              setPhraseErr('');
            }}
          />
          <div className="pn-phrase-scope">
            <span>Add to</span>
            <div className="pn-seg" role="group" aria-label="Add to">
              {(
                [
                  ['client', `${contactName.split(' ')[0] ?? contactName} only`],
                  ['all', 'All clients'],
                ] as const
              ).map(([k, t]) => (
                <button
                  key={k}
                  type="button"
                  aria-pressed={phraseScope === k}
                  onClick={() => setPhraseScope(k)}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          {phraseErr && (
            <p className="pn-inline-err" role="alert">
              {phraseErr}
            </p>
          )}
        </Modal>
      )}
      {clearAsk && (
        <Modal
          title={`Clear this note for "${contactName}"?`}
          onClose={() => setClearAsk(false)}
          actions={
            <>
              <button type="button" className="btn" onClick={() => setClearAsk(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn danger"
                onClick={() => {
                  clearNote();
                  setClearAsk(false);
                }}
              >
                Clear note
              </button>
            </>
          }
        >
          <p className="pn-modal-p">
            Everything typed or picked on this note is removed so you start with a blank note. The
            session&rsquo;s date and time, its diagnosis codes, Last session, measures and the
            treatment plan stay. Save Draft keeps the blank note; leaving without saving keeps the
            note as it was.
          </p>
        </Modal>
      )}
      {nsOpen && phrases && (
        <NoteSettingsWindow
          view={phrases}
          current={ns}
          onClose={() => setNsOpen(null)}
          onSaved={(v) => {
            setPhrases(v);
            setRailOpen({});
            setNotice('Note settings saved.');
          }}
          onShortcuts={() => {
            setNsOpen(null);
            setKeysOpen(true);
          }}
          onReport={(k) => {
            setNsOpen(null);
            setReportOpen(k);
          }}
          // ehr#724 (V7 V4): no Interventions tab on an Admin / phone note; ehr#708 (V5 12a): nor on
          // a discharge summary, which has no Interventions box
          {...(editsPracticeLists && !adminOnly && !isDischarge
            ? {
                interventions: practiceInterventions,
                onInterventions: setPracticeInterventions,
                hidden: interHide,
                onHidden: setInterHide,
                contactId,
                contactName,
              }
            : {})}
          startTab={nsOpen}
          aiBySupervisor={aiBySupervisor}
          // ehr#729: a kind's own tabs (Skills / rehab: the Note tab only) and default code row
          kindSettings={kindSettingsOf(kind)}
          {...(editsPracticeLists && defaultCodeKindOf(kind)
            ? {
                defaultCode: {
                  kind: defaultCodeKindOf(kind)!,
                  codes: serviceCodes,
                  onSaved: setServiceCodes,
                },
              }
            : {})}
          {...(canEditPracticeNoteSettings(nsUser?.role)
            ? {
                practice: {
                  value: practiceNotes,
                  onSaved: setPracticeNotes,
                  discharge: isDischarge,
                },
              }
            : {})}
          // ehr#724: nothing to address on an Admin / phone note, so no goal rule
          goalRules={!adminOnly}
          // ehr#725 (CASE_MANAGEMENT_NOTE_10-30-V9 14 A, 20 A): Show treatment plan goals, and the
          // Default billing code for admins and managers
          caseGoals={kind === CASE_MANAGEMENT}
          {...(editsPracticeLists && kind === CASE_MANAGEMENT
            ? { defaultCode: { kind, codes: serviceCodes, onSaved: setServiceCodes } }
            : {})}
          quick={{
            contactId,
            contactName,
            couples: kind === 'Couples',
            offCards: isDischarge ? DISCHARGE_HIDDEN_CARDS : undefined, // ehr#708
            // ehr#724 (V7 V5): Ratings starts empty; no Measures card, so no Show Measures
            ownRatingsOnly: adminOnly,
            noMeasures: adminOnly,
            railOff: caseRail,
            dotOff: quickDotTabOff(kind),
            onApplied: (v, added) => {
              setPhrases(v);
              if (!locked && railCardOn(kind, 'quick')) added.forEach((it) => addQuick(it));
            },
            // ehr#728: a Collateral contact note's Side cards tab
            ...(sideCardsOf(kind) ? { sideCards: sideCardsOf(kind) } : {}),
          }}
          {...(reviewHistoryOn
            ? {
                review: {
                  view: reviewView,
                  onView: setReviewView,
                  onHistory: () => {
                    setNsOpen(null);
                    setHistoryOpen(true);
                  },
                },
              }
            : {})}
        />
      )}
      {/* ehr#682 (V141 V86): Comment history, from Note settings */}
      {historyOpen && (
        <CommentHistory
          rows={historyRows({
            log: [...logKept, ...logNew],
            events: thread,
            name: (id) => userName(id),
            me: currentUserId ?? '',
            noted: logNoted.current,
            now: new Date().toISOString(),
          })}
          onClose={() => setHistoryOpen(false)}
        />
      )}
      {reportOpen && <NoteReportWindow kind={reportOpen} onClose={() => setReportOpen(null)} />}
      {keysOpen && <ShortcutsWindow onClose={() => setKeysOpen(false)} />}
      {selPick && suggestMode && (
        <SelectionPill
          x={selPick.x}
          y={selPick.y}
          onClick={() => openComposer(selPick.section, selPick.quote, selPick.pos)}
        />
      )}
      {dischargeAsk && (
        <DischargeConfirm
          contactId={contactId}
          noteId={noteId}
          okLabel={dischargeAsk.label}
          busy={busy}
          onClose={() => setDischargeAsk(null)}
          onOk={() => {
            const run = dischargeAsk.run;
            setDischargeAsk(null);
            run();
          }}
        />
      )}
      {cosignAsk && (
        <Modal
          title="Co-sign without sending your comments?"
          onClose={() => setCosignAsk(false)}
          className="pn-leave-ask"
          actions={
            <>
              <button type="button" className="btn" onClick={() => setCosignAsk(false)}>
                Go back
              </button>
              <button
                type="button"
                className="btn"
                disabled={busy || cosignLeft.length > 0}
                onClick={doCosign}
              >
                Co-sign anyway
              </button>
              <button
                type="button"
                className="btn primary"
                disabled={busy}
                onClick={() => {
                  setCosignAsk(false);
                  doRequestChanges();
                }}
              >
                Request changes
              </button>
            </>
          }
        >
          <p className="pn-modal-p">
            You have {plural(unsentComments, 'comment')} {userName(authorId)} hasn&rsquo;t seen.
            Co-signing finalizes the note and drops them. Your edits to the note&rsquo;s boxes
            {fieldCount > 0 ? ' and fields (times, picks and checkboxes)' : ''} are kept with the
            co-signature.
          </p>
        </Modal>
      )}
      {leaveAsk && (
        <Modal
          title="Save your changes?"
          onClose={() => setLeaveAsk(null)}
          className="pn-leave-ask"
          actions={
            <>
              <button type="button" className="btn" onClick={() => setLeaveAsk(null)}>
                Go back
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  const { go } = leaveAsk;
                  setLeaveAsk(null);
                  go();
                }}
              >
                Don&rsquo;t save
              </button>
              <button
                type="button"
                className={canSignHere ? 'btn' : 'btn primary'}
                disabled={busy}
                onClick={() => {
                  const { saved } = leaveAsk;
                  setLeaveAsk(null);
                  saveDraft(saved);
                }}
              >
                Save Draft
              </button>
              {canSignHere && (
                <button
                  type="button"
                  className="btn primary"
                  disabled={busy}
                  onClick={() => {
                    setLeaveAsk(null);
                    primaryFn();
                  }}
                >
                  {primaryLabel}
                </button>
              )}
            </>
          }
        >
          <p className="pn-modal-p">
            This note has changes you haven&rsquo;t saved.{' '}
            {canSignHere
              ? 'Sign it off, save it as a draft, or leave without saving them.'
              : 'Save it as a draft, or leave without saving them.'}
          </p>
        </Modal>
      )}
      {lateAsk !== null && (
        <Modal
          title="Late entry"
          onClose={() => setLateAsk(null)}
          className="pn-late-modal"
          actions={
            <>
              <button type="button" className="btn" onClick={() => setLateAsk(null)}>
                Go back
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={() => {
                  const reason = lateReason.trim();
                  if (!reason) return setLateErr('Add the reason for the late entry.');
                  const entry = { hours: lateAsk, reason };
                  setLateEntry(entry);
                  setLateAsk(null);
                  doSubmit({ lateEntry: entry }, true);
                }}
              >
                {primaryLabel}
              </button>
            </>
          }
        >
          <div className="pn-late">
            <b>{lateText(lateAsk)}</b>
            <p className="pn-modal-p">
              Notes signed more than {lateLimit} hours after the session need the reason for the
              late entry. It is kept with the note and printed on it.
            </p>
            <label className="pn-late-f">
              <span>Reason for late entry</span>
              <textarea
                value={lateReason}
                maxLength={2000}
                autoFocus
                aria-invalid={!!lateErr || undefined}
                onChange={(e) => {
                  setLateReason(e.target.value);
                  setLateErr('');
                }}
              />
            </label>
            {lateErr && (
              <p className="pn-inline-err" role="alert">
                {lateErr}
              </p>
            )}
          </div>
        </Modal>
      )}
      {signWarn && (
        <Modal
          title="Before you sign"
          onClose={() => setSignWarn(null)}
          className="pn-warn-modal"
          actions={
            <>
              <button type="button" className="btn" onClick={() => setSignWarn(null)}>
                Go back
              </button>
              <button
                type="button"
                className="btn primary"
                onClick={() => {
                  setSignWarn(null);
                  doSubmit({}, true);
                }}
              >
                Sign anyway
              </button>
            </>
          }
        >
          <ul className="pn-warn-list">
            {signWarn.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </Modal>
      )}

      {errOpen && err && (
        <Modal
          title="This note can’t be saved yet"
          onClose={() => setErrOpen(false)}
          className="pn-error-modal"
          actions={
            <button className="btn primary" onClick={closeErrAndGo}>
              OK
            </button>
          }
        >
          <p className="pn-error-text" role="alert">
            {err}
          </p>
        </Modal>
      )}
    </div>
  );
}

// ── ehr#437 (GROUP_NOTE_10-14-V26): one member's note in a group session ─────────────────────
export interface GroupMode {
  sessionId: string;
  /** The group's template (SOAP, DAP, …): one for every member's note. */
  format: string;
  /** The roster, the Group summary and the session tools, shown at the top of the note. */
  bar: ReactNode;
  /** The page asks the open note to save before it switches member. */
  bindSave: (fn: () => Promise<void>) => void;
  /** The other members' first names: a group note never names them (V73 101 A). */
  members: string[];
  /** ehr#571 round 2 (V22): the session has a Group summary; a present member's note needs it. */
  summaryWritten: boolean;
  /** V22 groupOwnItems: the Group summary and the other members' notes, for "One sentence only about ...". */
  shared: string[];
  /** V22 30 A: this member's note was signed; the page opens the next one that needs something. */
  onSigned?: () => void;
  /**
   * ehr#688 (V25 tab lock): this copy of the group session is read-only (it is open in another tab
   * of this browser); the member's note can't be changed here. The group page shows the banner.
   */
  readOnly?: boolean;
  /** ehr#688: the member's note was saved here, so a read-only copy in another tab reloads. */
  onPersist?: () => void;
}

// ── ehr#437 (FAMILY_COUPLES_NOTE_10-14-V15): the family / couples session page ──────────────
interface FamilyMode {
  sessionId: string;
  type: FamilyType;
  /** The identified client: the one note that bills. */
  billed: boolean;
  /** The attendee bar and the session summary, shown at the top of the note. */
  bar: ReactNode;
  /** The page asks the open note to save before it switches attendee. */
  bindSave: (fn: () => Promise<void>) => void;
  /** Everyone on the session but the client is Not present (70 A). */
  othersAway: boolean;
  /** 70 A: the client's note becomes a Progress Note. */
  onHandOff: () => void;
  /** 60 A: a Yes on a couples session; the session goes back to Family. */
  onPartnerYes: () => void;
  /** ehr#571 round 2 (V11): this attendee's note was signed; the page opens the next one. */
  onSigned?: () => void;
  /** ehr#571 round 2 (V11): the session has a Session summary; the note needs it to sign. */
  summaryWritten: boolean;
  /** ehr#682: the session is open in another tab; this copy is read-only. */
  tabReadOnly?: boolean;
  /** ehr#682: this note was saved; a read-only copy of the session in another tab reloads. */
  notifySaved?: () => void;
  /** ehr#689 F4: how many review comments the reviewer has on this note and not sent yet. */
  onReviewDraft?: (comments: number) => void;
}

/**
 * One family or couples session: the attendees (the client first, billed; family members from the
 * practice's contacts, each with a note on their own chart), Family or Couples, and the session
 * summary that is the same on every attendee's note. The note shown is one attendee's, in the
 * progress note page; another attendee's opens after this one's changes are saved.
 */
function FamilyNotePage({
  sessionId,
  initialContactId,
  history,
  historyFor,
  users,
  currentUserId,
  canReviewNotes,
  onClose,
  onHandOff,
}: {
  sessionId: string;
  initialContactId: string;
  history: ProgressNote[];
  /**
   * ehr#689: whose notes `history` is (the chart the page opened from, which can now be a family
   * member's, e.g. from To review); only that attendee's note gets it. Default: the session's client.
   */
  historyFor?: string;
  users: NoteUser[];
  currentUserId: string | null;
  canReviewNotes: boolean;
  onClose: () => void;
  /** 70 A: the client's note became a Progress Note; open it there (that note, its client). */
  onHandOff: (noteId: string, contactId: string) => void;
}) {
  const [view, setView] = useState<FamilySessionView | null>(null);
  // the open note remounts after the session writes to it (Safety between partners)
  const [rev, setRev] = useState(0);
  const [msg, setMsg] = useState('');
  // 60 A: asking each partner alone, one at a time, before the session goes to Couples
  const [asking, setAsking] = useState<{
    list: FamilyAttendeeView[];
    i: number;
    ans: Record<string, ('Yes' | 'No' | null)[]>;
    skip: Record<string, boolean>;
    err: string;
  } | null>(null);
  const [current, setCurrent] = useState(initialContactId);
  const [err, setErr] = useState('');
  const [summary, setSummary] = useState('');
  const [summarySaved, setSummarySaved] = useState('');
  // ehr#689 F7 (V15): the shared "What happened in the session" notes, as typed and as saved
  const [shared, setShared] = useState('');
  const [sharedSaved, setSharedSaved] = useState('');
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState('');
  const [found, setFound] = useState<{ id: string; firstName: string; lastName: string }[]>([]);
  // ehr#571 round 2 (FAMILY_COUPLES_NOTE_10-20-V11): Attendance for everyone, Sign ready notes
  const [attOpen, setAttOpen] = useState(false);
  const [signAll, setSignAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lateLimit, setLateLimit] = useState(72);
  const [supervised, setSupervised] = useState(false);
  // ehr#689 F4 (V15 "V9 sup"): Co-sign all ready notes (warn once on an open comment, confirm, results)
  const [cosignAsk, setCosignAsk] = useState(false);
  const [cosignWarned, setCosignWarned] = useState(false);
  const [cosignOut, setCosignOut] = useState<string[] | null>(null);
  const [draftComments, setDraftComments] = useState(0);
  const saveRef = useRef<() => Promise<void>>(async () => undefined);
  useEffect(() => {
    let live = true;
    api
      .get<{ lateEntryHours?: number } | null>('/practice/clinical')
      .then((c) => live && typeof c?.lateEntryHours === 'number' && setLateLimit(c.lateEntryHours))
      .catch(() => undefined);
    api
      .get<SupervisionView>('/progress-notes/supervision')
      .then((s) => live && setSupervised(Boolean(s?.required)))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);
  const load = () =>
    api
      .get<FamilySessionView>(`/family-notes/${sessionId}`)
      .then((v) => {
        setView(v);
        setSummary((s) => (s === summarySaved ? v.sessionSummary : s));
        setSummarySaved(v.sessionSummary);
        setShared((s) => (s === sharedSaved ? v.sharedNotes : s));
        setSharedSaved(v.sharedNotes);
        return v;
      })
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.message : 'Could not open the family session.'),
      );
  useEffect(() => {
    void load();
  }, [sessionId]);
  // ehr#682 (V131 "one open copy per note"): one copy of the session page can save in this
  // browser. The key is the session (family:<sessionId>), not one attendee's note: the page also
  // saves the shared Session summary, Family or Couples, the attendees, Attendance for everyone
  // and Sign ready notes, which write the other attendees' notes. A second tab is read-only.
  const summaryRef = useRef({ now: summary, saved: summarySaved, shared, sharedSaved });
  summaryRef.current = { now: summary, saved: summarySaved, shared, sharedSaved };
  const reopen = () => void load().then(() => setRev((r) => r + 1));
  const tabLock = useNoteTabLock(view ? familyLockKey(sessionId) : null, {
    // another copy took the session: save the open note and the summary first
    onLost: async () => {
      try {
        const note = saveRef.current();
        const { now, saved, shared: sh, sharedSaved: shSaved } = summaryRef.current;
        await note;
        // ehr#689 F7: the shared notes too
        if (now !== saved || sh !== shSaved) {
          const v = await api.patch<FamilySessionView>(`/family-notes/${sessionId}`, {
            ...(now !== saved ? { sessionSummary: now } : {}),
            ...(sh !== shSaved ? { sharedNotes: sh } : {}),
          });
          setSummarySaved(v.sessionSummary);
          setSharedSaved(v.sharedNotes);
        }
        return true;
      } catch {
        return false;
      }
    },
    onRegained: (reason) => {
      reopen();
      setErr('');
      setMsg(
        reason === 'other-closed'
          ? 'The other copy closed. This copy can be edited now.'
          : 'This copy can be edited now.',
      );
    },
    onRemoteSaved: reopen,
  });
  const tabRO = tabLock.readOnly;
  useEffect(() => {
    if (!adding || q.trim().length < 2) {
      setFound([]);
      return;
    }
    let live = true;
    const t = window.setTimeout(() => {
      api
        .get<{ items: { id: string; firstName: string; lastName: string }[] }>(
          `/contacts?search=${encodeURIComponent(q.trim())}`,
        )
        .then((r) => live && setFound((r.items ?? []).slice(0, 8)))
        .catch(() => live && setFound([]));
    }, 250);
    return () => {
      live = false;
      window.clearTimeout(t);
    };
  }, [q, adding]);
  if (!view)
    return (
      <div className="pn-page" role="dialog" aria-modal="true" aria-label={FAMILY_NOTE_TITLE}>
        <div className="pn-page-in pn-page-grid">
          <p className="muted">{err || 'Opening the family session…'}</p>
          {err && (
            <button type="button" className="btn" onClick={onClose}>
              Back to chart
            </button>
          )}
        </div>
      </div>
    );
  const me = view.attendees.find((a) => a.contactId === current) ?? view.attendees[0]!;
  const lead = view.leadId === currentUserId || canReviewNotes;
  const switchTo = (cid: string) => {
    if (cid === current) return;
    setErr('');
    saveRef
      .current()
      .then(() => load())
      .then(() => setCurrent(cid))
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.message : 'Could not save this note first.'),
      );
  };
  const patch = (p: { type?: FamilyType; sessionSummary?: string; sharedNotes?: string }) =>
    api
      .patch<FamilySessionView>(`/family-notes/${sessionId}`, p)
      .then((v) => {
        setView(v);
        setSummarySaved(v.sessionSummary);
        setSharedSaved(v.sharedNotes);
        tabLock.notifySaved(); // ehr#682
      })
      .catch((e: unknown) => setErr(e instanceof ApiError ? e.message : 'Could not save.'));
  const names = (l: FamilyAttendeeView[]) => l.map((a) => a.name.split(' ')[0]).join(' and ');
  // ehr#571 round 2 (V11): each attendee's line, from their saved note and the note's own checks
  const summaryWritten = summary.trim() !== '';
  const isOpen = (st?: string) => st === 'draft' || st === 'changes';
  const away = othersAway(view.attendees);
  const sessionDate = String(view.startsAt).slice(0, 10);
  const lineOf = (a: FamilyAttendeeView): RosterLine => {
    const b = a.note?.body ?? {};
    const absent = a.attendance === 'Not present';
    const left = a.note
      ? attendeeLeft(b, {
          format: a.note.format,
          kind: view.type,
          quick: quickList(b.quick),
          absent,
          family: {
            type: view.type,
            billed: a.billed,
            othersAway: away,
            summaryWritten,
            firstName: a.name.split(' ')[0] ?? a.name,
          },
        })
      : [];
    return rosterLine({ status: a.note?.status ?? null, left, absent, absentLabel: 'Not present' });
  };
  const lines = new Map(view.attendees.map((a) => [a.contactId, lineOf(a)]));
  const onRoster = view.attendees.filter((a) => a.onRoster);
  const attended = onRoster.filter((a) => a.attendance !== 'Not present').length;
  const ready = onRoster.filter(
    (a) => a.note && isOpen(a.note.status) && lines.get(a.contactId)!.group === 'ready',
  );
  const lateOf = (a: FamilyAttendeeView) =>
    lateHoursOf(a.note?.body ?? {}, sessionDate, lateLimit, new Date());
  const lateReady = ready.map(lateOf).filter((h): h is number => h !== null);
  const attRows: AttendanceRow[] = onRoster.map((a) => ({
    contactId: a.contactId,
    name: a.name,
    value: a.attendance ?? 'Attended',
    locked: !!a.note && !isOpen(a.note.status),
  }));
  const allLocked = attRows.every((r) => r.locked);
  // ehr#689 (V15, V11 fix 4): + Add attendee is the lead's, and goes once every note is signed
  const canAdd = addAttendeeShown(lead, view.attendees);
  // V11 session line: the session, its length (times from the client's note), billing, facilitator
  const client = view.attendees.find((a) => a.billed);
  const cs = (client?.note?.body.session ?? {}) as { time?: string; end?: string };
  const csEnd = sessionEndOf({ ...cs, durationMin: view.durationMin }); // ehr#666
  const billLine = client
    ? client.attendance === 'Not present'
      ? `90846 · Billed on ${client.name}'s note, not present`
      : `90847 · Billed on ${client.name}'s note`
    : '';
  const leadUser = users.find((u) => u.id === view.leadId);
  const facilitator = leadUser
    ? [leadUser.firstName, leadUser.lastName].filter(Boolean).join(' ')
    : '';
  const sessionLabel = view.type === 'Couples' ? 'Couples Counseling' : 'Family Therapy';
  const saveAttendance = (changed: Record<string, string>) => {
    const ids = Object.keys(changed);
    if (!ids.length) return setAttOpen(false);
    setBusy(true);
    setErr('');
    saveRef
      .current()
      .then(async () => {
        const fresh = await api.get<FamilySessionView>(`/family-notes/${sessionId}`);
        for (const cid of ids) {
          const a = fresh.attendees.find((x) => x.contactId === cid);
          if (!a || (a.note && !isOpen(a.note.status))) continue;
          const body = a.note
            ? { ...a.note.body, attendance: { status: changed[cid] } }
            : {
                attendance: { status: changed[cid] },
                // the session's start, as on the client's note (V11: a new attendee note starts there)
                session: {
                  ...(cs.time ? { time: cs.time } : {}),
                  type: sessionLabel,
                  durationMin: view.durationMin,
                  place: 'In person',
                },
              };
          await api.put(`/family-notes/${sessionId}/attendees/${cid}/note`, { body });
        }
        setAttOpen(false);
        await load();
        setRev((r) => r + 1);
        tabLock.notifySaved(); // ehr#682
        setMsg(`Attendance saved for ${ids.length} ${ids.length === 1 ? 'person' : 'people'}.`);
      })
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.message : 'Could not save the attendance.'),
      )
      .finally(() => setBusy(false));
  };
  // V11 (V22 openSignAll): the ready notes only, each signed as usual; a late one keeps the reason
  const signReady = (reason: string) => {
    setBusy(true);
    setErr('');
    saveRef
      .current()
      .then(async () => {
        const fresh = await api.get<FamilySessionView>(`/family-notes/${sessionId}`);
        const ids = new Set(ready.map((a) => a.contactId));
        for (const a of fresh.attendees) {
          if (!ids.has(a.contactId) || !a.note) continue;
          const hours = lateOf(a);
          if (hours === null) continue;
          await api.put(`/family-notes/${sessionId}/attendees/${a.contactId}/note`, {
            body: { ...a.note.body, lateEntry: { hours, reason } },
          });
        }
        const r = await api.post<GroupSubmitResult>(`/family-notes/${sessionId}/submit`, {
          contactIds: [...ids],
        });
        setSignAll(false);
        await load();
        setRev((x) => x + 1);
        tabLock.notifySaved(); // ehr#682
        const nameOf = (cid: string) =>
          view.attendees.find((a) => a.contactId === cid)?.name.split(' ')[0] ?? cid;
        setMsg(
          [
            r.submitted.length
              ? `${supervised ? 'Submitted' : 'Signed'} ${r.submitted.length}.`
              : 'None were ready.',
            ...r.held.map((h) => `${nameOf(h.contactId)}: ${h.reason}`),
          ].join(' '),
        );
      })
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.message : 'Could not sign the notes.'),
      )
      .finally(() => setBusy(false));
  };
  // V11 (V22 30 A): after one note is signed, the next attendee who still needs something opens
  const nextAfterSign = () =>
    void load().then((v) => {
      if (!v) return;
      tabLock.notifySaved(); // ehr#682
      const list = v.attendees.filter((a) => a.onRoster);
      const i = list.findIndex((a) => a.contactId === current);
      const order = [...list.slice(i + 1), ...list.slice(0, Math.max(0, i))];
      const next = order.find((a) => !a.note || isOpen(a.note.status));
      const done = supervised ? 'Submitted.' : 'Signed.';
      if (!next) return setMsg(done);
      setCurrent(next.contactId);
      setMsg(`${done} Next: ${next.name}.`);
    });
  // ehr#689 F4 (V15 "V9 sup"): the reviewer co-signs every attendee note pending review they can
  // (POST /family-notes/:id/cosign), each on its own: the client's billed note and each attendee's.
  // An open review comment on one of them warns once first; then a confirm; then each one's result.
  const cosignable = view.attendees.filter((a) => a.note?.canCosign);
  const openOn = cosignOpenWarning(
    cosignable.map((a) => ({
      name: a.name.split(' ')[0] ?? a.name,
      open: (a.note?.openComments ?? 0) + (a.contactId === me.contactId ? draftComments : 0),
    })),
  );
  const askCosign = () => {
    setErr('');
    setCosignOut(null);
    if (openOn && !cosignWarned) {
      setCosignWarned(true);
      setMsg('');
      return;
    }
    setCosignAsk(true);
  };
  const cosignAll = () => {
    setCosignAsk(false);
    setBusy(true);
    setErr('');
    setMsg('');
    saveRef
      .current()
      .then(async () => {
        const r = await api.post<GroupCosignResult>(`/family-notes/${sessionId}/cosign`, {
          contactIds: cosignable.map((a) => a.contactId),
        });
        setCosignWarned(false);
        await load();
        setRev((x) => x + 1);
        if (r.cosigned.length) tabLock.notifySaved(); // ehr#682
        const n = r.cosigned.length;
        setMsg(
          n
            ? `Co-signed ${n} note${n === 1 ? '' : 's'}. ${n === 1 ? 'It is' : 'They are'} locked.`
            : 'No note was co-signed.',
        );
        setCosignOut(
          cosignResultLines(
            r,
            (cid) => view.attendees.find((a) => a.contactId === cid)?.name ?? cid,
            view.attendees.map((a) => a.contactId),
          ),
        );
      })
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.message : 'Could not co-sign the notes.'),
      )
      .finally(() => setBusy(false));
  };
  // ehr#689 F7 (Jay 10-30 ruling 2, V15 draftEveryone / group #694): Generate everyone and Apply to
  // <name>'s note, from the shared notes plus each attendee's own, with the default generator (no
  // strict rules or "Client said" naming on family, answer 5). The notes as saved just now are used.
  const genBlock = familyGenerateBlock({
    lead,
    readOnly: tabRO,
    attendees: view.attendees,
    me: currentUserId,
    isManager: canReviewNotes,
  });
  const meSkip = tabRO ? 'read-only' : familySkipReason(me, currentUserId, canReviewNotes);
  const generateFor = async (only: string | null) => {
    await saveRef.current();
    // the shared notes as typed are saved first, so the other copy and the next load have them
    if (lead && shared !== sharedSaved) {
      const v = await api.patch<FamilySessionView>(`/family-notes/${sessionId}`, {
        sharedNotes: shared,
      });
      setSharedSaved(v.sharedNotes);
    }
    const fresh = await api.get<FamilySessionView>(`/family-notes/${sessionId}`);
    let n = 0;
    const skipped: { name: string; reason: string }[] = [];
    for (const a of fresh.attendees) {
      if (only ? a.contactId !== only : !a.onRoster) continue;
      const why = familySkipReason(a, currentUserId, canReviewNotes);
      if (why) {
        skipped.push({ name: a.name, reason: why });
        continue;
      }
      const body = a.note?.body ?? {
        // a new attendee note starts at the session's start, as Attendance for everyone does
        session: {
          ...(cs.time ? { time: cs.time } : {}),
          type: sessionLabel,
          durationMin: view.durationMin,
          place: 'In person',
        },
      };
      const text = familyTextFor(shared, body);
      const r = text
        ? generateFamilyNote(body, a.note?.format ?? 'SOAP', text, {
            durationMin: view.durationMin,
            sessionLabel,
            refresh: !only,
          })
        : { body, drafted: 0 };
      if (!r.drafted) {
        skipped.push({ name: a.name, reason: 'its boxes already have writing' });
        continue;
      }
      try {
        await api.put(`/family-notes/${sessionId}/attendees/${a.contactId}/note`, {
          body: r.body,
        });
        n++;
      } catch (e: unknown) {
        // the server's own rules (the review lock, a reviewer editing) hold one note back, not all
        skipped.push({ name: a.name, reason: e instanceof ApiError ? e.message : 'not saved' });
      }
    }
    await load();
    setRev((x) => x + 1);
    if (n) tabLock.notifySaved(); // ehr#682
    return { n, skipped };
  };
  const generateEveryone = () => {
    setErr('');
    setMsg('');
    if (genBlock) return setErr(genBlock);
    if (!shared.trim()) return setErr('Type what happened in the session first.');
    setBusy(true);
    generateFor(null)
      .then((r) => setMsg(familyGeneratedMsg(r.n, r.skipped)))
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.message : 'Could not generate everyone.'),
      )
      .finally(() => setBusy(false));
  };
  const applyToAttendee = () => {
    if (meSkip) return;
    const name = me.name.split(' ')[0] ?? me.name;
    setErr('');
    setMsg('');
    setBusy(true);
    generateFor(me.contactId)
      .then((r) =>
        setMsg(
          r.n
            ? `Generated ${name}’s note. Read it and make it theirs.`
            : `Nothing to generate for ${name}: type what happened in the session first, or ${name}’s boxes already have writing.`,
        ),
      )
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.message : `Could not generate ${name}’s note.`),
      )
      .finally(() => setBusy(false));
  };
  // 60 A: Couples once every partner who came has been asked alone and no Yes waits for review.
  const toCouples = () => {
    setErr('');
    setMsg('');
    saveRef
      .current()
      .then(() => load())
      .then((v) => {
        if (!v) return;
        const open = v.attendees.filter((a) => a.safety === 'open');
        if (open.length)
          return setErr(`Review Safety between partners on ${names(open)}’s note first.`);
        const ask = partnersToAsk(v.attendees);
        if (!ask.length) return patch({ type: 'Couples' });
        setAsking({
          list: ask,
          i: 0,
          ans: Object.fromEntries(ask.map((a) => [a.contactId, [null, null, null]])),
          skip: {},
          err: '',
        });
      })
      .catch((e: unknown) => setErr(e instanceof ApiError ? e.message : 'Could not save first.'));
  };
  const finishAsking = (a: NonNullable<typeof asking>) =>
    api
      .put<FamilySessionView>(`/family-notes/${sessionId}/partner-safety`, {
        answers: a.list.map((x) =>
          a.skip[x.contactId]
            ? { contactId: x.contactId, notPartner: true }
            : { contactId: x.contactId, a: a.ans[x.contactId] },
        ),
      })
      .then((v) => {
        setAsking(null);
        setView(v);
        setRev((r) => r + 1);
        tabLock.notifySaved(); // ehr#682
        const yes = v.attendees.filter((x) => x.safety === 'open');
        if (yes.length)
          setErr(
            `Safety between partners: Yes on ${names(yes)}’s note. The session stays Family until it is reviewed.`,
          );
        else setMsg('Couples session.');
      })
      .catch((e: unknown) =>
        setAsking({ ...a, err: e instanceof ApiError ? e.message : 'Could not save the answers.' }),
      );
  const handOff = (cid: string) =>
    saveRef
      .current()
      .then(() =>
        api.post<{ id: string }>(`/family-notes/${sessionId}/attendees/${cid}/to-progress`, {}),
      )
      .then((n) => onHandOff(n.id, cid))
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.message : 'Could not open it as a Progress Note.'),
      );
  const partnerYes = () =>
    saveRef
      .current()
      .then(() => patch({ type: 'Family' }))
      .then(() =>
        setErr(
          `Safety between partners: Yes on ${me.name.split(' ')[0]}’s note. The session is back to Family until it is reviewed.`,
        ),
      );
  const roster = (p: { add?: string[]; remove?: string[] }) =>
    api
      .put<FamilySessionView>(`/family-notes/${sessionId}/roster`, p)
      .then((v) => {
        setView(v);
        setAdding(false);
        setQ('');
        tabLock.notifySaved(); // ehr#682
      })
      .catch((e: unknown) =>
        setErr(e instanceof ApiError ? e.message : 'Could not change the attendees.'),
      );

  const askWho = asking ? asking.list[asking.i]! : null;
  const askAns = asking && askWho ? asking.ans[askWho.contactId]! : [];
  const askNext = (skip: boolean) => {
    if (!asking || !askWho) return;
    const done = askAns.every((v) => v === 'Yes' || v === 'No');
    if (!skip && !done)
      return setAsking({ ...asking, err: `Answer all three for ${askWho.name.split(' ')[0]}.` });
    const next = { ...asking, skip: { ...asking.skip, [askWho.contactId]: skip }, err: '' };
    if (asking.i < asking.list.length - 1) return setAsking({ ...next, i: asking.i + 1 });
    void finishAsking(next);
  };
  const askModal = asking && askWho && (
    <Modal
      title="Safety between partners"
      onClose={() => setAsking(null)}
      className="pn-pvs-modal"
      actions={
        <>
          <button type="button" className="btn" onClick={() => setAsking(null)}>
            Cancel
          </button>
          {asking.i > 0 && (
            <button
              type="button"
              className="btn"
              onClick={() => setAsking({ ...asking, i: asking.i - 1, err: '' })}
            >
              Back
            </button>
          )}
          <button type="button" className="btn" onClick={() => askNext(true)}>
            Not a partner
          </button>
          <button type="button" className="btn primary" onClick={() => askNext(false)}>
            {asking.i === asking.list.length - 1 ? 'Save' : 'Next'}
          </button>
        </>
      }
    >
      <div className="pn-pvs-who">
        <b>Ask {askWho.name} alone</b>
        <span className="muted">
          {asking.i + 1} of {asking.list.length}
        </span>
      </div>
      {PARTNER_SAFETY_QUESTIONS.map((q, i) => (
        <div key={q} className="pn-pvs-row">
          <span>{q}</span>
          <div className="pn-seg" role="group" aria-label={q}>
            {(['Yes', 'No'] as const).map((v) => (
              <button
                key={v}
                type="button"
                aria-pressed={askAns[i] === v}
                onClick={() => {
                  const ans = [...askAns];
                  ans[i] = v;
                  setAsking({
                    ...asking,
                    ans: { ...asking.ans, [askWho.contactId]: ans },
                    err: '',
                  });
                }}
              >
                {v}
              </button>
            ))}
          </div>
        </div>
      ))}
      {asking.err && <em className="pn-inline-err">{asking.err}</em>}
    </Modal>
  );
  // ehr#682 (V131): a second copy of the session is read-only until it is free
  const tabBanner = tabRO && (
    <div className="pn-banner lock pn-tablock" role="status">
      <span>
        This family session is open in another tab. This copy is read-only, so the two can&rsquo;t
        save over each other.
      </span>
      <button
        type="button"
        className="btn small primary"
        onClick={tabLock.takeOver}
        disabled={tabLock.state === 'opening'}
      >
        {tabLock.state === 'opening' ? 'Opening here...' : 'Open here instead'}
      </button>
    </div>
  );
  const bar = (
    <section className="pn-sec pn-fam" aria-label="Family session">
      <div className="pn-sec-h">
        <h3>{view.title}</h3>
        <span className="pn-sec-sum pn-sess-line">
          <span>{new Date(view.startsAt).toLocaleDateString('en-US', { timeZone: 'UTC' })}</span>
          <span>
            {cs.time && csEnd
              ? `${storedTime(cs.time)} to ${storedTime(csEnd)} (${view.durationMin} min)`
              : `${view.durationMin} min`}
          </span>
          {billLine && <span>{billLine}</span>}
          {facilitator && <span>Facilitator: {facilitator}</span>}
        </span>
        <span className="pn-sp" />
        <div className="pn-seg" role="group" aria-label="Family or couples">
          {(['Family', 'Couples'] as const).map((t) => (
            <button
              key={t}
              type="button"
              aria-pressed={view.type === t}
              disabled={!lead || tabRO}
              onClick={() =>
                t === view.type
                  ? undefined
                  : t === 'Couples'
                    ? toCouples()
                    : void patch({ type: t })
              }
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      <div className="pn-grp-roster" aria-label="Attendees">
        <div className="pn-grp-roster-h">
          <b>Attendees</b>
          <span className="muted">
            {attended} of {onRoster.length} attended
          </span>
          {!allLocked && (
            <button
              type="button"
              className="pn-link"
              disabled={tabRO}
              onClick={() => setAttOpen(true)}
            >
              Attendance
            </button>
          )}
        </div>
        <RosterGroups
          entries={view.attendees.map((a) => ({
            contactId: a.contactId,
            name: a.name,
            line: lines.get(a.contactId)!,
            tags: (
              <>
                {a.billed && <span className="pn-tag pn-tag-green">Billed</span>}
                {a.safety === 'open' && <span className="pn-tag pn-tag-bad">Needs review</span>}
              </>
            ),
          }))}
          current={me.contactId}
          onPick={switchTo}
        />
        {canAdd && (
          <div className="pn-grp-roster-f">
            {!adding ? (
              <button
                type="button"
                className="pn-link pn-fam-add"
                disabled={tabRO}
                onClick={() => setAdding(true)}
              >
                + Add attendee
              </button>
            ) : (
              <div className="pn-fam-search">
                <input
                  autoFocus
                  value={q}
                  placeholder="Search the practice's contacts"
                  aria-label="Search contacts"
                  onChange={(e) => setQ(e.target.value)}
                />
                {found
                  .filter((c) => !view.attendees.some((a) => a.contactId === c.id))
                  .map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className="pn-fam-pick"
                      onClick={() => void roster({ add: [c.id] })}
                    >
                      {[c.firstName, c.lastName].filter(Boolean).join(' ')}
                    </button>
                  ))}
                <button type="button" className="pn-link" onClick={() => setAdding(false)}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <div className="pn-fam-sum" data-pn-target="gsum">
        <div className="pn-nec-h">
          <span>Session summary</span>
          <span className="pn-tag">Same on every attendee&rsquo;s note</span>
        </div>
        <textarea
          value={summary}
          readOnly={!lead || tabRO}
          placeholder="What happened in the session, for everyone who was there."
          aria-label="Session summary"
          onChange={(e) => setSummary(e.target.value)}
          onBlur={() => summary !== summarySaved && void patch({ sessionSummary: summary })}
        />
      </div>
      {/* ehr#689 F7 (V15 groupAiBody): the shared notes Generate everyone and Apply generate from */}
      <div className="pn-fam-sum pn-fam-shared">
        <div className="pn-nec-h">
          <span>What happened in the session</span>
        </div>
        <textarea
          value={shared}
          readOnly={!lead || tabRO}
          placeholder="Type, paste or dictate what happened in the session."
          aria-label="What happened in the session"
          onChange={(e) => setShared(e.target.value)}
          onBlur={() => lead && shared !== sharedSaved && void patch({ sharedNotes: shared })}
        />
      </div>
      <div className="pn-grp-tools-act pn-fam-act">
        <button
          type="button"
          className="btn"
          disabled={busy || !!genBlock}
          title={genBlock ?? undefined}
          onClick={generateEveryone}
        >
          Generate everyone
        </button>
        <button
          type="button"
          className="btn"
          disabled={busy || !!meSkip}
          title={
            meSkip
              ? `${me.name.split(' ')[0]}’s note is locked or not yours to write.`
              : `Apply to ${me.name.split(' ')[0]}’s note`
          }
          onClick={applyToAttendee}
        >
          Apply to {me.name.split(' ')[0]}&rsquo;s note
        </button>
        <button
          type="button"
          className="btn primary"
          disabled={busy || !ready.length || tabRO}
          onClick={() => setSignAll(true)}
        >
          {supervised ? 'Submit' : 'Sign'} {ready.length ? `${ready.length} ` : ''}ready note
          {ready.length === 1 ? '' : 's'}
        </button>
        {cosignable.length > 0 && (
          <button
            type="button"
            className="btn primary"
            data-test="cosign-all"
            disabled={busy || tabRO}
            onClick={askCosign}
          >
            Co-sign all ready notes ({cosignable.length})
          </button>
        )}
      </div>
      {genBlock && <p className="muted pn-grp-why">{genBlock}</p>}
      {err && (
        <p className="pn-inline-err" role="alert">
          {err}
        </p>
      )}
      {/* ehr#689 F4 (V15): one warning while a review comment is open; the next press goes on */}
      {cosignWarned && openOn && !err && (
        <p className="pn-inline-err pn-fam-cosign-warn" role="alert">
          {openOn} Press Co-sign all ready notes again to co-sign anyway.
        </p>
      )}
      {msg && !err && (
        <p className="pn-fam-msg" role="status">
          {msg}
        </p>
      )}
      {cosignOut && cosignOut.length > 0 && (
        <ul className="pn-grp-cosign-out" aria-label="Co-sign results">
          {cosignOut.map((t) => (
            <li key={t}>{t}</li>
          ))}
        </ul>
      )}
      {cosignAsk && (
        <ConfirmDialog
          message={
            <>
              <b>
                Co-sign {cosignable.length} ready note{cosignable.length === 1 ? '' : 's'}?
              </b>
              <br />
              {cosignable.map((a) => a.name).join(', ')}. Each note is co-signed on its own and
              locked. A note that can&rsquo;t be co-signed yet (a required part still blank) is
              skipped, with the reason.
            </>
          }
          confirmLabel="Co-sign all"
          danger={false}
          onConfirm={cosignAll}
          onClose={() => setCosignAsk(false)}
        />
      )}
      {/* inside the note page, so it stacks over it */}
      {askModal}
      {attOpen && (
        <AttendanceWindow
          rows={attRows}
          busy={busy}
          onSave={saveAttendance}
          onClose={() => setAttOpen(false)}
        />
      )}
      {signAll && (
        <SignReadyWindow
          names={ready.map((a) => a.name)}
          lateHours={lateReady.length ? Math.max(...lateReady) : null}
          supervised={supervised}
          busy={busy}
          onConfirm={signReady}
          onClose={() => setSignAll(false)}
        />
      )}
    </section>
  );

  return (
    <>
      <ProgressNoteEditor
        key={`${me.contactId}:${me.note?.id ?? 'new'}:${rev}`}
        editId={me.note?.id ?? null}
        noteType={view.type}
        history={me.contactId === (historyFor ?? view.clientId) ? history : []}
        family={{
          sessionId,
          type: view.type,
          billed: me.billed,
          bar: (
            <>
              {tabBanner}
              {bar}
            </>
          ),
          bindSave: (fn) => {
            saveRef.current = fn;
          },
          othersAway: othersAway(view.attendees),
          onHandOff: () => void handOff(me.contactId),
          onPartnerYes: () => void partnerYes(),
          summaryWritten,
          onSigned: nextAfterSign,
          tabReadOnly: tabRO,
          notifySaved: () => tabLock.notifySaved(),
          onReviewDraft: setDraftComments,
        }}
        contactId={me.contactId}
        contactName={me.name}
        users={users}
        currentUserId={currentUserId}
        canReviewNotes={canReviewNotes}
        onClose={onClose}
        onSaved={() => void load()}
      />
    </>
  );
}

// ── Credify Note template (calendar#46, PROGRESS_NOTE_A-15 renderCredifyNote) ─
/** One chip section: toggle chips (multi-select) plus an "Other (optional)" field. */
function ChipSection({
  title,
  options,
  picked,
  other,
  onToggle,
  onOther,
}: {
  title: string;
  options: readonly string[];
  picked: string[];
  other: string;
  onToggle: (option: string) => void;
  onOther: (v: string) => void;
}) {
  return (
    <div className="pn-cred-section">
      <label>{title}</label>
      <div className="pn-chip-grid">
        {options.map((o) => {
          const on = picked.includes(o);
          return (
            <button
              key={o}
              type="button"
              className={`pn-chip${on ? ' active' : ''}`}
              aria-pressed={on}
              onClick={() => onToggle(o)}
            >
              {o}
            </button>
          );
        })}
      </div>
      <input
        type="text"
        className="pn-chip-other"
        placeholder="Other (optional)"
        value={other}
        onChange={(e) => onOther(e.target.value)}
      />
    </div>
  );
}

export function CredifyNoteFields({
  value,
  onChange,
  renderText,
  lockChoices = false,
}: {
  value: CredifyFields;
  // Updater form: quick successive edits (chip, chip, Other…) each build on the
  // latest state instead of a stale copy.
  onChange: (update: (prev: CredifyFields) => CredifyFields) => void;
  /** Renders one of the template's free-text fields (the editor's NoteField). */
  renderText: (section: [string, string, string]) => ReactNode;
  /** ehr#571 (reopen 10-23): the supervisor edits the boxes in place; the picks stay locked. */
  lockChoices?: boolean;
}) {
  const set = <K extends keyof CredifyFields>(k: K, v: CredifyFields[K]) =>
    onChange((prev) => ({ ...prev, [k]: v }));
  const toggle = (k: 'concerns' | 'interventions' | 'nextSteps') => (o: string) =>
    onChange((prev) => ({
      ...prev,
      [k]: prev[k].includes(o) ? prev[k].filter((x) => x !== o) : [...prev[k], o],
    }));
  const [objective, assessment] = FORMAT_SECTIONS.CREDIFY;
  return (
    <div className="pn-cred">
      <fieldset className="pn-lock pn-lock-contents" disabled={lockChoices}>
        <ChipSection
          title="Presenting concerns"
          options={CREDIFY_NOTE_OPTIONS.concerns}
          picked={value.concerns}
          other={value.concernsOther}
          onToggle={toggle('concerns')}
          onOther={(v) => set('concernsOther', v)}
        />
        <ChipSection
          title="Interventions used"
          options={CREDIFY_NOTE_OPTIONS.interventions}
          picked={value.interventions}
          other={value.interventionsOther}
          onToggle={toggle('interventions')}
          onOther={(v) => set('interventionsOther', v)}
        />
        <div className="pn-cred-section">
          <label>Client response</label>
          <div className="pn-cred-response">
            <Dd
              value={value.clientResponse}
              searchable={false}
              placeholder="Select one"
              onChange={(v) => set('clientResponse', v)}
              options={CREDIFY_NOTE_OPTIONS.response.map((o) => ({ value: o, label: o }))}
            />
          </div>
          <input
            type="text"
            className="pn-chip-other"
            placeholder="Other (optional)"
            value={value.clientResponseOther}
            onChange={(e) => set('clientResponseOther', e.target.value)}
          />
        </div>
      </fieldset>
      {objective && renderText(objective)}
      {assessment && renderText(assessment)}
      <fieldset className="pn-lock pn-lock-contents" disabled={lockChoices}>
        <ChipSection
          title="Next steps"
          options={CREDIFY_NOTE_OPTIONS.nextSteps}
          picked={value.nextSteps}
          other={value.nextStepsOther}
          onToggle={toggle('nextSteps')}
          onOther={(v) => set('nextStepsOther', v)}
        />
      </fieldset>
    </div>
  );
}
